import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AiCombo } from "../../components/AiCombo";
import type { ChangeEvent, CSSProperties, MutableRefObject, ReactNode } from "react";
import "./SettingsDetailPage.css";
import { api } from "../../api/client";
import { useAuthStore } from "../../auth/store";
import { useNotify } from "../../components/notifications";
import { useSaveFlow, useDeleteFlow, useConfirm } from "../../components/confirm";
import { LoaderSymbol, BusyIcon } from "../../components/loading";
import { useHotkeys, type HotkeyDef } from "../../components/hotkeys";
import { useAutoFocusFirstControl } from "../../hooks/useAutoFocusFirstControl";
import { CheckedCombo } from "../../components/CheckedCombo";
import { LookupCombo } from "../../components/LookupCombo";
import { GridLookupModal } from "./GridLookupModal";
import { ComboSelect } from "../../components/ComboSelect";
import { RichTextEditor } from "../settings/systemManagement/RichTextEditor";
import { DxRichEditor } from "../../components/DxRichEditor";
import { rtfToPlainText } from "../../utils/rtf";
import { ExportMenu } from "../../components/AppToolbar";
import { AppDataGrid, GridPager, type AppColumn } from "../../components/AppDataGrid";
import { AppDialog } from "../../components/AppDialog";
import { GridSearch } from "../../components/GridSearch";
import { AuditTrailButton } from "../../components/AuditTrailButton";
import { useSort } from "../../components/SortableHeader";
import { ColFilterButton, cascadeRows, passesColumnFilter, type ColumnFilterValue } from "../../components/ColumnFilterDropdown";
import { cascadeUrl, depsOf, fieldLockReason, isFieldLocked, PENDING_LABELS_KEY } from "./moduleCrud";
import { cascadeLevels, fetchOptions, primeOptionCache, sharedOptionCache } from "./cascadeFetch";
import type { AssocColumn, CrudChildGrid, CrudField, CrudRecordAction, CrudStaticSection, ModuleCrudConfig } from "./moduleCrud";
import { invalidatePreviews, prefetchPreview, readGet, readPreview } from "./previewCache";
import { fmtLocalDate, fmtLocalDateTime } from "../../utils/datetime";
import { MODULE_CRUD, CLIENT_EDIT_CONFIG } from "./moduleCrud";
import { ModuleCrudDialog } from "./ModuleCrudDialog";
import { EddQueryBuilderDialog } from "./EddQueryBuilderDialog";
import { QuickAddDialog } from "./QuickAddDialog";
import { OpenProspectDialog } from "./OpenProspectDialog";
import { CountryDialog } from "./CountryDialog";
import { StateDialog } from "./StateDialog";
import { CityDialog } from "./CityDialog";
import { ContactDialog } from "./ContactDialog";
import { ClientDialog } from "./ClientDialog";
import { ReagentFormulaDialog } from "./ReagentFormulaDialog";
import { TestMethodDetailDialog } from "../settings/testMethodDetail/TestMethodDetailDialog";
import { ContactEditDialog } from "../settings/contactEdit/ContactEditDialog";
import { confirmDiscardIfUnsaved, registerUnsavedGuard } from "../../hooks/useUnsavedGuard";
import { MaintenanceTaskChecklistGrid } from "./MaintenanceTaskChecklistGrid";
import { ProjectNotesButton } from "../../components/ProjectNotes";
import { ItemTestsGrid } from "./ItemTestsGrid";
import { StockQtyEditDialog } from "./StockQtyEditDialog";
import { UsDateInput } from "../../components/UsDateInput";
import { DatePickText } from "../../components/DatePickText";
import { DailyQcFieldConfigGrid } from "./DailyQcFieldConfigGrid";
import type { BufferedChildHandle } from "./itemTestsModel";
import { MultiPickField } from "./ParameterPickerDialog";

interface Option {
  id: string;
  name: string;
}

/** The subset of a ModuleCrudConfig this detail view needs.  A full
 *  ModuleCrudConfig satisfies it (ModuleListView path); a bespoke page supplies
 *  just these four + an `adapter` for its own data layer.  `basePath` is only
 *  required when NO adapter is given (the default REST path). */
/** The slice of a module's MODULE_CRUD config that the full-page detail view
 *  understands.
 *
 *  This is an OMIT (deny-list), deliberately NOT a Pick (allow-list).
 *
 *  It used to be a Pick, and that failed SILENTLY and repeatedly: a detail-layout
 *  property could be declared in moduleCrud.ts, set by a form's config, and read
 *  by the render below — yet be missing from the Pick, in which case the setting
 *  simply did nothing, with no error at the call site. It bit us at least twice:
 *    · 2026-09-23 — merge 576e0371 dropped `wideLayout` / `horizontalLabels` /
 *      `columnRows`, disabling the 2-column + horizontal-label layouts.
 *    · 2026-09-24 — `detailMaxWidth` / `horizontalLabelWidth` were never added,
 *      so the Storage form's 100px label column was ignored.
 *  Each time the layout work looked "missing" even though the commits were in.
 *
 *  With an Omit, a NEW config property is available to the detail view by
 *  DEFAULT, so adding one can no longer be silently dropped by a merge. Only the
 *  LIST/grid-only concerns below are excluded — they have no meaning on a detail
 *  view. Adding a property here is now a deliberate act, not an omission.
 *
 *  `basePath` is excluded and re-declared optional: a bespoke page that supplies
 *  its own DetailDataAdapter has no REST base path. */
export type DetailConfig = Omit<
  ModuleCrudConfig,
  | "basePath"
  // ── list / grid-only concerns — not applicable to a detail view ──
  | "columnFieldMap"
  | "inlineRowEdit"
  | "hideEditPencil"
  | "readOnlyList"
  | "editOnlyList"
  | "pureListView"
  | "fitAllColumns"
  | "pencilRowEdit"
  | "defaultPageSize"
  | "bulkLoad"
  | "relatedLookups"
  | "dialogEdit"
  | "hideViewToggle"
  | "plainList"
> & { basePath?: string };

/** Optional data-layer override so a bespoke page (TanStack mutations, custom
 *  endpoints, …) can reuse this exact detail view instead of the REST basePath.
 *  When omitted, the view uses `api.get/post/put` against `config.basePath`. */
export interface DetailDataAdapter {
  /** Load one record's field values for Edit (keyed by CrudField.key). */
  load: (id: string) => Promise<Record<string, unknown>>;
  /** Persist a new record; return its id if known (so Save can stay on it), else null. */
  create: (payload: Record<string, unknown>) => Promise<string | null>;
  /** Persist an existing record. */
  update: (id: string, payload: Record<string, unknown>) => Promise<void>;
}

interface Props {
  config: DetailConfig;
  /** Optional bespoke data layer; when omitted, REST via `config.basePath`. */
  adapter?: DetailDataAdapter;
  /** null = New (create); a record id = Edit. */
  id: string | null;
  /** How the record should OPEN — 'view' (read-only, row-click) or 'edit'
   *  (editable, Edit action). Defaults to 'view' for an existing record, 'edit'
   *  for New. Lets the list distinguish row-click (view) from the Edit pencil. */
  initialMode?: "view" | "edit";
  /** Form title (nav label) + breadcrumb group, sourced from ModuleListView. */
  title: string;
  group: string;
  /** Ordered ids of the current list page — powers the prev/next arrows. */
  siblingIds?: string[];
  onHome: () => void;
  /** Jump to another record (Edit) — used by the prev/next arrows. */
  onNavigate: (id: string) => void;
  /** Return to the list view (Cancel / Save & Close). */
  onClose: () => void;
  /** Refresh the underlying list after a successful save. */
  onSaved: () => void;

  /** OPTIONAL — read-only reference panels shown behind a tab strip BELOW the
   *  fields (e.g. Message Library's Used In / Same Wording).
   *
   *  Distinct from `config.childGrids`, which is for EDITABLE child collections
   *  fetched from an endpoint. These render whatever the calling page already
   *  holds, so they need no listUrl and no child config. Omit for no tabs. */
  extraTabs?: DetailExtraTab[];

  /** OPTIONAL — a panel beside the field grid (e.g. Message Library's live
   *  Preview of how a message will appear). Omit for the normal full-width form. */
  /** Handed the values as currently TYPED, not the saved record: a preview that
   *  refreshed only on save would be showing the record rather than the edit. */
  aside?: (values: Record<string, unknown>) => ReactNode;

  /** OPTIONAL — a bespoke child area rendered full-width below the fields whose
   *  edits are BUFFERED on the client and committed by the header Save
   *  (2026-10-08; first user: the PLM Reference Library's asbestos types).
   *
   *  The area registers a {@link BufferedChildHandle} on `handleRef`; Save then
   *  validates it BEFORE the confirm and merges its payload into the header
   *  POST / PUT, so the record and its children are one request and one
   *  transaction. It reloads its rows when `reloadToken` changes (after every
   *  save) or the record does, and calls `onDirty` on a user edit so leaving
   *  the page asks first (standing rule 15).
   *
   *  The generic form of the `itemTestsGrid` config flag: a new form passes its
   *  own area here instead of adding another hard-wired flag to this page. */
  /** OPTIONAL — form-specific buttons rendered in BOTH action bars, beside the
   *  config-driven `recordActions`. For an action that needs a DIALOG rather
   *  than a plain POST (Test Code's Rename Test / Copy Test / Copy Parameters),
   *  which `recordActions` cannot express. Receives the saved record id, and is
   *  not rendered while the record is new. */
  /** OPTIONAL — rendered between the header and the FIRST action bar, above the
   *  fields. For a status strip that belongs to the record as a whole rather
   *  than to any one section (Test Code's setup checklist, which the prototype
   *  puts directly under the title). Not rendered while the record is new. */
  /** OPTIONAL — extra class on the detail wrapper, so one form can restyle its
   *  own chrome without touching the shared rules every other form shares. */
  detailClassName?: string;
  headerSlot?: (id: string) => ReactNode;
  /** Form-specific record actions. viewOnly is passed so a slot can disable
    *  the ones that WRITE while the record is open read-only — the shared bar
    *  has no way to know which of a form's own buttons mutate.
    *
    *  reload re-fetches THIS record. An action that changes the open record
    *  (Rename Test) must call it: refreshing the list alone leaves the detail
    *  showing what it fetched before the change. */
  recordActionsSlot?: (id: string, viewOnly: boolean, reload: () => void) => ReactNode;
  /** OPTIONAL — a form-specific button rendered in the VIEW-mode bar immediately BEFORE Edit
   *  (and NOT in the Save-trio edit bar). For a record action that needs a DIALOG and must sit
   *  ahead of Edit — Open / My Open Prospects' "Close Prospects" (owner report 2026-10-28,
   *  image 1043). Receives the saved record id; not rendered while the record is new. */
  recordActionsSlotBeforeEdit?: (id: string) => ReactNode;
  bufferedChild?: (ctx: BufferedChildContext) => ReactNode;
  /** OPTIONAL — field values to seed a NEW record with, on top of the config's
   *  own empties. Used by Test Code's "Copy Test", which opens the New form
   *  already carrying the source test's Matrix / Method / Department / Category
   *  so the user only types the new name. Ignored when editing. */
  newRecordDefaults?: Record<string, unknown>;

  /** OPTIONAL — force the square-control look (de-yellow editable fields + square
   *  corners, i.e. `.sd-grid--square`) even when the module's own config does not
   *  set `squareControls`. ModuleListView passes this for every Settings ▸ Utility
   *  form so the whole group reads square without tagging each moduleCrud entry
   *  (owner request 2026-10-13). Additive with `config.squareControls`. */
  forceSquareControls?: boolean;
}

/** What a {@link Props.bufferedChild} area is handed. */
export interface BufferedChildContext {
  /** The record being edited; null while it is new and unsaved. */
  id: string | null;
  /** The record's display name — the same one the header and breadcrumb show.
   *  A child that names the record (a dialog title, a message) reads it here
   *  instead of fetching the record again. */
  recordName: string;
  viewOnly: boolean;
  /** The form's current values, so the area can follow a field as it is edited. */
  values: Record<string, unknown>;
  /** Bumped after every successful save — re-read the saved rows. */
  reloadToken: number;
  handleRef: MutableRefObject<BufferedChildHandle | null>;
  onDirty: () => void;
  /** Updates server-computed flags the field locks read, WITHOUT re-reading the
   *  record, so a child grid can unlock a field while the user's typing stands. */
  patchRecord: (partial: Record<string, unknown>) => void;
}

/** One read-only reference panel on the detail view. */
export interface DetailExtraTab {
  /** Stable key — also the React key. */
  id: string;
  label: string;
  /** Shown as a count chip on the tab, like the child-grid tabs. */
  count?: number;
  /** Rendered only while the tab is active, so an expensive panel costs nothing
   *  until someone opens it. */
  render: () => ReactNode;
}

/** After-save behaviour for the Save trio. */
type AfterSave = "stay" | "new" | "close";

/** ISO / date string → yyyy-MM-dd for <input type=date>. */
function toDateInput(raw: unknown): string {
  if (raw == null || raw === "") return "";
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Best-effort extract of the created record id from a POST response so
 *  "Save" on a New record can switch to edit mode instead of duplicating it. */
export function createdIdFrom(resData: unknown, idField: string): string | null {
  const body = (resData as { data?: unknown })?.data;
  if (typeof body === "string" && body) return body;
  if (body && typeof body === "object") {
    // The CREATE response carries the new id under a generic key — IcmMasterControllerBase
    // returns `{ data: { id } }`, standalone controllers `{ data: { oid } }` / `{ data: { id } }`
    // — which is NOT necessarily the LIST's idField ("oid"). Try the form's idField first, then
    // the generic `id` / `oid`. Without the `id` fallback, a form whose create returns `data.id`
    // (every IcmMasterControllerBase form, e.g. Document Creator) got a null createdId, so its
    // buffered child rows (new-document Attachments) were silently dropped on save (owner report
    // 2026-10-12).
    const b = body as Record<string, unknown>;
    for (const key of [idField, "id", "oid"]) {
      const v = b[key];
      if (v != null && v !== "") return String(v);
    }
  }
  return null;
}

/** Empty (New) form values from the config's field defaults. */
/** Overlays a NEW record's seed values with the caller's defaults (Props.
 *  newRecordDefaults). Only keys the caller supplies are touched, so a form that
 *  passes nothing behaves exactly as before. */
function withNewDefaults(
  base: Record<string, unknown>,
  defaults?: Record<string, unknown>,
): Record<string, unknown> {
  if (!defaults) return base;
  const out = { ...base };
  for (const [k, v] of Object.entries(defaults)) {
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  }
  return out;
}

function emptyValues(
  config: DetailConfig,
  currentUserEmail?: string,
  currentUserName?: string,
): Record<string, unknown> {
  const v: Record<string, unknown> = {};
  // Today as yyyy-mm-dd (the date-input value format) for defaultNow preview fields.
  const d = new Date();
  const today = `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, "0")}-${`${d.getDate()}`.padStart(2, "0")}`;
  for (const f of config.fields) {
    v[f.key] =
      // A field flagged defaultCurrentUserEmail (e.g. Email "From") seeds the signed-in
      // user's email on a NEW record; falls through to the normal defaults otherwise.
      f.defaultCurrentUserEmail && currentUserEmail
        ? currentUserEmail
        : // defaultCurrentUserName seeds the Employee DisplayName (e.g. EDD Builder "Author"
          // — a read-only preview of the server's stamp); defaultNow seeds today's date
          // (e.g. "Date Created"). New record only; both fall through when unavailable.
          f.defaultCurrentUserName && currentUserName
          ? currentUserName
          : f.defaultNow
            ? today
            : f.defaultValue !== undefined
              ? f.defaultValue
              : f.type === "checkbox"
                ? false
                : f.type === "multiselect" || f.type === "assocgrid"
                  ? []
                  : "";
    // A 'file' field also tracks its display name in a sibling key.
    if (f.type === "file" && f.fileNameKey) v[f.fileNameKey] = "";
  }
  return v;
}

/**
 * The single shared Settings-BO Detail View (Class A/B).  Driven entirely by
 * the same ModuleCrudConfig the list uses, so every CRUD-enabled settings form
 * gets the identical detail screen for free: header + breadcrumb + prev/next,
 * action bar top & bottom, and the Save trio (Save / Save & New / Save & Close)
 * through the shared useSaveFlow (confirm → audited handler → "Saved
 * successfully."). Never bypasses the API handler.
 */
export function SettingsDetailPage({
  config,
  adapter,
  id,
  initialMode,
  title,
  group,
  siblingIds = [],
  onHome,
  onNavigate,
  onClose,
  onSaved,
  extraTabs,
  aside,
  bufferedChild,
  recordActionsSlot,
  recordActionsSlotBeforeEdit,
  newRecordDefaults,
  headerSlot,
  detailClassName,
  forceSquareControls,
}: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const { confirm, prompt } = useConfirm();

  // currentId is seeded from the prop but can change locally (Save on a New
  // record → edit the just-created row; Save & New → back to null).
  const [currentId, setCurrentId] = useState<string | null>(id);
  /** The record as the API returned it, kept beside `values` because
   *  toValues() keeps only the CONFIGURED field keys: a server-computed flag
   *  that drives the form (Test Code's hasTestGuideRows) has nowhere else to
   *  live. Read only by the field-lock helpers below. */
  const [rawRecord, setRawRecord] = useState<Record<string, unknown>>({});
  const editing = currentId !== null;
  const dailyQcFlushRef = useRef<((id: string) => Promise<void>) | null>(null);
  // Bespoke child area whose edits are BUFFERED and committed by the header Save
  // (Assign Tests To Item → Tests): doSave validates it and merges its payload.
  const bufferedChildRef = useRef<BufferedChildHandle | null>(null);
  // Bumped after every successful save so that area re-reads the saved rows.
  const [childReload, setChildReload] = useState(0);
  // ALPACA parity (BO UI Standard v2, 2026-07-02): opening an EXISTING record
  // from the list shows the detail READ-ONLY first ('view' — Edit/Close action
  // bar); New goes straight to 'edit'.  Edit switches the SAME detail to edit.
  /** Map one loaded record onto the form's values, keyed by CrudField.key.
   *  Shared by the synchronous cache seed and the async fetch so the two can
   *  never disagree about how a value is coerced. */
  const toValues = (cfg: DetailConfig, d: Record<string, unknown>) => {
    // The API serialises C# properties with camelCase, which lowercases only the
    // FIRST letter — so an acronym property (RouteID, PWSID, TaskID, PWSSystemName)
    // comes back as "routeID" / "pWSID" / "taskID" / "pWSSystemName", NOT the
    // "routeId" / "pwsid" / "taskId" / "pwsSystemName" a field key usually uses. That
    // silently left such fields blank / showing "(auto)". Match keys CASE-INSENSITIVELY
    // so the value always lands — the systemic fix for that whole bug class (a C#
    // object never has two keys differing only by case, so the match is unambiguous).
    const ciByLower = new Map<string, unknown>();
    for (const k of Object.keys(d)) ciByLower.set(k.toLowerCase(), d[k]);
    const get = (k: string | undefined): unknown =>
      k == null ? undefined : k in d ? d[k] : ciByLower.get(k.toLowerCase());

    const next: Record<string, unknown> = {};
    for (const f of cfg.fields) {
      const raw = get(f.key);
      if (f.type === "date") next[f.key] = toDateInput(raw);
      else if (f.type === "checkbox") next[f.key] = Boolean(raw);
      else if (f.type === "multiselect" || f.type === "assocgrid")
        next[f.key] = Array.isArray(raw) ? raw.map(String) : [];
      else if (f.type === "file") {
        next[f.key] = raw == null ? "" : String(raw);
        if (f.fileNameKey) {
          const nm = get(f.fileNameKey);
          next[f.fileNameKey] = nm == null ? "" : String(nm);
        }
      }
      // A fixed-decimals number (legacy's {0:n2}) is held as its FORMATTED string,
      // so a stored 0 renders "0.00" the moment the record loads. buildPayload
      // still posts a real number — Number("0.00") is 0.
      else if (f.type === "number" && f.decimals != null && raw != null && raw !== "") {
        const n = Number(raw);
        next[f.key] = Number.isFinite(n) ? n.toFixed(f.decimals) : String(raw);
      }
      // A select's <option> values are option IDS, so the form value has to be the
      // stored FK Oid — which the API returns under `idKey` (vendorOid), NOT under
      // `key` (vendor, the display name, or nothing at all). Reading `key` here left
      // every FK dropdown on Items and Vendors showing "— None —" however set the
      // record was, and made the form unsaveable, since a required FK read as blank.
      else if (f.type === "select" && f.idKey) {
        const id = get(f.idKey);
        next[f.key] = id == null ? "" : String(id);
      } else next[f.key] = raw == null ? "" : String(raw);

      // Companion display name for a select (unitsId -> unitsName), kept so a
      // stored value absent from a narrowed list can still be LABELLED rather
      // than shown as the literal "(current)". Never posted - the payload is
      // built from the declared fields alone. Same fix as ModuleCrudDialog.
      if (f.type === "select") {
        const nameKey = `${f.key.replace(/Id$/, "")}Name`;
        const nm = get(nameKey);
        if (nm != null) next[nameKey] = String(nm);
      }
    }
    // Read-only context for visibleWhen rules (never a field, never posted).
    for (const k of cfg.passthroughKeys ?? []) next[k] = get(k);
    return next;
  };

  /** The record the list already warmed on hover, if it is still fresh. Read
   *  DURING the first render so an opened row paints complete, with no loading
   *  gate and no second render that changes what is on screen. */
  const seeded = (() => {
    if (id === null || adapter || !config.basePath) return null;
    const cached = readGet(`${config.basePath}/${id}`);
    return cached && typeof cached === "object" ? toValues(config, cached as Record<string, unknown>) : null;
  })();

  const [mode, setMode] = useState<"view" | "edit">(
    initialMode ?? (id !== null ? (config.detailOpenInEdit ? "edit" : "view") : "edit"),
  );
  const viewOnly = mode === "view";
  // Items ▸ Stock Qty Edit popup (config.stockQtyEdit).
  const [stockQtyOpen, setStockQtyOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [values, setValues] = useState<Record<string, unknown>>(() => seeded ?? withNewDefaults(emptyValues(config, useAuthStore.getState().user?.email ?? "", useAuthStore.getState().user?.name ?? ""), newRecordDefaults));
  /** Child rows entered BEFORE the parent was saved, keyed by child-grid title.
   *  They are POSTed straight after the parent is created (see doSave), which is
   *  the only moment a parent id exists to attach them to. */
  const [pendingChildren, setPendingChildren] = useState<Record<string, Record<string, unknown>[]>>({});
  /** Active extraTabs panel. */
  const [extraTab, setExtraTab] = useState(0);
  /** Active child-grid tab, when the config opts into childGridsTabbed. */
  const [childTab, setChildTab] = useState(0);
  /** Active top-level page tab when the config has fieldTabs: 0 = General
   *  Information, n = fieldTabs[n - 1]. */
  const [pageTab, setPageTab] = useState(0);
  /** Keys of the fields that live on a fieldTabs page, not in the main grid. */
  const tabFieldKeys = useMemo(
    () => new Set((config.fieldTabs ?? []).flatMap((t) => t.fields)),
    [config.fieldTabs],
  );
  // Lookup lists the list view already warmed. Seeded here rather than in the
  // effect below, so a select is never briefly empty on a prefetched open.
  const [options, setOptions] = useState<Record<string, Option[]>>(() => {
    const seed: Record<string, Option[]> = {};
    for (const f of config.fields) {
      if (!f.optionsUrl || f.dependsOn) continue;
      const cached = readGet(f.optionsUrl);
      if (Array.isArray(cached)) seed[f.key] = cached as Option[];
    }
    return seed;
  });
  const [loading, setLoading] = useState(id !== null && !seeded);
  const [saving, setSaving] = useState(false);
  // Focus the first field when the detail is in edit/new mode (mouse-free, ADR-0013).
  // Gated on !loading: an existing record opens with loading=true (the body is still a
  // spinner, no field yet), so focusFirstControl would fall through to its BUTTON
  // fallback and land on the "Next record" arrow (the first enabled header button) —
  // NOT the first field. The seeded / hover-warmed and New paths have loading=false
  // from the start (unchanged), while the unseeded path (Alt+E, toolbar Edit) now waits
  // for the field to render, so it lands on the first field exactly like a hover-warmed
  // mouse click (user report 2026-09-22: "Alt+E focuses Next record, not the field").
  useAutoFocusFirstControl(wrapRef, mode === "edit" && !loading);
  /** Bumped to re-run the record fetch in place — used by a record action that
   *  changes the CURRENT record without navigating away from it. */
  const [reloadToken, setReloadToken] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [triedSave, setTriedSave] = useState(false);

  // Unsaved-edit guard (hooks/useUnsavedGuard.ts). True from the first USER edit
  // until the record is saved, deleted, reloaded or left. Set only by the user's
  // own changes (set / file pick / queued child rows) — never by the programmatic
  // fills (defaults, preview ids, cascades, record loads), which call setValues
  // directly. A ref, so it can be read at click time with no stale closure.
  const dirtyRef = useRef(false);
  useEffect(() => registerUnsavedGuard(() => dirtyRef.current), []);
  /** Leave (Cancel / Close / breadcrumb / prev-next) — asks first if there is
   *  unsaved work: "Unsaved changes will be lost. Do you want to proceed?" */
  const leave = (go: () => void) => () => {
    void confirmDiscardIfUnsaved().then((ok) => {
      if (ok) {
        dirtyRef.current = false;
        go();
      }
    });
  };

  // Cascade wiring: map each PARENT field key -> the child field keys that
  // declare `dependsOn` it, so changing a parent can clear its descendants.
  const childrenOf = useMemo(() => {
    const m: Record<string, string[]> = {};
    // One entry per PARENT, so a field with several parents is cleared by any
    // of them changing.
    for (const f of config.fields) for (const k of depsOf(f)) (m[k] ??= []).push(f.key);
    return m;
  }, [config]);

  const set = (k: string, v: unknown) => {
    if (!viewOnly) dirtyRef.current = true;
    setValues((s) => {
      if (s[k] === v) return { ...s, [k]: v };
      const next = { ...s, [k]: v };
      // Mutually-exclusive checkbox group (e.g. ICP-MS Select Mass 1..4): checking
      // one clears its siblings, so at most one is ever selected (legacy rule).
      const fld = config.fields.find((f) => f.key === k);
      if (fld?.exclusiveWith && v === true) {
        for (const other of fld.exclusiveWith) next[other] = false;
      }
      if (fld?.clearsWhenChecked && v === true) {
        for (const other of fld.clearsWhenChecked) {
          const of = config.fields.find((f) => f.key === other);
          next[other] = of?.type === "checkbox" ? false : "";
        }
      }
      // Clear dependent descendants when a parent changes (legacy cascade reset).
      const clear = (key: string) => {
        for (const childKey of childrenOf[key] ?? []) {
          const cf = config.fields.find((f) => f.key === childKey);
          next[childKey] = cf?.type === "multiselect" ? [] : "";
          clear(childKey);
        }
      };
      clear(k);
      return next;
    });
  };

  // Keep the local id in step when the parent swaps the record (prev/next).
  // Navigating between records (or re-opening) always lands in view mode for
  // an existing record, edit mode for New.
  useEffect(() => {
    setCurrentId(id);
    setMode(initialMode ?? (id !== null ? (config.detailOpenInEdit ? "edit" : "view") : "edit"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, initialMode]);

  // Load select options once per config.  A field carrying a STATIC `options`
  // list is seeded straight away (no request); only `optionsUrl` fields are
  // fetched.  Statics are applied first so the control is never briefly empty.
  useEffect(() => {
    let alive = true;
    const statics = config.fields.filter(
      (f) => (f.type === "select" || f.type === "radio" || f.type === "datasource") && !f.optionsUrl && f.options,
    );
    if (statics.length > 0) {
      setOptions((prev) => ({ ...prev, ...Object.fromEntries(statics.map((f) => [f.key, f.options!])) }));
    }
    // Independent (non-cascade) option lists load once; cascade fields with
    // `dependsOn` are loaded by the effect below, keyed on their parent's value.
    // Skip anything already seeded from the cache - the list view warms these
    // while the user is reading it, so a prefetched open issues no lookup GETs.
    const selects = config.fields.filter(
      (f) =>
        (f.type === "select" || f.type === "multiselect" || f.type === "radio") &&
        f.optionsUrl &&
        !f.dependsOn &&
        readGet(f.optionsUrl!) == null,
    );
    if (selects.length === 0)
      return () => {
        alive = false;
      };
    Promise.all(
      selects.map(async (f) => {
        try {
          const { data } = await api.get<{ data?: Option[] }>(f.optionsUrl!);
          return [f.key, data.data ?? []] as const;
        } catch {
          return [f.key, [] as Option[]] as const;
        }
      }),
    ).then((pairs) => {
      if (alive) setOptions((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
    });
    return () => {
      alive = false;
    };
  }, [config]);

  // Preselect `defaultFirstOption` once that field's options are known. Separate
  // from the fetch above because that effect SKIPS lists already warmed by the
  // prefetch cache — the default has to land whichever way the options arrived.
  //
  // NEW records only, and only into an EMPTY field: it must not overwrite the
  // user's pick, and it must never populate an existing record that was
  // deliberately left blank. Clearing the field back to None sticks, because
  // this keys on the OPTIONS, not on the value.
  const [formNonce, setFormNonce] = useState(0);
  useEffect(() => {
    if (currentId !== null) return;
    const wanted = config.fields.filter((f) => f.defaultFirstOption);
    if (wanted.length === 0) return;
    setValues((cur) => {
      const next = { ...cur };
      let touched = false;
      for (const f of wanted) {
        if (String(cur[f.key] ?? "").trim() !== "") continue;
        const first = (options[f.key] ?? [])[0];
        if (!first) continue; // nothing to offer -> the field stays on None
        next[f.key] = first.id;
        touched = true;
      }
      return touched ? next : cur;
    });
  }, [options, currentId, config, formNonce]);

  // Cascade: (re)load each dependent field's options from
  // `optionsUrl?{dependsOn}={parentValue}` whenever its parent value changes —
  // e.g. Bottle Sharing's Matrix (by Sample Matrix) and Test (by Matrix). An
  // empty parent yields an empty list. Runs on edit-prefill too, so the loaded
  // child values resolve to labels. `depSig` is the signature of parent values.
  const dependents = useMemo(
    () =>
      config.fields.filter(
        (f) =>
          (f.type === "select" || f.type === "multiselect" || f.type === "radio") &&
          !!f.optionsUrl &&
          !!f.dependsOn,
      ),
    [config],
  );
  const depSig = dependents
    .map(
      (f) =>
        `${f.key}=${depsOf(f)
          .map((k) => String(values[k] ?? ""))
          .join(",")}`,
    )
    .join("|");
  // SHARED with the child dialogs (see cascadeFetch.sharedOptionCache): what
  // this page looks up warms the Edit dialog, and vice versa, instead of each
  // paying its own cold round trips.
  const optionCache = useRef(sharedOptionCache);

  // One request that warms every cascading list this form can need — see
  // ModuleCrudConfig.cachePrimeUrl. Best-effort: unprimed, the cascade behaves
  // exactly as before.
  // Awaited by DEEPER levels only (see the loop below), never by level 0 —
  // ModuleCrudDialog carries the full reasoning.
  const primePromise = useRef<Promise<void> | null>(null);
  const primeUrl =
    config.cachePrimeUrl && (config.cachePrimeDependsOn ?? []).every((k) => String(values[k] ?? "").trim() !== "")
      ? cascadeUrl(config.cachePrimeUrl, { dependsOn: config.cachePrimeDependsOn }, values)
      : null;
  useEffect(() => {
    if (!primeUrl) return;
    primePromise.current = primeOptionCache(primeUrl, optionCache.current);
  }, [primeUrl]);

  const levels = useMemo(() => cascadeLevels(dependents), [dependents]);
  useEffect(() => {
    if (dependents.length === 0) return;
    let alive = true;
    void (async () => {
      // A LOCAL working copy of the values. Each level's auto-selection is
      // written here before the next level builds its URL, which is the whole
      // point: Units asks with the Method that Method's level just picked,
      // instead of asking once without it and again with it.
      const work: Record<string, unknown> = { ...values };
      const fetched: Array<readonly [string, Option[]]> = [];
      const picked: Record<string, unknown> = {};

      for (let li = 0; li < levels.length; li++) {
        const level = levels[li]!;
        // Levels 1+ wait for the prime (one request answers them all); level 0
        // never does. See ModuleCrudDialog for the full reasoning.
        // Every level waits, level 0 included - the prime now carries the
        // level-0 lists too, so racing past it duplicates a request rather
        // than saving one (ModuleCrudDialog carries the full reasoning).
        if (primePromise.current) await primePromise.current;
        if (!alive) return;
        const pairs = await Promise.all(
          level.map(async (f) => {
            // Locked (enabledWhen unmet): ask for nothing and offer nothing, so the
            // disabled control cannot flash a list the user is not meant to pick from.
            // disabledWhen is not asked here: this effect does not re-run when that
            // lock lifts, so the control would come back with an empty list.
            if (isFieldLocked({ enabledWhen: f.enabledWhen }, work)) return [f.key, [] as Option[]] as const;
            // cascadeRequiresParent: show an EMPTY list (not the unfiltered endpoint's whole table)
            // while a parent is still unset, but keep the control ENABLED and reading "N/A" — the
            // fix for a City combo that loaded every city before a State was chosen (owner report
            // 2026-11-02). Only when the endpoint returns all for a missing filter (/customers/*);
            // endpoints that already return empty for a missing parent never reach here differently.
            if (f.cascadeRequiresParent && depsOf(f).some((k) => String(work[k] ?? "").trim() === ""))
              return [f.key, [] as Option[]] as const;
            const opts = await fetchOptions(cascadeUrl(f.optionsUrl!, f, work), optionCache.current);
            // KEEP A STORED VALUE VISIBLE. A narrowed list need not contain what the
            // record already holds - a Permit Limit saved when Units still offered
            // the whole table can sit on an analyte/method pair that records none.
            // Dropping it would render the control blank and silently clear the
            // value on the next save, so it is re-added, labelled from its
            // companion `<key>Name` (unitsId -> unitsName) the row already carries.
            const cur = String(work[f.key] ?? "").trim();
            if (cur && !opts.some((o) => o.id === cur)) {
              const nameKey = f.key.replace(/Id$/, "") + "Name";
              const label = String(values[nameKey] ?? "").trim();
              const withCurrent: Option[] = [{ id: cur, name: label || "(current)" }, ...opts];
              return [f.key, withCurrent] as const;
            }
            return [f.key, opts] as const;
          }),
        );
        if (!alive) return;
        fetched.push(...pairs);

        for (const [key, opts] of pairs) {
          const f = config.fields.find((x) => x.key === key);
          if (!f || opts.length === 0) continue;
          // Only ever auto-select from a list that was actually NARROWED. With NO
          // parent set, cascadeUrl asks unfiltered and the list is the whole table -
          // binding its first row would pick an arbitrary value the user never
          // implied (Method jumping to '4500- S2- F' before any Parameter was
          // chosen). SOME, not EVERY: an optional parent like the permit's Sample
          // Matrix may legitimately be blank, and requiring it would stop Units
          // binding on permits that have none.
          // autoSelectRequires: ALL listed keys must be non-empty — used by Method so
          // the always-present sampleMatrixId context value alone does not trigger an
          // auto-pick before the user has chosen a Parameter. Default: SOME dep set.
          const autoTriggerKeys = f.autoSelectRequires ?? null;
          const isTriggered = autoTriggerKeys
            ? autoTriggerKeys.every((k) => String(work[k] ?? "").trim() !== "")
            : depsOf(f).some((k) => String(work[k] ?? "").trim() !== "");
          if (!isTriggered) continue;
          // autoSelectFirst takes any first option; autoSelectSingle only an
          // unambiguous one. Either way the field must still be EMPTY - that guard
          // is what stops this stamping over a value the user just chose, or over
          // an existing record's value while the cascade re-runs on edit-prefill.
          if (!(f.autoSelectFirst || (f.autoSelectSingle && opts.length === 1))) continue;
          if (String(work[key] ?? "").trim() !== "") continue;
          work[key] = opts[0]!.id;
          picked[key] = opts[0]!.id;
        }
      }

      if (!alive) return;
      setOptions((prev) => ({ ...prev, ...Object.fromEntries(fetched) }));
      // Re-check emptiness against the CURRENT state, not the snapshot this pass
      // started from - the user may have typed while the requests were in flight.
      setValues((cur) => {
        const next = { ...cur };
        let touched = false;
        for (const [k, v] of Object.entries(picked)) {
          if (String(cur[k] ?? "").trim() !== "") continue;
          next[k] = v;
          touched = true;
        }
        return touched ? next : cur;
      });
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depSig, dependents, levels]);

  // Edit: prefill via the adapter (bespoke) or GET base/{id} (REST).  New: empty.
  useEffect(() => {
    setError(null);
    setTriedSave(false);
    dirtyRef.current = false;
    if (currentId === null) {
      // NEW: seed any preview field (e.g. the next Permit ID) from the cache the
      // LIST view warmed while the user was still looking at it. Read
      // SYNCHRONOUSLY, so the form renders once - already complete - instead of
      // either flashing a value in afterwards or holding New back behind a
      // spinner. Both of those were tried and both were visible to the user.
      const seeded = withNewDefaults(emptyValues(config, useAuthStore.getState().user?.email ?? "", useAuthStore.getState().user?.name ?? ""), newRecordDefaults);
      let missing = false;
      for (const f of config.fields.filter((x) => x.previewUrl)) {
        const cached = readPreview(f.previewUrl!, f.key);
        if (cached != null) seeded[f.key] = cached;
        else {
          missing = true;
          prefetchPreview(f.previewUrl!, f.key);
        }
      }
      setValues(seeded);
      setLoading(false);

      // Cold cache (a deep link straight to New, or an expired entry): fill the
      // value in when it lands. Only reachable when the prefetch has not had a
      // chance to run, and never leaves the field wrong - just briefly empty.
      if (!missing) return;
      let alivePreview = true;
      void Promise.allSettled(
        config.fields
          .filter((x) => x.previewUrl && readPreview(x.previewUrl, x.key) == null)
          .map(async (f) => {
            const res = await api.get<{ data?: Record<string, unknown> }>(f.previewUrl!);
            return [f.key, res.data?.data?.[f.key]] as const;
          }),
      ).then((results) => {
        if (!alivePreview) return;
        const late: Record<string, unknown> = {};
        for (const r of results) {
          if (r.status === "fulfilled" && r.value[1] != null) late[r.value[0]] = r.value[1];
        }
        if (Object.keys(late).length) setValues((s) => ({ ...s, ...late }));
      });
      return () => {
        alivePreview = false;
      };
    }
    // Warmed by the hover prefetch: apply it and skip the request entirely.
    //
    // The values MUST be set here, not only in the useState seed above: that seed
    // runs once, while this effect re-runs whenever currentId changes. Walking to
    // another record with the prev/next arrows would otherwise keep showing the
    // PREVIOUS record's values. On the first mount this is a redundant set of
    // identical data - one extra render, nothing visibly changes.
    const warmed = !adapter && config.basePath ? readGet(`${config.basePath}/${currentId}`) : undefined;
    if (warmed && typeof warmed === "object") {
      setValues(toValues(config, warmed as Record<string, unknown>));
      setRawRecord(warmed as Record<string, unknown>);
      setLoading(false);
      return;
    }

    let alive = true;
    setLoading(true);
    const fetchRecord = async (): Promise<Record<string, unknown>> => {
      if (adapter) return adapter.load(currentId);
      const res = await api.get<{ data?: Record<string, unknown> }>(`${config.basePath}/${currentId}`);
      return res.data.data ?? {};
    };
    fetchRecord()
      .then((d) => {
        if (!alive) return;
        setValues(toValues(config, d));
        setRawRecord(d);
      })
      .catch(() => {
        if (alive) setError(`Could not load this ${config.entityLabel.toLowerCase()}.`);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [config, adapter, currentId, reloadToken]);

  const fieldEmpty = (f: CrudField) =>
    f.type === "multiselect"
      ? !Array.isArray(values[f.key]) || (values[f.key] as unknown[]).length === 0
      : String(values[f.key] ?? "").trim() === "";
  const missingRequired = useMemo(
    () => config.fields.filter((f) => f.required && f.type !== "readonly" && fieldEmpty(f)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.fields, values],
  );
  const isEmpty = (f: CrudField) => !!f.required && f.type !== "readonly" && fieldEmpty(f);
  /** The value a field displays: a read-only "days until" field is computed live from its date field
   *  (due date − today, 0 when no date — G5 getter), everything else is the stored form value. */
  /** A select's options, plus the record's stored value when the list hides it (opt-in `storedLabelKey`). */
  const optionsOf = (f: CrudField): Option[] => {
    let list = options[f.key] ?? [];
    // ciValue: the stored FK and the option ids can differ only in GUID case (an
    // UPPERCASE CAST(Oid AS NVARCHAR) id vs a lowercase JSON GUID). The read-only
    // native <select> matches case-SENSITIVELY, so rewrite the one matching option's
    // id to the stored value's exact string — then it preselects in view mode too.
    // Opt-in per field (crm-unfollowed-prospects.topicId); inert everywhere else.
    if (f.ciValue) {
      const sv = String(values[f.key] ?? "");
      if (sv) list = list.map((o) => (String(o.id).toLowerCase() === sv.toLowerCase() ? { ...o, id: sv } : o));
    }
    const label = f.storedLabelKey ? String(values[f.storedLabelKey] ?? "") : "";
    const stored = String(values[f.key] ?? "");
    if (!label || !stored || list.some((o) => String(o.id).toLowerCase() === stored.toLowerCase())) return list;
    return [{ id: stored, name: label }, ...list];
  };
  const shownValue = (f: CrudField): unknown => {
    if (!f.daysUntilKey) return values[f.key];
    const d = String(values[f.daysUntilKey] ?? "").slice(0, 10);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
    if (!m) return "0";
    const now = new Date();
    const due = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!);
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    return String(Math.round((due - today) / 86_400_000));
  };

  // Prev/next within the current list page.
  const idx = currentId ? siblingIds.indexOf(currentId) : -1;
  const prevId = idx > 0 ? siblingIds[idx - 1] : null;
  const nextId = idx >= 0 && idx < siblingIds.length - 1 ? siblingIds[idx + 1] : null;

  /** The all-zero Guid: see `emptyMeansClear`. */
  const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

  const buildPayload = (): Record<string, unknown> => {
    const payload: Record<string, unknown> = {};
    for (const f of config.fields) {
      if (f.inlineType === "multiselect" && f.optionsUrl) {
        // A popup field is sent only when the user picked in it, so untouched links stay as they are.
        if (Array.isArray(values[`${f.key}Ids`])) payload[f.key] = values[`${f.key}Ids`];
        continue;
      }
      if (f.type === "readonly" || f.type === "spacer" || f.type === "section") continue;
      const v = values[f.key];
      // A select with `idKey` posts the picked option's id under THAT key, because
      // that is what the API binds (InventoryItemInput.VendorOid, not `vendor`).
      // Posting it under `key` left every FK unbound -> null server-side, which the
      // required-Vendor check then rejected outright: the form could not save.
      const outKey = f.type === "select" && f.idKey ? f.idKey : f.key;
      if (f.type === "checkbox") payload[outKey] = Boolean(v);
      // An RTF document goes back byte-for-byte: trimming it would make an untouched
      // document look edited, and the server would rewrite it.
      else if (f.type === "richdoc") payload[outKey] = v == null || v === "" ? null : String(v);
      else if (f.type === "multiselect" || f.type === "assocgrid") payload[outKey] = Array.isArray(v) ? v : [];
      else if (f.type === "number" || (f.type === "select" && f.numericValue)) {
        // Send a real JSON number. Posting the string "90" would fail to bind to
        // an int? server-side. Blank or non-numeric becomes null = "not supplied".
        // A numeric-enum select (e.g. Sample Status Color) posts its int the same way.
        const s = v == null ? "" : String(v).trim();
        const n = s === "" ? null : Number(s);
        payload[outKey] = n != null && Number.isFinite(n) ? n : null;
      } else {
        const s = v == null ? "" : String(v).trim();
        // emptyMeansClear: an EMPTY pick is an instruction to clear the stored
        // value, not an omission. Posting null would be indistinguishable from
        // "field not supplied" on an API whose null means "leave it alone", so
        // the empty Guid is sent as the explicit clear (owner report 2026-10-28:
        // picking "— None —" on a holding time saved nothing).
        payload[outKey] = s === "" ? (f.emptyMeansClear ? EMPTY_GUID : null) : s;
      }
    }
    return payload;
  };

  /** Returns the record's id after a successful save (the newly created one
   *  for a New), or null if the save did not go through - the Permit Limits
   *  grid uses it to attach a child to a record that had not been saved yet. */
  /**
   * Writes the child rows captured while the parent had no id yet.
   *
   * AWAITED, and called from inside the save step rather than from onSaved.
   * onSaved is also what sets `currentId`, and the child grid reloads the moment
   * it does - so firing these as `void api.post(...)` there meant the reload's
   * GET raced the POSTs (and against a ~280 ms remote database, beat them). The
   * grid came back empty even when every write eventually succeeded.
   *
   * A row that FAILS stays in the buffer instead of being cleared with the rest:
   * the parent is already committed, and silently dropping a limit the user
   * entered is worse than leaving it visibly pending. A child failure never
   * rolls back the parent.
   */
  const writePendingChildren = async (parentId: string) => {
    const stillPending: Record<string, Record<string, unknown>[]> = {};
    for (const grid of config.childGrids ?? []) {
      const child = MODULE_CRUD[grid.childKey];
      const queued = pendingChildren[grid.title] ?? [];
      if (!child || queued.length === 0) continue;
      const failed: Record<string, unknown>[] = [];
      for (const row of queued) {
        // Display-only labels never reach the server - the body posted here is
        // byte-identical to what a saved-parent dialog would send.
        const body = { ...row };
        delete body[PENDING_LABELS_KEY];
        try {
          // Resolve a "{id}" parent template in the child basePath (nested CRUD routes).
          const base = child.basePath!.includes("{id}") ? child.basePath!.replace("{id}", parentId) : child.basePath!;
          await api.post(base, { ...body, [grid.parentField]: parentId, ...(grid.fixedValues ?? {}) });
        } catch {
          failed.push(row);
        }
      }
      if (failed.length > 0) {
        stillPending[grid.title] = failed;
        notify.error(`${failed.length} of ${queued.length} ${grid.title} could not be saved — still pending.`);
      }
    }
    setPendingChildren(stillPending);
  };

  const doSave = async (after: AfterSave): Promise<string | null> => {
    setTriedSave(true);
    if (missingRequired.length > 0) {
      // Field-level required validation is shown INLINE under each field + the
      // required-empty highlight (§18.6 standard) — not a page banner.
      setError(null);
      return null;
    }
    // A buffered child area is checked BEFORE the confirm — the point where the
    // legacy detail cancelled the Save with its warning.
    const childError = bufferedChildRef.current?.validate() ?? null;
    if (childError) {
      notify.warning(childError);
      return null;
    }
    setError(null);
    setSaving(true);
    const wasEditing = editing;
    let createdId: string | null = null;
    // Shared save flow: confirm → audited handler (POST/PUT) → "Saved
    // successfully." Create is reason-exempt; Edit gates the reason (rule 11).
    await runSave({
      formName: config.formName,
      auditAction: wasEditing ? "edit" : "create",
      successMessage: config.saveSuccessMessage ?? `${config.entityLabel} saved successfully.`,
      save: async () => {
        // Nothing to write (every field read-only): G5's Correction Verification Save commits nothing yet still acknowledges.
        if (config.detailSaveNoop) return;
        const payload = { ...buildPayload(), ...(bufferedChildRef.current?.payload() ?? {}) };
        if (wasEditing) {
          if (adapter) await adapter.update(currentId!, payload);
          else await api.put(`${config.basePath}/${currentId}`, payload);
        } else if (adapter) {
          createdId = await adapter.create(payload);
        } else {
          const res = await api.post(config.basePath!, payload);
          createdId = createdIdFrom(res.data, config.idField);
        }
        // Buffered children are written HERE, still inside the awaited save, so
        // they exist before onSaved sets currentId and the grid reloads.
        if (!wasEditing && createdId) await writePendingChildren(createdId);
        // Never drop queued child rows silently: without the new id they cannot be attached.
        else if (!wasEditing && Object.values(pendingChildren).some((rows) => rows.length > 0))
          notify.error("Saved, but the new record's id could not be read — the rows added to its grids were not saved. Open the record and add them again.");
        // Same for the Daily QC Log Book "Field Configuration" rows chosen and
        // edited while the template had no id yet.
        if (!wasEditing && createdId && dailyQcFlushRef.current) await dailyQcFlushRef.current(createdId);
      },
      onSaved: () => {
        // The prefetched copy of this record is now out of date - drop it, or
        // reopening the row would show pre-save values from the hover cache.
        invalidatePreviews();
        // Child rows were already written (awaited) in the save step above.
        if (!wasEditing && createdId) {
          // The previewed id has just been used up; re-warm for the next New.
          for (const f of config.fields.filter((x) => x.previewUrl)) {
            prefetchPreview(f.previewUrl!, f.key);
          }
        }
        dirtyRef.current = false; // persisted — leaving now must not warn
        if (config.itemTestsGrid || bufferedChild) setChildReload((n) => n + 1);
        onSaved(); // refresh the list behind the detail
        if (after === "close") {
          onClose();
          return;
        }
        if (after === "new") {
          setCurrentId(null);
          setValues(withNewDefaults(emptyValues(config, useAuthStore.getState().user?.email ?? "", useAuthStore.getState().user?.name ?? ""), newRecordDefaults));
          setRawRecord({});
          setTriedSave(false);
          setMode("edit");
          setFormNonce((n) => n + 1); // re-apply defaultFirstOption on the blank form
          return;
        }
        // 'stay' — remain on the saved record. Back to read-only VIEW mode,
        // unless the form always opens editable (detailOpenInEdit), matching the
        // legacy XAF detail that stays editable after Save.
        const afterMode = config.detailOpenInEdit ? "edit" : "view";
        if (!wasEditing) {
          if (createdId) {
            setCurrentId(createdId);
            setMode(afterMode);
          } // reload
          else onClose(); // couldn't resolve the new id → safest: return to list
        } else {
          setMode(afterMode);
          if (config.reloadAfterSave) doRefresh();
        }
      },
      onError: (e) => {
        const msg =
          (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ??
          "Save failed.";
        setError(msg);
        notify.error(msg);
      },
    });
    setSaving(false);
    return createdId ?? currentId;
  };

  // Header record name = the caption field's value (config.titleField, mirroring
  // the legacy XAF [DefaultProperty]; falls back to the first field). For a FK
  // 'select' caption field (e.g. Bottle Sharing's Sample Matrix, Assign
  // Dashboard's Dashboard View), resolve the stored id → its option label so the
  // header/breadcrumb reads the name, not a raw GUID. When titleField is set the
  // caption shows even on a NEW record once the field has a value.
  const captionField = config.fields.find((f) => f.key === config.titleField) ?? config.fields[0];
  const captionVal = values[captionField?.key ?? ""];
  const captionDisplay =
    captionField?.type === "select"
      ? ((options[captionField.key] ?? []).find((o) => o.id === String(captionVal ?? ""))?.name ?? "")
      : String(captionVal ?? "");
  const recordName = editing
    ? captionDisplay || config.entityLabel
    : config.titleField && captionDisplay
      ? captionDisplay
      : `New ${config.entityLabel}`;

  // Detail toolbar is config-driven (per-form): Edit shows unless
  // detailHideEdit; Delete shows for an existing REST record when
  // detailShowDelete. Delete uses the shared flow (danger confirm → reason gate
  // → soft delete → "Deleted.") then returns to the list.
  const showEdit = !config.detailHideEdit;
  const canDelete = !!config.detailShowDelete && editing && !!config.basePath && !adapter;
  // Legacy detail-toolbar New / Refresh (opt-in: detailShowNew / detailShowRefresh).
  const canNew = !!config.detailShowNew && editing && !adapter;
  const canRefresh = !!config.detailShowRefresh && editing;
  /** New from the open record: a blank record in the same page, exactly as Save and New leaves it. */
  const startNew = () => {
    setCurrentId(null);
    setValues(emptyValues(config, useAuthStore.getState().user?.email ?? ""));
    setRawRecord({});
    setPendingChildren({});
    setTriedSave(false);
    setError(null);
    setPageTab(0);
    setMode("edit");
    setFormNonce((n) => n + 1); // re-apply defaultFirstOption on the blank form
  };
  /** Refresh: re-read the record and its child grids from the server, skipping the hover cache. */
  const doRefresh = () => {
    invalidatePreviews();
    setError(null);
    setReloadToken((n) => n + 1);
  };
  /* What a field-lock rule is judged against: the record as the SERVER
     returned it, the user's in-progress edits on top, and whether this record
     is saved at all. A rule that needs only form fields is unaffected. */
  const lockCtx = { ...rawRecord, ...values, __savedId: currentId };

  const doDelete = async () => {
    if (!canDelete || currentId === null) return;
    // A form may add a sentence saying what this record carries, so a
    // record-level Delete cannot be mistaken for a narrower action on the same
    // screen. Its failure must not block the delete — the warning is help, not
    // a gate — so it is tracked by the shared axios handling and ignored here.
    let warning: string | null = null;
    try { warning = (await config.deleteWarning?.(currentId)) ?? null; } catch { warning = null; }
    await runDelete({
      formName: config.formName,
      message: `Delete ${recordName}? This cannot be undone.${warning ? ` ${warning}` : ""}`,
      delete: async () => {
        await api.delete(`${config.basePath}/${currentId}`);
      },
      onDeleted: () => {
        dirtyRef.current = false;
        invalidatePreviews();
        onSaved();
        onClose();
      },
    });
  };

  /** Run one record action: shared confirm -> POST -> shared success -> navigate.
   *  Deliberately reuses the same confirm/notify services as Save and Delete
   *  (standing rule 7), so it cannot drift into a bespoke dialog or toast. */
  const runRecordAction = async (action: CrudRecordAction) => {
    // A form-values probe (e.g. DB Connection "Test Connection") posts the CURRENT
    // field values to `${basePath}/${path}` and works BEFORE the record is saved
    // (no currentId needed). It changes nothing, so it skips the save flow, the
    // list refresh and any navigation — just confirm → POST → show the result.
    if (action.sendFormValues) {
      if (!config.basePath) return;
      const ok = await confirm({
        title: action.confirmTitle,
        message: action.confirmMessage,
        confirmLabel: action.label,
      });
      if (!ok) return;
      setSaving(true);
      try {
        await api.post(`${config.basePath}/${action.path}`, buildPayload());
        notify.success(action.successMessage.replace(/\s{2,}/g, " "));
      } catch (e) {
        const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
        notify.error(msg ?? `${action.label} failed.`);
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!currentId && action.warnOnNew) {
      notify.warning(action.warnOnNew);
      return;
    }
    if (!currentId) return;

    // An action marked saveFirst depends on this record's field values, which the
    // server reads from the STORED row - so the form is committed first, in the
    // SAME shared save flow as the action (one reason gate, one confirm, one
    // blocking loader, one acknowledgement). Required fields are checked exactly
    // as Save checks them, so the action cannot write a half-filled record.
    if (action.saveFirst && !adapter && config.basePath) {
      setTriedSave(true);
      if (missingRequired.length > 0) {
        setError(`Required: ${missingRequired.map((f) => f.label).join(", ")}.`);
        return;
      }
      setError(null);
    }

    // An action that must be explained asks for its reason in the SAME shared
    // dialog that confirms it (the prompt variant), and posts it for the handler
    // to audit. Cancel — or an empty box, which the dialog does not let through —
    // runs nothing.
    let actionBody: Record<string, unknown> = {};
    if (!action.saveFirst) {
      if (action.reasonPrompt) {
        const reason = await prompt({
          title: action.confirmTitle,
          message: action.confirmMessage,
          placeholder: action.reasonPrompt.placeholder ?? "Reason",
          required: true,
          confirmLabel: action.label,
        });
        if (!reason?.trim()) return;
        actionBody = { reason: reason.trim() };
      } else {
        const ok = await confirm({
          title: action.confirmTitle,
          message: action.confirmMessage,
          confirmLabel: action.label,
        });
        if (!ok) return;
      }
    }

    setSaving(true);
    try {
      let data: Record<string, unknown> = {};
      let ran = !action.saveFirst;
      if (action.saveFirst) {
        // runSave supplies the confirm, the reason gate and the acknowledgement;
        // the PUT and the action POST are one unit of work inside it, so a failed
        // action never leaves the user thinking only the save happened.
        ran = await runSave({
          formName: config.formName,
          auditAction: "edit",
          confirmTitle: action.confirmTitle,
          confirmMessage: action.confirmMessage,
          // successDialog, not successMessage: it is evaluated AFTER the save,
          // which is the only point the minted id ({id}) exists.
          successDialog: () => ({
            type: "success",
            title: "Success",
            message: action.successMessage
              .replace("{id}", String(data.permitId ?? data.id ?? ""))
              .replace(/\s{2,}/g, " "),
          }),
          save: async () => {
            if (!adapter && config.basePath) {
              await api.put(`${config.basePath}/${currentId}`, buildPayload());
            }
            const r = await api.post<{ data?: Record<string, unknown> }>(
              `${config.basePath}/${currentId}/${action.path}`,
              {},
            );
            data = r.data?.data ?? {};
          },
          onError: (e) => {
            const m = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
            setError(m ?? null);
            notify.error(m ?? `${action.label} failed.`);
          },
        });
        if (!ran) return; // cancelled, or runSave already reported the failure
      } else {
        const res = await api.post<{ data?: Record<string, unknown> }>(
          `${config.basePath}/${currentId}/${action.path}`,
          actionBody,
        );
        data = res.data?.data ?? {};
      }
      // The action rewrote this record and usually a related one; every cached
      // copy is now stale, including the one we are about to navigate to.
      invalidatePreviews();
      // saveFirst already acknowledged through the shared save flow - a second
      // message here would double up.
      if (!action.saveFirst)
        notify.success(action.successMessage.replace("{id}", String(data.permitId ?? data.id ?? "")));

      onSaved(); // the list behind the detail is stale either way
      const target = action.navigateToField ? data[action.navigateToField] : null;
      if (target) onNavigate(String(target));
      else if (action.closeAfter) onClose();
      else setReloadToken((n) => n + 1);
    } catch (e) {
      // A 409 here is EXPECTED and user-facing (already renewed) rather than a
      // fault, so the server's own message is surfaced instead of a generic error.
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      if (msg) notify.error(msg);
      else throw e;
    } finally {
      setSaving(false);
    }
  };

  // Actions apply to a SAVED record only (they act on something that must already
  // exist) — EXCEPT a sendFormValues probe, which tests the entered values and so
  // shows even on a new, unsaved record. Each may still hide itself for this row.
  const recordActions = (config.recordActions ?? []).filter(
    (a) => (a.sendFormValues || a.warnOnNew || a.disabledOnNew || currentId) && (!a.visibleWhen || a.visibleWhen(values)),
  );
  // Rendered in BOTH action bars; placement (before Edit vs after Delete) is the
  // caller's recordActionsFirst flag.
  const recordActionButtons = recordActions.map((a) => (
    <button
      key={a.key}
      className={`sd-abtn${a.primary ? " primary" : ""}`}
      onClick={() => void runRecordAction(a)}
      disabled={saving || loading || (!!a.disabledOnNew && !currentId)}
    >
      <span className="tb-i">{a.icon ?? "⚙"}</span> {a.label}
    </button>
  ));

  // Glyphs = the EXACT BO UI Standard prototype .abtn icons (💾 💾 📄 ↩︎).
  // Save action set — XAF-canonical order & wording (BO UI Standard §4, decision
  // 2026-07-08): Save · Save and Close · Save and New · | · Cancel. Words spelled
  // "and" (never "&"), a thin divider precedes Cancel (XAF parity).
  // VIEW mode (ALPACA parity): Edit (blue ✎) + Close — no Save/Cancel.
  // Project Notes (BO UI Standard §8) — rendered FIRST on the bar, before the
  // record actions, because it is a companion to the record rather than one of
  // its verbs. Gated on the record existing: on a New form there is nothing to
  // attach a note to, and the button says so instead of vanishing.
  const projectNotes = config.projectNotesBase ? (
    <ProjectNotesButton
      className="sd-abtn"
      recordId={currentId}
      baseUrl={currentId ? config.projectNotesBase.replace("{id}", currentId) : null}
      // The RECORD's own label (the header's "Document – DOC1005"), not the page
      // title: the popup shows it as the note's Source ID, so "Document Creator"
      // there would name the form instead of the record the note is about.
      recordLabel={recordName}
      recordSubLabel={config.entityLabel}
      businessObject={config.formName}
      readOnly={!!config.projectNotesReadOnly}
    />
  ) : null;

  /** Opt-in TWO-GROUP bar (CLAUDE.md rule 12: a deviation lands as a SHARED
   *  opt-in on the shared component, never a per-form override).
   *
   *  The default bar is one right-aligned run. A form whose legacy/prototype bar
   *  separates "what you can do to this RECORD" from "how you leave the form"
   *  sets `detailActionsSplit`: the record actions (Audit Trail, the form's own
   *  recordActions, Delete, Refresh) go LEFT and the Save group stays RIGHT.
   *  Nothing else changes — same shared buttons, same handlers, same hotkeys.
   *  Only Test Code passes it (owner request 2026-10-13, prototype v12). */
  const split = !!config.detailActionsSplit;

  /** Left group of the split bar. Rendered in BOTH bars, as the prototype does. */
  const LeftGroup = () => (
    <div className="sd-ab-left">
      {config.formName && currentId && (
        <AuditTrailButton formName={config.formName} sourceId={currentId} label="Audit Trail" />
      )}
      {canNew && (
        <button className="sd-abtn" onClick={leave(startNew)} disabled={loading}>
          <span className="tb-i tb-i-green">＋</span> New
        </button>
      )}
      {/* G5 keeps New and Refresh live on a NEW record (opt-in detailBarOnNew); Delete stays visible but greyed. */}
      {!editing && !!config.detailBarOnNew && !!config.detailShowNew && !adapter && (
        <button className="sd-abtn" onClick={leave(startNew)} disabled={loading}>
          <span className="tb-i tb-i-green">＋</span> New
        </button>
      )}
      {!config.detailRecordActionsLast && recordActionButtons}
      {currentId && recordActionsSlot?.(currentId, viewOnly, doRefresh)}
      {/* Delete is normally VIEW-mode only (BO UI Standard §4, owner request
          2026-09-30). The split bar shows it in edit mode too, because the
          prototype's bar does — scoped to the opting-in form. */}
      {!!config.detailShowDelete && !!currentId && !!config.basePath && !adapter && (
        <button className="sd-abtn danger" onClick={doDelete} disabled={saving || loading}>
          <span className="tb-i tb-i-red">🗑</span> Delete
        </button>
      )}
      {!editing && !!config.detailBarOnNew && !!config.detailShowDelete && !!config.basePath && !adapter && (
        <button className="sd-abtn danger" disabled>
          <span className="tb-i tb-i-red">🗑</span> Delete
        </button>
      )}
      {!editing && !!config.detailBarOnNew && !!config.detailShowRefresh && (
        <button className="sd-abtn" onClick={leave(startNew)} disabled={loading} title="Start over with a blank record">
          <span className="tb-i tb-i-green">🔄</span> Refresh
        </button>
      )}
      {currentId && (
        <button
          className="sd-abtn"
          onClick={() => setReloadToken((n) => n + 1)}
          disabled={saving || loading}
          title="Re-read this record from the server"
        >
          {config.detailActionsSplit ? <span className="tb-i tb-i-green">🔄</span> : <span className="tb-i">⟳</span>} Refresh
        </button>
      )}
      {config.detailRecordActionsLast && recordActionButtons}
    </div>
  );

  const ActionBar = ({ bottom }: { bottom?: boolean }) =>
    split ? (
      <div className={`sd-actionbar sd-actionbar--split${bottom ? " bottom" : ""}`}>
        <LeftGroup />
        <div className="sd-ab-right">
          {viewOnly ? (
            <>
              {showEdit && (
                <button className="sd-abtn primary" onClick={() => setMode("edit")} disabled={loading}>
                  <span className="tb-i tb-i-blue">✎</span> Edit
                </button>
              )}
              <button className="sd-abtn cancel" onClick={leave(onClose)}>
                <span className="tb-i">↩︎</span> Close
              </button>
            </>
          ) : (
            <>
              <button className="sd-abtn primary" onClick={() => doSave("stay")} disabled={saving || loading}>
                {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
              </button>
              <button className="sd-abtn" onClick={() => doSave("close")} disabled={saving || loading}>
                <span className="tb-i">💾</span> Save and Close
              </button>
              {!config.detailHideSaveNew && (
                <button className="sd-abtn" onClick={() => doSave("new")} disabled={saving || loading}>
                  <span className="tb-i">📄</span> Save and New
                </button>
              )}
              <span className="sd-abtn-sep" aria-hidden="true" />
              <button className="sd-abtn cancel" onClick={leave(onClose)} disabled={saving}>
                <span className="tb-i">↩︎</span> Cancel
              </button>
            </>
          )}
        </div>
      </div>
    ) : (
    <div className={`sd-actionbar${bottom ? " bottom" : ""}`}>
      {projectNotes}
      {viewOnly ? (
        <>
          {/* A form whose legacy bar places its form-specific action AHEAD of the
              standard ones opts in via recordActionsFirst (e.g. DB Connection's
              "Test Connection" before Edit) — same shape as AppToolbar newFirst. */}
          {config.recordActionsFirst && recordActionButtons}
          {canNew && (
            <button className="sd-abtn" onClick={leave(startNew)} disabled={loading}>
              <span className="tb-i tb-i-green">＋</span> New
            </button>
          )}
          {/* VIEW-mode-only form action placed immediately BEFORE Edit — Open / My Open Prospects'
              "Close Prospects" (owner report 2026-10-28, image 1043). Saved record only. */}
          {currentId && recordActionsSlotBeforeEdit?.(currentId)}
          {showEdit && (
            <button className="sd-abtn primary" onClick={() => setMode("edit")} disabled={loading}>
              <span className="tb-i tb-i-blue">✎</span> Edit
            </button>
          )}
          {canDelete && (
            <button className="sd-abtn danger" onClick={doDelete} disabled={loading}>
              <span className="tb-i tb-i-red">🗑</span> Delete
            </button>
          )}
          {/* ALPACA: present in view mode but not usable until Edit is pressed. */}
          {config.stockQtyEdit && (
            <button className="sd-abtn" disabled title="Press Edit to use Stock Qty Edit">
              <span className="tb-i tb-i-blue">✎</span> Stock Qty Edit
            </button>
          )}
          {!config.recordActionsFirst && recordActionButtons}
          {currentId && recordActionsSlot?.(currentId, viewOnly, doRefresh)}
          {canRefresh && (
            <button className="sd-abtn" onClick={doRefresh} disabled={loading}>
              <span className="tb-i tb-i-green">🔄</span> Refresh
            </button>
          )}
          <button className="sd-abtn cancel" onClick={leave(onClose)}>
            <span className="tb-i">↩︎</span> Close
          </button>
        </>
      ) : (
        <>
          {/* recordActionsFirst → a form-specific action (e.g. Test Connection) sits
              BEFORE Save (owner request); otherwise it follows the Save trio. */}
          {config.recordActionsFirst && recordActionButtons}
          <button className="sd-abtn primary" onClick={() => doSave("stay")} disabled={saving || loading}>
            {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
          </button>
          <button className="sd-abtn" onClick={() => doSave("close")} disabled={saving || loading}>
            <span className="tb-i">💾</span> Save and Close
          </button>
          {!config.detailHideSaveNew && (
            <button className="sd-abtn" onClick={() => doSave("new")} disabled={saving || loading}>
              <span className="tb-i">📄</span> Save and New
            </button>
          )}
          {/* No Delete in EDIT mode — BO UI Standard §4 / rule 12: edit mode is the
              Save trio + Cancel only. Delete lives in VIEW mode (Edit · Delete · Close),
              so a record is deleted from the view it is read in, not mid-edit (owner
              request 2026-09-30). */}
          {config.stockQtyEdit && (
            <button className="sd-abtn" onClick={() => setStockQtyOpen(true)}
              disabled={saving || loading || currentId === null}>
              <span className="tb-i tb-i-blue">✎</span> Stock Qty Edit
            </button>
          )}
          {!config.recordActionsFirst && recordActionButtons}
          {currentId && recordActionsSlot?.(currentId, viewOnly, doRefresh)}
          <span className="sd-abtn-sep" aria-hidden="true" />
          <button className="sd-abtn cancel" onClick={leave(onClose)} disabled={saving}>
            <span className="tb-i">↩︎</span> Cancel
          </button>
        </>
      )}
    </div>
    );

  // Keyboard shortcuts (CLAUDE.md standing rule 14, §18.6, ADR-0013): the Save
  // trio, Cancel/Close, Delete and record prev/next — so every detail opened
  // through this shared page is keyboard-drivable with no per-form code.  Esc is
  // allowInInput:false so it never discards edits while typing in a field (it
  // closes when focus is on a button/the page); the store already yields Esc to
  // any open confirm/prompt dialog (aria-modal).  In view mode Ctrl+S / F2 open
  // Edit (ALPACA parity).  Each binding mirrors its button's disabled state.
  const detailKeys: HotkeyDef[] = [];
  if (viewOnly) {
    if (showEdit) {
      detailKeys.push({
        combo: "mod+s",
        label: "Edit",
        group: "Detail",
        order: 1,
        disabled: loading,
        run: () => setMode("edit"),
      });
      detailKeys.push({
        combo: "alt+e",
        label: "Edit",
        group: "Detail",
        order: 1,
        disabled: loading,
        run: () => setMode("edit"),
      });
      detailKeys.push({
        combo: "f2",
        label: "Edit",
        group: "Detail",
        order: 1,
        disabled: loading,
        hidden: true,
        run: () => setMode("edit"),
      });
    }
    if (canDelete)
      detailKeys.push({
        combo: "alt+d",
        label: "Delete",
        group: "Detail",
        order: 4,
        disabled: loading,
        run: () => {
          void doDelete();
        },
      });
    if (canNew)
      detailKeys.push({
        combo: "alt+n",
        label: "New",
        group: "Detail",
        order: 0,
        disabled: loading,
        run: () => leave(startNew)(),
      });
    if (canRefresh)
      detailKeys.push({
        combo: "alt+r",
        label: "Refresh",
        group: "Detail",
        order: 5,
        disabled: loading,
        run: doRefresh,
      });
    detailKeys.push({
      combo: "esc",
      label: "Close",
      group: "Detail",
      order: 9,
      allowInInput: false,
      run: () => onClose(),
    });
  } else {
    detailKeys.push({
      combo: "mod+s",
      label: "Save",
      group: "Detail",
      order: 1,
      disabled: saving || loading,
      run: () => {
        void doSave("stay");
      },
    });
    if (!config.detailHideSaveNew)
      detailKeys.push({
        combo: "mod+shift+s",
        label: "Save and New",
        group: "Detail",
        order: 2,
        disabled: saving || loading,
        run: () => {
          void doSave("new");
        },
      });
    detailKeys.push({
      combo: "mod+enter",
      label: "Save and Close",
      group: "Detail",
      order: 3,
      disabled: saving || loading,
      run: () => {
        void doSave("close");
      },
    });
    if (canDelete)
      detailKeys.push({
        combo: "alt+d",
        label: "Delete",
        group: "Detail",
        order: 4,
        disabled: saving || loading,
        run: () => {
          void doDelete();
        },
      });
    detailKeys.push({
      combo: "esc",
      label: "Cancel",
      group: "Detail",
      order: 9,
      disabled: saving,
      allowInInput: false,
      run: () => {
        if (!saving) onClose();
      },
    });
  }
  detailKeys.push({
    combo: "alt+left",
    label: "Previous record",
    group: "Detail",
    order: 7,
    disabled: !prevId || saving,
    run: () => {
      if (prevId && !saving) onNavigate(prevId);
    },
  });
  detailKeys.push({
    combo: "alt+right",
    label: "Next record",
    group: "Detail",
    order: 8,
    disabled: !nextId || saving,
    run: () => {
      if (nextId && !saving) onNavigate(nextId);
    },
  });
  useHotkeys(detailKeys);

  return (
    <div className="bl-page mlv-host">
      <div className="bl-grid-area">
        <div className={`sd-wrap${detailClassName ? ` ${detailClassName}` : ""}`} ref={wrapRef}>
          {/* Header — title + breadcrumb + prev/next record arrows */}
          <div className="sd-head">
            <div>
              <h2>
                {config.entityLabel} – {recordName}
              </h2>
              <div className="sd-bc">
                <a onClick={leave(onHome)}>Home</a>
                <i className="ti ti-chevron-right" />
                <span>{group}</span>
                <i className="ti ti-chevron-right" />
                <a onClick={leave(onClose)}>{title}</a>
                <i className="ti ti-chevron-right" />
                <span>{recordName}</span>
              </div>
            </div>
            <div className="sd-navarrows">
              <button
                className="sd-na"
                title="Previous record"
                disabled={!prevId || saving}
                onClick={() => prevId && leave(() => onNavigate(prevId))()}
              >
                ‹
              </button>
              <button
                className="sd-na next"
                title="Next record"
                disabled={!nextId || saving}
                onClick={() => nextId && leave(() => onNavigate(nextId))()}
              >
                ›
              </button>
            </div>
          </div>

          {currentId && <Fragment key={reloadToken}>{headerSlot?.(currentId)}</Fragment>}

          <ActionBar />
          {config.stockQtyEdit && stockQtyOpen && currentId !== null && (
            <StockQtyEditDialog
              itemIds={[currentId]}
              onClose={() => setStockQtyOpen(false)}
              onSaved={() => {
                setStockQtyOpen(false);
                // The record changed on the server (StockQty) — re-read it, so a later Save cannot write the old
                // value back over the new one (ALPACA refreshes the object space after the editor too).
                doRefresh();
              }}
            />
          )}

          <div
            className={
              `sd-body` +
              `${aside || config.fields.some((f) => f.type === "datasource") ? " sd-body--aside" : ""}` +
              // Datasource aside aligns its top with the FIRST field (Division), not the
              // "General Information" heading — owner request 2026-09-30.
              `${!aside && config.fields.some((f) => f.type === "datasource") ? " sd-body--ds" : ""}`
            }
          >
            {loading ? (
              <div className="sd-loading">
                <LoaderSymbol size={20} /> Loading…
              </div>
            ) : (
              <>
                {/* Non-validation errors only (load / save failure). Field-level
                    required validation shows INLINE per field (§18.6 standard),
                    not as a banner here. */}
                {error && (
                  <div className="sd-error sd-error--top" role="alert">
                    <i className="ti ti-alert-triangle" />
                    <span>{error}</span>
                  </div>
                )}
                {/* fieldTabs (opt-in): the legacy XAF top-level TabbedGroup — "General
                    Information" plus one tab per moved field (Technical Writer's
                    Article). The tab caption replaces the section heading. */}
                {(config.fieldTabs?.length ?? 0) > 0 && (
                  <div className="sd-assoc-tabbar sd-pagetabbar" role="tablist">
                    {["General Information", ...config.fieldTabs!.map((t) => t.title)].map((t, i) => (
                      <button
                        key={t}
                        type="button"
                        role="tab"
                        aria-selected={i === pageTab}
                        className={`sd-assoc-tab${i === pageTab ? " active" : ""}`}
                        onClick={() => setPageTab(i)}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                )}
                {config.fieldTabs?.map((t, ti) => (
                  <div
                    key={t.title}
                    role="tabpanel"
                    className={`sd-pagetab sd-pagetab-panel${config.squareControls ? " sd-pagetab--square" : ""}`}
                    hidden={pageTab !== ti + 1}
                  >
                    {config.fields
                      .filter((f) => t.fields.includes(f.key))
                      .map((f) => (
                        <Field
                          key={f.key}
                          field={f}
                          value={values[f.key]}
                          options={optionsOf(f)}
                          showEmpty={!viewOnly && triedSave && isEmpty(f)}
                          readOnly={viewOnly || !!f.readOnly}
                          hideLabel
                          viewHtml={f.htmlKey ? String(values[f.htmlKey] ?? "") : undefined}
                          onChange={(v) => set(f.key, v)}
                        />
                      ))}
                  </div>
                ))}
                {/* Everything else is the General Information page. display:contents
                    keeps its children laid out exactly as without the wrapper, so a
                    form with no fieldTabs is unaffected. */}
                <div
                  className={`sd-pagetab-main${config.fieldTabs?.length ? " sd-pagetab-panel" : ""}`}
                  role={config.fieldTabs?.length ? "tabpanel" : undefined}
                  hidden={pageTab !== 0}
                >
                {!config.hideSectionHeader && !config.fieldTabs?.length && (
                  <h3 className="sd-section-h">General Information</h3>
                )}
                {/* COLUMN-major needs an explicit row count: CSS grid fills down
                    the first column only when it knows how tall a column is. Half
                    the fields (rounded up) go in the left column. */}
                {/* RESTORED 2026-09-24: the three class expressions below had been
                    replaced by empty template literals (`+ ``` x4) — almost certainly
                    a botched conflict resolution in 021a6de ("Fix merge conflict
                    markers left in 5 frontend files"), which stripped the markers AND
                    the code between them. The layout CSS in SettingsDetailPage.css
                    keys ENTIRELY off these class names, so with them gone every
                    column-major / wide / horizontal-label layout silently rendered as
                    a plain grid: the config was set, the CSS existed, and nothing
                    connected the two. Recovered verbatim from 70fe43a. */}
                <div
                  className={
                    `sd-grid${config.singleColumn ? " sd-grid--single" : ""}` +
                    `${config.columnMajor && !config.singleColumn ? " sd-grid--colmajor" : ""}` +
                    `${config.sideLabels ? " sd-grid--side" : ""}` +
                    `${config.wideLayout ? " sd-grid--wide" : ""}` +
                    `${config.squareControls || forceSquareControls ? " sd-grid--square" : ""}` +
                    `${config.plainControls ? " sd-grid--plain" : ""}` +
                    `${config.compactControls ? " sd-grid--compact" : ""}` +
                    `${config.bigSectionHeaders ? " sd-grid--bigheads" : ""}` +
                    `${config.horizontalLabels ? " sd-grid--hlabels" : ""}`
                  }
                  style={{
                    ...(config.columnMajor && !config.singleColumn
                      ? ({
                          "--sd-rows": String(
                            config.columnRows ??
                              Math.ceil(
                                // Count only VISIBLE fields so an auto 2-column split
                                // (no columnRows) follows what the user actually sees —
                                // a conditional field (visibleWhen) must not reserve a
                                // row while hidden, or the columns go lopsided. No-op for
                                // forms without any field-level visibleWhen.
                                config.fields.filter((f) => f.type !== "assocgrid" && f.type !== "datasource" && !f.hideInLayout && !tabFieldKeys.has(f.key) && (!f.visibleWhen || f.visibleWhen(values))).length / 2,
                              ),
                          ),
                        } as CSSProperties)
                      : {}),
                    ...(config.detailMaxWidth ? { maxWidth: config.detailMaxWidth } : {}),
                    ...(config.horizontalLabelWidth
                      ? // The value was missing from this template literal, so it
                        // emitted the invalid CSS `px` and the override silently did
                        // nothing. (Fixed 2026-09-24.)
                        ({ "--sd-hl-w": `${config.horizontalLabelWidth}px` } as CSSProperties)
                      : {}),
                  }}
                >
                  {config.fields
                    // `visibleWhen` (e.g. Document Creator's Is Retire = existing-only,
                    // Reason for Retiring = shown once Is Retire is ticked) hides a field
                    // from the layout entirely; the count filter above matches, so the
                    // 2-column split stays balanced. No-op where no field declares it.
                    .filter((f) => f.type !== "assocgrid" && f.type !== "datasource" && !f.hideInLayout && !tabFieldKeys.has(f.key) && (!f.visibleWhen || f.visibleWhen(values)))
                    .map((f, i, arr) => {
                      // A checkbox keeps an empty label spacer so its box lands level
                      // with the INPUT beside it rather than with that input's label.
                      // When the field it is PAIRED with is also a checkbox there is
                      // no label to line up against, and the spacer only pushes both
                      // boxes down, leaving a band of dead space above them (owner,
                      // 2026-09-16). So it is dropped for that case alone.
                      //
                      // Column-major only, because that is where the pairing is
                      // knowable: `.full` spanning is disabled in that mode and the
                      // split is a clean ceil(n/2), so index i pairs with i ± rows.
                      // Row-major grids are deliberately left alone - a `.full` field
                      // shifts their pairing, so the partner cannot be derived from
                      // the index. An odd-length list leaves its last field with no
                      // partner, which keeps the spacer: the conservative answer.
                      const rows = Math.ceil(arr.length / 2);
                      const partner =
                        config.columnMajor && !config.singleColumn
                          ? i < rows
                            ? arr[i + rows]
                            : arr[i - rows]
                          : undefined;
                      const pairedWithCheckbox = f.type === "checkbox" && partner?.type === "checkbox";
                      return f.type === "section" ? (
                        // Full-width sub-section heading between field groups
                        // (Qualifier: Sample / QC / Surrogate Automation). Spans all
                        // columns; no input, never in the payload.
                        <h3 key={f.key} className="sd-section-h sd-subsection-h">
                          {f.label}
                        </h3>
                      ) : f.formulaBuilder ? (
                        // The legacy Reagent "Result Correction Formula" builder — a
                        // textarea plus a "Formula" button that opens the builder popup.
                        // Approach label + each variable's chosen unit are resolved here
                        // (the grid holds `values` + `options`) and handed to the field.
                        <FormulaBuilderField
                          key={f.key}
                          field={f}
                          value={values[f.key]}
                          readOnly={viewOnly || !!f.readOnly}
                          showEmpty={!viewOnly && triedSave && isEmpty(f)}
                          approachLabel={
                            (options[f.formulaBuilder.approachKey] ?? []).find(
                              (o) => o.id === String(values[f.formulaBuilder!.approachKey] ?? ""),
                            )?.name ?? ""
                          }
                          unitInfo={f.formulaBuilder.units.map((u) => {
                            const oid = String(values[u.key] ?? "");
                            const label =
                              (options[u.key] ?? []).find((o) => o.id === oid)?.name ??
                              String(values[`${u.key.replace(/Id$/, "")}Name`] ?? "");
                            return { variable: u.variable, unit: label, isSet: oid.trim() !== "" };
                          })}
                          operatorsUrl={f.formulaBuilder.operatorsUrl}
                          onChange={(v) => set(f.key, v)}
                        />
                      ) : f.inlineType === "multiselect" && f.optionsUrl ? (
                        // Checkbox popup field (G5 Parameter popup). Needs a saved record: its linked
                        // rows are looked up by the record id.
                        <MultiPickField
                          key={f.key}
                          label={f.label}
                          required={f.required}
                          text={String(values[f.key] ?? "")}
                          disabled={viewOnly || id === null}
                          optionsUrl={f.optionsUrl.replace("{id}", String(id ?? ""))}
                          picked={Array.isArray(values[`${f.key}Ids`]) ? (values[`${f.key}Ids`] as string[]) : undefined}
                          onApply={(ids, names) => {
                            set(`${f.key}Ids`, ids);
                            set(f.key, ids.map((x) => names[x] ?? x).join(", "));
                          }}
                        />
                      ) : f.type === "image" ? (
                        <ImageField
                          key={f.key}
                          field={f}
                          value={values[f.key]}
                          readOnly={viewOnly}
                          onChange={(b64) => set(f.key, b64)}
                        />
                      ) : f.type === "file" ? (
                        <FileField
                          key={f.key}
                          field={f}
                          readOnly={viewOnly}
                          oid={values[f.key] == null ? "" : String(values[f.key])}
                          fileName={f.fileNameKey ? String(values[f.fileNameKey] ?? "") : ""}
                          onChange={(oid, name) => {
                            dirtyRef.current = true;
                            setValues((s) => ({
                              ...s,
                              [f.key]: oid,
                              ...(f.fileNameKey ? { [f.fileNameKey]: name } : {}),
                            }));
                          }}
                        />
                      ) : (
                        <Field
                          key={f.key}
                          field={f}
                          square={!!(config.squareControls || forceSquareControls)}
                          value={shownValue(f)}
                          quickAddContext={values}
                          options={optionsOf(f)}
                          showEmpty={!viewOnly && triedSave && isEmpty(f)}
                          readOnly={viewOnly || !!f.readOnly || isFieldLocked(f, lockCtx) || (!!f.lockOnEdit && id !== null)}
                          lockReason={viewOnly ? null : fieldLockReason(f, lockCtx)}
                          pairedWithCheckbox={pairedWithCheckbox}
                          horizontalLabels={config.horizontalLabels}
                          viewHtml={f.htmlKey ? String(values[f.htmlKey] ?? "") : undefined}
                          usDate={!!config.usDateFormat}
                          insertBase={
                            f.type === "datasource" && f.insertIntoKey
                              ? String(values[f.insertIntoKey] ?? "")
                              : undefined
                          }
                          onChange={(v) => set(f.key, v)}
                          onFill={(patch) => {
                            for (const [k, v] of Object.entries(patch)) set(k, v);
                          }}
                          onOptionsReplaced={(opts) =>
                            setOptions((s) => ({ ...s, [f.key]: opts }))
                          }
                        />
                      );
                    })}
                </div>
                {/* An optional panel BESIDE the fields (Message Library's live
                    Preview). Rendered as a sibling of the grid inside a flex row
                    that only exists when a form supplies one — so every other
                    detail view keeps its full-width single-column layout. */}
                {aside && <aside className="sd-aside">{aside(values)}</aside>}
                {/* Data Source merge-token panel (type 'datasource') renders as a
                    RIGHT-side aside (legacy XAF layout — the panel sits top-right next
                    to the Body), not inline in the field grid. */}
                {!aside && config.fields.some((f) => f.type === "datasource") && (
                  <aside className="sd-aside">
                    {config.fields
                      .filter((f) => f.type === "datasource")
                      .map((f) => (
                        <Field
                          key={f.key}
                          field={f}
                          value={values[f.key]}
                          options={optionsOf(f)}
                          showEmpty={false}
                          readOnly={viewOnly}
                          insertBase={f.insertIntoKey ? String(values[f.insertIntoKey] ?? "") : undefined}
                          onChange={(v) => set(f.key, v)}
                          onFill={(patch) => {
                            for (const [k, v] of Object.entries(patch)) set(k, v);
                          }}
                        />
                      ))}
                  </aside>
                )}
                {/* Association grids (legacy child-collection tabs, e.g. Test
                    Methods Link/Unlink) render full-width below the main form.
                    Fields sharing an `assocTabGroup` (Email To / CC / Bcc) become
                    ONE tabbed panel; the rest each render as their own panel. */}
                {(() => {
                  const assoc = config.fields.filter((f) => f.type === "assocgrid");
                  if (assoc.length === 0) return null;
                  const groups = new Map<string, CrudField[]>();
                  for (const f of assoc)
                    if (f.assocTabGroup) {
                      const g = groups.get(f.assocTabGroup) ?? [];
                      g.push(f);
                      groups.set(f.assocTabGroup, g);
                    }
                  return (
                    <>
                      {assoc
                        .filter((f) => !f.assocTabGroup)
                        .map((f) => (
                          <AssocGridField
                            key={f.key}
                            field={f}
                            value={Array.isArray(values[f.key]) ? (values[f.key] as string[]) : []}
                            readOnly={viewOnly}
                            onChange={(v) => set(f.key, v)}
                          />
                        ))}
                      {[...groups.entries()].map(([g, fs]) => (
                        <AssocTabGroupField
                          key={g}
                          fields={fs}
                          values={values}
                          readOnly={viewOnly}
                          onChange={(k, v) => set(k, v)}
                        />
                      ))}
                    </>
                  );
                })()}
                {/* Read-only reference tables (e.g. Permit Lifecycle Events).
                    Declared in MODULE_CRUD.staticSections — no table, no endpoint. */}
                {config.staticSections?.map((s) => (
                  <StaticSection key={s.title} section={s} />
                ))}
                {/* Editable child collections (e.g. Permit Limits). Disabled until
                    the parent is saved - a child needs a parent id to belong to.

                    With childGridsTabbed (opt-in) they share ONE panel behind a tab
                    strip, the legacy XAF TabbedGroup look. Every grid stays MOUNTED
                    and the inactive ones are hidden with CSS rather than unmounted:
                    switching tabs then costs no refetch and keeps each grid's own
                    selection / maximised state, which an unmount would throw away. */}
                {config.childGridsTabbed && (config.childGrids?.length ?? 0) > 0 && (
                  <div className="sd-assoc-tabbar sd-childtabbar" role="tablist">
                    {config.childGrids!.map((g, i) => (
                      <button
                        key={g.title}
                        type="button"
                        role="tab"
                        aria-selected={i === childTab}
                        className={`sd-assoc-tab${i === childTab ? " active" : ""}`}
                        onClick={() => setChildTab(i)}
                      >
                        {g.title}
                      </button>
                    ))}
                  </div>
                )}
                {config.childGrids?.map((g, i) => {
                  if (g.visibleWhen && !g.visibleWhen(values)) return null;
                  const tabbed = config.childGridsTabbed && (config.childGrids?.length ?? 0) > 0;
                  const grid = (
                    <ChildGrid
                      // Keyed on reloadToken so Refresh / a record action re-reads the rows too.
                      key={`${g.title}#${reloadToken}`}
                      grid={g}
                      // Hide the sort-indicator caret in the tab grids' headers (keep the
                      // filter chevron + click-to-sort) when the form opts in (owner request 2026-10-26).
                      hideSortIndicator={config.childGridsHideSortIndicator}
                      parentId={currentId}
                      // A child collection is part of the record, so it follows the
                      // record's mode: no New / Edit / Delete until the user has
                      // pressed Edit. Opening a saved permit read-only must not
                      // offer to add a limit to it.
                      viewOnly={viewOnly}
                      // Only the keys the grid declares - the child form has no
                      // business reading the whole parent record.
                      parentContext={Object.fromEntries((g.parentContext ?? []).map((k) => [k, values[k]]))}
                      pending={pendingChildren[g.title] ?? []}
                      onPending={(rows) => {
                        dirtyRef.current = true;
                        setPendingChildren((s) => ({ ...s, [g.title]: rows }));
                      }}
                    />
                  );
                  if (!tabbed) return grid;
                  return (
                    <div key={g.title} role="tabpanel" className="sd-childtabpanel" hidden={i !== childTab}>
                      {grid}
                    </div>
                  );
                })}
                {/* Bespoke Maintenance Setup "Task Checklist" scheduling grid
                    (Link/Unlink over dbo.MaintenanceTaskCheckList) — full-width
                    below the form, mirroring the legacy detail. */}
                {config.taskChecklistGrid && <MaintenanceTaskChecklistGrid parentId={currentId} viewOnly={viewOnly} />}
                {/* Bespoke Daily QC Log Book Builder "Field Configuration" dual
                    list (Available | add/remove | Selected) — full-width below the
                    header. On a NEW record the picks and their edits are buffered
                    and written by Save via flushRef, before the page switches to
                    the saved template. */}
                {config.dailyQcFieldConfig && (
                  <DailyQcFieldConfigGrid parentId={currentId} viewOnly={viewOnly} flushRef={dailyQcFlushRef} />
                )}
                {/* Bespoke Assign Tests To Item "Tests" tab — edits are buffered
                    here and committed by Save (legacy Items_DetailView_NN). */}
                {config.itemTestsGrid && (
                  <ItemTestsGrid
                    itemId={currentId}
                    viewOnly={viewOnly}
                    reloadToken={childReload}
                    handleRef={bufferedChildRef}
                    onDirty={() => {
                      dirtyRef.current = true;
                    }}
                  />
                )}
                {/* A form's own buffered child area (generic slot) — committed
                    by the header Save, exactly like the Tests tab above. */}
                {bufferedChild?.({
                  id: currentId,
                  recordName,
                  viewOnly,
                  values,
                  reloadToken: childReload,
                  handleRef: bufferedChildRef,
                  onDirty: () => {
                    dirtyRef.current = true;
                  },
                  patchRecord: (partial) => setRawRecord((r) => ({ ...r, ...partial })),
                })}
                {/* Read-only reference panels a form supplies itself, behind the
                    SAME tab strip the child grids use (standing rule 9).

                    Unlike `childGrids` these need no endpoint and no child config:
                    they show what the page already knows — where a message is
                    raised, which other rows share its wording — so a REST-driven
                    child grid cannot express them. Purely additive: a form that
                    passes none renders exactly as before. */}
                {(extraTabs?.length ?? 0) > 0 && (
                  <>
                    <div className="sd-assoc-tabbar sd-childtabbar" role="tablist">
                      {extraTabs!.map((t, i) => (
                        <button
                          key={t.id}
                          type="button"
                          role="tab"
                          aria-selected={i === extraTab}
                          className={`sd-assoc-tab${i === extraTab ? " active" : ""}`}
                          onClick={() => setExtraTab(i)}
                        >
                          {t.label}
                          {t.count !== undefined && <span className="sd-assoc-tabn">{t.count}</span>}
                        </button>
                      ))}
                    </div>
                    <div className="sd-extra-panel">{extraTabs![extraTab]?.render()}</div>
                  </>
                )}
                </div>
              </>
            )}
          </div>

          <ActionBar bottom />
        </div>
      </div>
    </div>
  );
}

/** Renders one {@link CrudChildGrid}: the records belonging to the record being
 *  edited, with their own New / Edit / Delete.
 *
 *  The child's New/Edit reuses the shared <ModuleCrudDialog> driven by the
 *  child's own MODULE_CRUD entry, and the parent id is passed as `fixedValues`
 *  so every child is stamped with its parent and cannot be re-parented.
 *
 *  New works before the parent has been saved. The dialog opens immediately and
 *  the row is held in `pending` until the parent is created, at which point the
 *  detail page POSTs it with the new parent id.
 *
 *  It is done this way because `Permitsetup.PermitLibrary` is NULLABLE: POSTing a
 *  child with no parent id would not fail, it would quietly create an ORPHAN -
 *  invisible in every permit's grid but sitting in the table for good. Buffering
 *  avoids that without forcing the user to save the permit first, which is what
 *  the earlier "save the parent, then open" attempt did (and which stalled on the
 *  parent's own required fields). */
/** One child-grid row as the body renders it: the record, its stable key, and
 *  whether it is still waiting on the parent to be saved. Tagged before sort /
 *  filter so neither depends on the row's position. */
type ChildGridEntry = { r: Record<string, unknown>; rid: string; isPending: boolean };

export function ChildGrid({
  grid,
  hideSortIndicator = false,
  parentId,
  viewOnly: recordViewOnly,
  parentContext,
  pending,
  onPending,
}: {
  grid: CrudChildGrid;
  /** Hide the sort-indicator caret (⇅/▲/▼) in the header — the column name, the
   *  filter chevron and click-to-sort all stay (owner request 2026-10-26,
   *  config.childGridsHideSortIndicator). */
  hideSortIndicator?: boolean;
  parentId: string | null;
  /** The owning record is being READ, not edited - so this collection is read-only
   *  too and its New / Edit / Delete are disabled (a new record opens in edit
   *  mode, so pending children are unaffected). */
  viewOnly: boolean;
  /** Values lifted from the owning record (see CrudChildGrid.parentContext),
   *  passed through to the child dialog for its cascades. */
  parentContext?: Record<string, unknown>;
  /** Rows entered before the parent existed; shown alongside the saved ones. */
  pending: Record<string, unknown>[];
  onPending: (rows: Record<string, unknown>[]) => void;
}) {
  // A grid that opts in (actionsInView) keeps its own New / Edit / Delete while the record is only being READ —
  // G5's Correction Verification opens its detail read-only yet lets the verification rows be added.
  const viewOnly = recordViewOnly && !grid.actionsInView;
  const notify = useNotify();
  const confirmDelete = useDeleteFlow();
  const runSave = useSaveFlow();
  const { confirm } = useConfirm();
  const rawChildConfig = MODULE_CRUD[grid.childKey];
  // A child basePath may template the parent id as "{id}" (to reuse a NESTED CRUD route,
  // e.g. /api/v1/clients/{id}/contacts). Resolve it with the parent's id so every CRUD call
  // — the dialog's GET/POST/PUT, the inline-row PUT and the DELETE — hits the right nested
  // route. Flat basePaths have no "{id}", so they are untouched. (Owner request 2026-10-23,
  // Unfollowed Clients child grids reuse the Client master CRUD.)
  const childConfig =
    rawChildConfig?.basePath?.includes("{id}") && parentId
      ? { ...rawChildConfig, basePath: rawChildConfig.basePath.replace("{id}", parentId) }
      : rawChildConfig;
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  // Selection is held as a SET either way, so the toolbar has one shape to reason
  // about. Single-select (the default) simply never holds more than one member —
  // picking a row replaces the set, exactly as the radio always behaved.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  /** The sole selected row, or null when none / several are ticked. Every
   *  single-row action (Edit, Download) keys off this. */
  const selected = selectedIds.size === 1 ? [...selectedIds][0]! : null;
  /** True when the single selected row is a buffered one (no server record yet):
   *  it can be deleted (dropped from the buffer) but not opened for edit. */
  const selectedIsPending = selected != null && selected.startsWith("pending-");
  const setSelected = (rid: string | null) => setSelectedIds(rid === null ? new Set() : new Set([rid]));
  // `row` is the already-loaded record, handed to the dialog so an Edit opens
  // populated instead of re-fetching what this grid just rendered.
  const [editing, setEditing] = useState<{ id: string | null; row?: Record<string, unknown> } | null>(null);
  // Legacy XAF Link picker: candidate rows (not on this instrument), the checked
  // set (multi-select), a search filter, and a busy flag.
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkRows, setLinkRows] = useState<Record<string, unknown>[]>([]);
  const [linkSel, setLinkSel] = useState<Set<string>>(new Set());
  const [linkSearch, setLinkSearch] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  // Export + Maximize (BO UI Standard, user request 2026-09-23: "in the detail
  // view if there is a grid they must have the export to and maximus actions")
  // — built into this ONE shared child-grid renderer so EVERY CrudChildGrid gets
  // both for free (Sub Item, Instrument Certificate/Attachment, Permit Limits, …),
  // no per-grid opt-in needed.
  const [maximized, setMaximized] = useState(false);
  useEffect(() => {
    if (!maximized) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMaximized(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [maximized]);

  // ── Edit IN THE ROWS' OWN CELLS (CrudChildGrid.pencilRowEdit) ─────────────
  // Separate from `editing`, which still drives the dialog for New. Edit puts
  // EVERY checked row's CELLS into editors in place — each cell keeps its
  // column, so a row still reads as a row (owner request 2026-09-23).
  const rowEditable = grid.pencilRowEdit === true;
  const [editRowIds, setEditRowIds] = useState<Set<string>>(new Set());
  /** rowId → that row's edited field values. Per row, because each row is a
   *  separate record being changed independently. */
  const [rowDrafts, setRowDrafts] = useState<Record<string, Record<string, string>>>({});
  /** rowId → fieldKey → options. Also per row: the lists are CASCADED, so two
   *  rows on different analytes have different Method and Units options — one
   *  shared map would show row B the options belonging to row A. */
  const [cellOpts, setCellOpts] = useState<Record<string, Record<string, Option[]>>>({});
  const [savingRow, setSavingRow] = useState(false);
  const editingRows = editRowIds.size > 0;

  const load = useCallback(async () => {
    if (!parentId) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const url = grid.listUrl.replace("{id}", parentId);
      const res = await api.get<{ data?: Record<string, unknown>[] }>(url);
      setRows(res.data?.data ?? []);
    } catch {
      notify.error(`Could not load ${grid.title}.`);
    } finally {
      setLoading(false);
    }
  }, [grid.listUrl, grid.title, parentId, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const idField = childConfig?.idField ?? "oid";
  const rowId = (r: Record<string, unknown>) => String(r[idField] ?? "");
  // A pending row carries its display labels under one reserved key (the payload
  // itself holds FK Oids). Flattened here so the grid's name/display columns
  // render exactly what the dialog showed; the POST drops the key again.
  const withLabels = (r: Record<string, unknown>) => {
    const labels = r[PENDING_LABELS_KEY];
    return labels && typeof labels === "object" ? { ...r, ...(labels as object) } : r;
  };

  // A pending row (entered before the parent exists) has no server-derived
  // auto-number yet — e.g. the Document Attachment "Rev Number", assigned on
  // persist and (being readonly) never sent in the buffered payload — so its cell
  // would read blank until the document is saved. For a grid with `seqField`, fill
  // in the DISPLAY value the server will assign: prefix + (max existing + pending
  // index + 1), the legacy AfterConstruction rule, so a buffered row shows "Rev1" /
  // "Rev2" at once. Display only — the server still derives it authoritatively.
  const seqExistingMax = (() => {
    if (!grid.seqField) return 0;
    const { key, prefix } = grid.seqField;
    const re = new RegExp(`^${prefix}(\\d+)$`, "i");
    return rows.reduce((mx, r) => {
      const m = re.exec(String(r[key] ?? "").trim());
      return m ? Math.max(mx, Number(m[1])) : mx;
    }, 0);
  })();
  const withPendingSeq = (p: Record<string, unknown>, i: number) => {
    if (!grid.seqField) return p;
    const { key, prefix } = grid.seqField;
    if (String(p[key] ?? "").trim()) return p; // already numbered
    return { ...p, [key]: `${prefix}${seqExistingMax + i + 1}` };
  };

  // Saved rows first, then anything still waiting on the parent. A pending row
  // has no id, so it is keyed by position and cannot be edited or deleted until
  // it has been written.
  const allRows = [...rows, ...pending.map((p, i) => withLabels(withPendingSeq(p, i)))];
  /** Ids the user can act on: saved rows (by server id) + buffered rows (by their
   *  `pending-<i>` position key). A pending row can't be edited yet, but it CAN be
   *  deleted — dropped from the buffer before the parent is saved (owner request
   *  2026-10-22). */
  const selectableIds = [...rows.map(rowId).filter(Boolean), ...pending.map((_, i) => `pending-${i}`)];

  // ── Header SORT + column FILTER (user request 2026-09-26) ─────────────────
  // The same two header controls every list grid has — click the label to sort
  // (asc → desc → none, the shared useSort cycle) and the inline chevron that
  // opens the shared Excel-style multi-select filter (ColFilterButton). The
  // rows are tagged ONCE with their id and pending flag BEFORE sorting, because
  // a pending row used to be recognised by its POSITION (i >= rows.length), and
  // a sorted or filtered view has no stable positions to lean on.
  const sorter = useSort<ChildGridEntry>();
  const [filters, setFilters] = useState<Record<string, ColumnFilterValue>>({});
  /** The DISPLAYED text of one cell — the same string the body renders, so the
   *  filter list and the filter match never disagree with what the user sees. */
  const cellText = useCallback(
    (r: Record<string, unknown>, key: string): string => {
      const v = r[key];
      if (v == null) return "";
      const col = grid.columns.find((c) => c.key === key);
      return col?.type === "date" ? fmtLocalDate(v as string) : col?.type === "datetime" ? fmtLocalDateTime(v as string) : String(v);
    },
    [grid.columns],
  );
  const entries: ChildGridEntry[] = [
    ...rows.map((r, i) => ({ r, rid: rowId(r) || `row-${i}`, isPending: false })),
    ...pending.map((p, i) => ({ r: withLabels(withPendingSeq(p, i)), rid: `pending-${i}`, isPending: true })),
  ];
  const filterActive = Object.values(filters).some((v) => v != null);
  const viewEntries = sorter.apply(
    filterActive
      ? entries.filter((e) => Object.entries(filters).every(([k, sel]) => passesColumnFilter(sel, cellText(e.r, k))))
      : entries,
    // Sort by the RAW value (a date column's ISO value, a numeric limit), so the
    // shared comparator orders chronologically / numerically, not as text.
    (e, k) => e.r[k],
  );
  const entryText = (e: ChildGridEntry, k: string) => cellText(e.r, k);

  // ── PAGING (CrudChildGrid.paged) ──────────────────────────────────────────
  // Opt-in per grid, behind the ONE shared <GridPager> footer (rule 7 / §18.6);
  // a grid that does not ask keeps the plain `Total Records: n` footer below.
  // The slice comes AFTER the filter and the sort, so page 1 is the first page
  // of what the user is actually looking at — never a page of the raw order.
  const paged = grid.paged === true;
  const [pageSize, setPageSize] = useState(grid.pageSize ?? 20);
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(viewEntries.length / pageSize));
  // Clamped rather than stored back, so a filter that shrinks the list below the
  // current page renders correctly on the SAME pass instead of flashing an empty
  // grid until an effect corrects it.
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageEntries = paged ? viewEntries.slice((safePage - 1) * pageSize, safePage * pageSize) : viewEntries;
  // Filtering is a new question, so it starts at the first page. Sorting is not:
  // it re-orders the same set, and the page the user is on stays meaningful.
  useEffect(() => {
    setPage(1);
  }, [filters, pageSize]);

  // ── Row-cell editing helpers (pencilRowEdit) ──────────────────────────────
  // A grid COLUMN shows a resolved display value (parameterName,
  // operatorDisplay) while the editable FIELD holds the FK id (parameterId,
  // operator). The mapping is the same `${base}Id` / `${base}Name` /
  // `${base}Display` convention ModuleCrudDialog uses when it writes labels
  // back, so it is read here rather than configured per column. A column with
  // no field behind it (Synonym, the computed Status) stays read-only.
  const fieldForColumn = (colKey: string): CrudField | null => {
    if (!childConfig) return null;
    const fields = childConfig.fields;
    const exact = fields.find((f) => f.key === colKey && f.type !== "readonly");
    if (exact) return exact;
    const base = colKey.replace(/(Name|Display)$/, "");
    if (base === colKey) return null;
    return fields.find((f) => (f.key === `${base}Id` || f.key === base) && f.type !== "readonly") ?? null;
  };

  /** Cascade context for ONE row: the parent's context (Sample Matrix), then
   *  the row's STORED values, then its draft on top.
   *
   *  The stored values are what makes a field like Units work. Units narrows by
   *  Parameter AND Method — but Method has no column of its own here, so a
   *  draft built from the visible columns alone carries no methodId: Units'
   *  enabledWhen would fail and it would render empty and disabled even though
   *  the row plainly has units (owner report 2026-09-23). */
  const rowCtx = (rid: string, draft: Record<string, string>): Record<string, unknown> => ({
    ...(parentContext ?? {}),
    ...(rows.find((r) => rowId(r) === rid) ?? {}),
    ...draft,
  });

  /** Options for ONE row's select fields, stored under the row id so rows
   *  never share a list. */
  const loadCellOptions = async (rid: string, fields: CrudField[], draft: Record<string, string>) => {
    const ctx = rowCtx(rid, draft);
    for (const f of fields) {
      if (f.type !== "select" || !f.optionsUrl) continue;
      // enabledWhen unmet → no list to fetch yet (Method before a Parameter).
      if (f.enabledWhen?.some((k) => String(ctx[k] ?? "").trim() === "")) {
        setCellOpts((o) => ({ ...o, [rid]: { ...(o[rid] ?? {}), [f.key]: [] } }));
        continue;
      }
      const opts = await fetchOptions(cascadeUrl(f.optionsUrl, f, ctx), sharedOptionCache);
      setCellOpts((o) => ({ ...o, [rid]: { ...(o[rid] ?? {}), [f.key]: opts } }));
    }
  };

  /** Put EVERY checked row into cell editors at once. */
  const startRowEdit = (targets: Record<string, unknown>[]) => {
    if (!childConfig || targets.length === 0) return;
    const editable = grid.columns.map((c) => fieldForColumn(c.key)).filter((f): f is CrudField => f !== null);
    const drafts: Record<string, Record<string, string>> = {};
    const ids = new Set<string>();
    for (const r of targets) {
      const rid = rowId(r);
      if (!rid) continue;
      const draft: Record<string, string> = {};
      for (const f of editable) {
        const v = r[f.key];
        draft[f.key] = f.type === "date" ? toDateInput(v) : v == null ? "" : String(v);
      }
      drafts[rid] = draft;
      ids.add(rid);
    }
    setRowDrafts(drafts);
    setEditRowIds(ids);
    setCellOpts({});
    for (const rid of ids) void loadCellOptions(rid, editable, drafts[rid] ?? {});
  };

  const cancelRowEdit = () => {
    setEditRowIds(new Set());
    setRowDrafts({});
    setCellOpts({});
  };

  /** A cell changed. Anything downstream of it (Method and Units both hang off
   *  Parameter) is re-fetched and re-bound, exactly as the dialog does — an
   *  edit that left a stale Method under a new analyte would save a pairing
   *  the lab never chose. */
  const changeCell = (rid: string, f: CrudField, value: string) => {
    if (!childConfig) return;
    const next = { ...(rowDrafts[rid] ?? {}), [f.key]: value };
    const dependents = childConfig.fields.filter((d) => depsOf(d).includes(f.key));
    for (const d of dependents) next[d.key] = "";
    setRowDrafts((cur) => ({ ...cur, [rid]: next }));
    if (dependents.length === 0) return;
    void (async () => {
      // The dependents were just cleared in `next`, and `next` wins over the
      // stored record — so the re-fetch below sees them EMPTY and re-binds
      // them, rather than being handed their pre-change values.
      const ctx = rowCtx(rid, next);
      // cascadeLevels orders the chain so Units re-fetches only after Method
      // has re-bound — Units narrows by BOTH.
      for (const level of cascadeLevels(dependents)) {
        for (const d of level) {
          if (d.type !== "select" || !d.optionsUrl) continue;
          if (d.enabledWhen?.some((k) => String(ctx[k] ?? "").trim() === "")) {
            setCellOpts((o) => ({ ...o, [rid]: { ...(o[rid] ?? {}), [d.key]: [] } }));
            continue;
          }
          const opts = await fetchOptions(cascadeUrl(d.optionsUrl, d, ctx), sharedOptionCache);
          setCellOpts((o) => ({ ...o, [rid]: { ...(o[rid] ?? {}), [d.key]: opts } }));
          const canAuto =
            !d.autoSelectRequires || d.autoSelectRequires.every((k) => String(ctx[k] ?? "").trim() !== "");
          const first = opts[0];
          if (d.autoSelectFirst && canAuto && first) {
            ctx[d.key] = first.id;
            setRowDrafts((cur) => ({ ...cur, [rid]: { ...(cur[rid] ?? {}), [d.key]: first.id } }));
          }
        }
      }
    })();
  };

  /** Write EVERY row currently in cell-edit. Validation runs over all of them
   *  FIRST, so a bad cell in the last row does not leave the earlier ones
   *  already written. */
  const saveRow = async () => {
    if (!childConfig || editRowIds.size === 0) return;
    const ids = [...editRowIds];
    /** How to point at a row in a message — its first column (the analyte),
     *  else its position, so "Limit1 is required" says WHICH row. */
    const rowLabel = (rid: string) => {
      const rec = rows.find((r) => rowId(r) === rid);
      const firstCol = grid.columns[0];
      const v = rec && firstCol ? rec[firstCol.key] : null;
      const s = v == null ? "" : String(v).trim();
      return s !== "" ? s : `row ${ids.indexOf(rid) + 1}`;
    };
    for (const rid of ids) {
      const draft = rowDrafts[rid] ?? {};
      for (const c of grid.columns) {
        const f = fieldForColumn(c.key);
        if (!f) continue;
        const v = (draft[f.key] ?? "").trim();
        if (f.required && v === "") {
          notify.warning(`${f.label} is required (${rowLabel(rid)}).`);
          return;
        }
        if (f.maxLength && v.length > f.maxLength) {
          notify.warning(`${f.label} cannot exceed ${f.maxLength} characters (${rowLabel(rid)}).`);
          return;
        }
      }
    }
    setSavingRow(true);
    try {
      await runSave({
        formName: childConfig.formName,
        auditAction: "edit",
        save: async () => {
          for (const rid of ids) {
            const draft = rowDrafts[rid] ?? {};
            // Full record in, edited cells over the top: fields with no column
            // (Method, Limit3) keep whatever they already held rather than
            // being blanked by a payload that never saw them.
            const base =
              rows.find((r) => rowId(r) === rid) ??
              (await api.get<{ data?: Record<string, unknown> }>(`${childConfig.basePath}/${rid}`)).data?.data ??
              {};
            const payload: Record<string, unknown> = {};
            for (const f of childConfig.fields) {
              if (f.type === "readonly") continue;
              const edited = Object.prototype.hasOwnProperty.call(draft, f.key);
              const src = edited ? draft[f.key] : base[f.key];
              if (f.type === "checkbox") payload[f.key] = Boolean(src);
              else if (f.type === "multiselect") {
                payload[f.key] = Array.isArray(src) ? src : src == null || src === "" ? [] : [String(src)];
              } else if (f.type === "date") {
                const s = toDateInput(src);
                payload[f.key] = s === "" ? null : s;
              } else {
                const s = src == null ? "" : String(src).trim();
                payload[f.key] = s === "" ? null : s;
              }
            }
            Object.assign(payload, { [grid.parentField]: parentId });
            // One audited PUT per row — a multi-row selection is not a bulk
            // endpoint, exactly as the multi-row Delete.
            await api.put(`${childConfig.basePath}/${rid}`, payload);
          }
        },
        onSaved: () => {
          cancelRowEdit();
          void load();
        },
        onError: (e) => {
          const msg =
            (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ??
            "Save failed.";
          notify.error(msg);
        },
      });
    } finally {
      setSavingRow(false);
    }
  };

  const remove = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0 || !childConfig) return;
    // A buffered row (pending-<i>) is deleted by dropping it from the buffer; a
    // saved row by an audited server DELETE. A selection may mix the two.
    const pendingIdxs = new Set(
      ids.filter((id) => id.startsWith("pending-")).map((id) => Number(id.slice("pending-".length))),
    );
    const savedIds = ids.filter((id) => !id.startsWith("pending-"));
    await confirmDelete({
      formName: childConfig.formName,
      message: ids.length === 1 ? "Delete the selected record?" : `Delete the ${ids.length} selected records?`,
      // Saved rows deleted one at a time: each is its own audited DELETE, so a bulk
      // selection still produces one audit entry per record. Buffered rows are just
      // removed from the pending list (they were never written).
      delete: async () => {
        for (const id of savedIds) await api.delete(`${childConfig.basePath}/${id}`);
        if (pendingIdxs.size > 0) onPending(pending.filter((_, i) => !pendingIdxs.has(i)));
      },
      onDeleted: () => {
        setSelectedIds(new Set());
        void load();
      },
    });
  };

  /** A buffered-row capture from a child dialog (parent not saved yet): a New
   *  (no editing id) APPENDS to the buffer; editing an existing buffered row
   *  (pending-<i>) REPLACES it in place, so an edit doesn't duplicate the row. */
  const capturePending = (payload: Record<string, unknown>): Record<string, unknown>[] => {
    const id = editing?.id;
    if (id && id.startsWith("pending-")) {
      const i = Number(id.slice("pending-".length));
      return pending.map((p, idx) => (idx === i ? payload : p));
    }
    return [...pending, payload];
  };

  // Download a child-row attachment (legacy preview): fetch the blob and save it
  // with the original filename. Same endpoint the detail file field uses.
  const downloadChildFile = async (base: string, oid: string, fileName: string) => {
    try {
      const res = await api.get(`${base}/${oid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "attachment";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      notify.error("Could not download the file.");
    }
  };

  // Legacy XAF association Link/Unlink (opt-in via grid.linkable).
  const openLink = async () => {
    if (!grid.linkListUrl || !parentId) return;
    setLinkOpen(true);
    setLinkSel(new Set());
    setLinkSearch("");
    setLinkRows([]);
    try {
      const url = grid.linkListUrl.replace("{id}", parentId);
      const res = await api.get<{ data?: Record<string, unknown>[] }>(url);
      setLinkRows(res.data?.data ?? []);
    } catch {
      notify.error(`Could not load ${grid.title}.`);
    }
  };
  // Link ALL checked candidates (legacy links the whole selection at once).
  const doLink = async () => {
    if (linkSel.size === 0 || !parentId || !childConfig) return;
    setLinkBusy(true);
    try {
      const q = grid.linkQueryParam ?? "labwareId";
      // A tab may link a different entity than its placeholder childKey (prospect Quotes
      // borrows the note childKey but links quotes) — linkActionBase overrides the POST base.
      const linkBase = grid.linkActionBase ?? childConfig.basePath;
      for (const oid of linkSel) {
        await api.post(`${linkBase}/${oid}/link?${q}=${parentId}`);
      }
      setLinkOpen(false);
      setLinkSel(new Set());
      notify.success(linkSel.size === 1 ? "Linked." : `Linked ${linkSel.size} records.`);
      void load();
    } catch {
      notify.error("Link failed.");
    } finally {
      setLinkBusy(false);
    }
  };
  const unlink = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0 || !childConfig) return;
    const ok = await confirm({
      title: "Unlink",
      message:
        ids.length === 1
          ? "Unlink the selected record? The record itself is kept."
          : `Unlink the ${ids.length} selected records? The records themselves are kept.`,
      confirmLabel: "Unlink",
      tone: "danger",
    });
    if (!ok) return;
    try {
      const q = grid.linkQueryParam ?? "labwareId";
      const linkBase = grid.linkActionBase ?? childConfig.basePath;
      for (const id of ids) {
        await api.post(`${linkBase}/${id}/unlink?${q}=${parentId}`);
      }
      setSelectedIds(new Set());
      notify.success(ids.length === 1 ? "Unlinked." : `Unlinked ${ids.length} records.`);
      void load();
    } catch {
      notify.error("Unlink failed.");
    }
  };

  // Export rows/headers for the "Export to" dropdown — the rows AS SHOWN
  // (current sort and column filters applied), like every list grid's export.
  const exportHeaders = grid.columns.map((c) => c.label);
  const exportRows = viewEntries.map((e) => grid.columns.map((c) => cellText(e.r, c.key)));

  // Tabler icon in front of a toolbar button's caption — only on a grid that opts in with `iconButtons` (the Instrument
  // detail's Certificates / Attachments), as the Country / State / City dialogs draw theirs. The icon's colour comes from
  // the shared theme/iconColors.css, so it matches those dialogs.
  const tiIcon = (cls: string) => (grid.iconButtons ? <><i className={`ti ${cls}`} aria-hidden="true" />{" "}</> : null);

  // Link / Unlink (legacy XAF association) — opt-in per grid (grid.linkable). Link needs a SAVED parent to attach
  // to; both follow the record's mode. Unlink is bulk-capable: it acts on every ticked row. Shown between New and
  // Edit, or after Delete when grid.linkLast (New · Edit · Delete · Link · Unlink — the Instrument grids).
  const linkButtons = grid.linkable ? (
    <>
      <button
        type="button"
        className="ca-btn"
        disabled={viewOnly || editingRows || !parentId}
        title={!parentId ? "Save the record first" : undefined}
        onClick={() => void openLink()}
      >
        {tiIcon("ti-link")}Link
      </button>
      <button
        type="button"
        className="ca-btn"
        disabled={viewOnly || editingRows || selectedIds.size === 0}
        onClick={() => void unlink()}
      >
        {tiIcon("ti-unlink")}Unlink
      </button>
    </>
  ) : null;

  return (
    <div className={`sd-static sd-child${maximized ? " sd-child-maximized" : ""}${grid.bordered ? " sd-child-bordered" : ""}`}>
      <h3 className="sd-sub-h">{grid.title}</h3>
      {/* The whole toolbar row is dropped for a view-only grid that has no actions
          (grid.hideToolbar) so no blank strip is left (Retired Document, 2026-09-30). */}
      {!grid.hideToolbar && (
      <div className="sd-child-bar">
        {/* All three follow the OWNING record's mode: a permit opened read-only
            must not offer to add, change or drop one of its limits.

            New is dropped on an M:N collection where Link replaces it (hideNew) —
            legacy XAF parity, e.g. Vendors → Evaluation. */}
        {!grid.hideNew && (
          <button
            type="button"
            className="ca-btn"
            disabled={viewOnly || editingRows}
            onClick={() => setEditing({ id: null })}
          >
            {grid.coloredIcons && <span className="tb-i tb-i-green">＋</span>}{grid.coloredIcons ? ' ' : ''}{tiIcon("ti-plus")}New
          </button>
        )}
        {!grid.linkLast && linkButtons}
        {/* Edit and Delete additionally need a row to act on - that is ordinary
            grid behaviour, not a parent-saved restriction.

            On a pencilRowEdit grid Edit takes the WHOLE checked selection and
            opens those rows' own CELLS for editing; elsewhere it opens the one
            checked record in the dialog. While rows are being edited in place,
            Edit/New/Delete step aside for Save/Cancel, so there is never a
            second pending edit. */}
        {!grid.hideEdit && !editingRows && (
          <button
            type="button"
            className="ca-btn"
            disabled={viewOnly || (rowEditable ? selectedIds.size === 0 : !selected)}
            onClick={() => {
              if (rowEditable) {
                startRowEdit(rows.filter((x) => selectedIds.has(rowId(x))));
                return;
              }
              // A buffered row's data comes from the pending buffer (it has no server
              // record); a saved row's from `rows` when the list row IS the full
              // record (opt-in per grid) — a partial row would prefill blanks.
              const pIdx = selectedIsPending ? Number(selected!.slice("pending-".length)) : -1;
              setEditing({
                id: selected,
                row:
                  pIdx >= 0
                    ? withLabels(withPendingSeq(pending[pIdx]!, pIdx))
                    : grid.listReturnsFullRecord
                      ? rows.find((r) => rowId(r) === selected)
                      : undefined,
              });
            }}
          >
            {grid.coloredIcons && <span className="tb-i tb-i-blue">✎</span>}{grid.coloredIcons ? ' ' : ''}{tiIcon("ti-pencil")}Edit
          </button>
        )}
        {/* The editing rows' own actions — the only way out of row-edit, so a
            row can never be left half-changed by wandering off elsewhere. */}
        {editingRows && (
          <>
            <button type="button" className="ca-btn ca-btn-solid" disabled={savingRow} onClick={() => void saveRow()}>
              <BusyIcon busy={savingRow} icon="ti-device-floppy" />
              {editRowIds.size === 1 ? " Save" : ` Save ${editRowIds.size} rows`}
            </button>
            <button type="button" className="ca-btn" disabled={savingRow} onClick={cancelRowEdit}>
              Cancel
            </button>
          </>
        )}
        {/* Delete is dropped alongside New on an M:N collection (hideDelete), where
            Unlink is the destructive action on offer and the shared row stays. */}
        {/* Delete is bulk-capable too — one audited DELETE per ticked row. */}
        {!grid.hideDelete && (
          <button
            type="button"
            className="ca-btn"
            disabled={viewOnly || editingRows || selectedIds.size === 0}
            onClick={() => void remove()}
          >
            {grid.coloredIcons && <span className="tb-i tb-i-red">🗑</span>}{grid.coloredIcons ? ' ' : ''}{tiIcon("ti-trash")}Delete
          </button>
        )}
        {grid.linkLast && linkButtons}
        {grid.refreshButton && (
          <button type="button" className="ca-btn" disabled={loading} onClick={() => void load()}>
            <span className="tb-i tb-i-green">🔄</span> Refresh
          </button>
        )}
        {/* Labelled "Export to ▾" ON the toolbar, after Delete (grid.exportButton —
            the legacy XAF child-grid bar New · Edit · Delete · Export to). Read-only
            safe, so it ignores viewOnly. It then replaces the header export icon. */}
        {grid.exportButton && (
          <ExportMenu filename={grid.title} headers={exportHeaders} rows={exportRows} />
        )}
        {/* Download the SELECTED row's file (legacy XAF FileAttachment toolbar).
            Read-only safe, so it does NOT follow viewOnly — downloading changes
            nothing, and a record opened read-only must still let you take a copy. */}
        {grid.downloadAction && (
          <button
            type="button"
            className="ca-btn"
            disabled={!selected}
            onClick={() => {
              const row = rows.find((r) => rowId(r) === selected);
              const oid = String(row?.[grid.downloadAction!.fileOidKey] ?? "");
              if (!oid) {
                notify.error("This row has no file to download.");
                return;
              }
              const nameCol = grid.columns.find((c) => c.type === "file");
              void downloadChildFile(
                grid.downloadAction!.urlBase,
                oid,
                String((nameCol && row?.[nameCol.key]) ?? "attachment"),
              );
            }}
          >
            Download
          </button>
        )}
        {/* Export + Maximize — ON by default for every child grid (BO UI Standard,
            user request 2026-09-23). A grid can opt OUT with grid.hideHeaderTools
            where the owner wants a plain embedded grid (Document Creator Attachments,
            2026-09-30). */}
        {!grid.hideHeaderTools && (
          <>
            <span className="adg-header-sp" />
            {!grid.exportButton && (
              <ExportMenu
                iconOnly
                triggerClassName="adg-max-btn"
                filename={grid.title}
                headers={exportHeaders}
                rows={exportRows}
              />
            )}
            <button
              type="button"
              className="adg-max-btn"
              onClick={() => setMaximized((m) => !m)}
              title={maximized ? "Restore (Esc)" : "Maximize"}
              aria-label={maximized ? "Restore" : "Maximize"}
            >
              <i className={`ti ${maximized ? "ti-arrows-minimize" : "ti-arrows-maximize"}`} aria-hidden="true" />
            </button>
          </>
        )}
      </div>
      )}
      {/* A paged grid ALWAYS renders its pager footer (even empty → "Page 1 of 1 (0 items)",
          owner request 2026-10-23), so the footer — not the table — closes the card. */}
      <div className="sd-static-scroll">
        <table className="sd-static-tbl">
          <thead>
            <tr>
              {/* Select-all, only in multiSelect mode — with a radio there is
                  nothing for it to mean. Indeterminate when a subset is ticked,
                  the same three-state behaviour the shared AppDataGrid uses. */}
              <th className="adg-cb-col" aria-label="Select">
                {grid.multiSelect && (
                  <input
                    type="checkbox"
                    checked={selectableIds.length > 0 && selectedIds.size === selectableIds.length}
                    ref={(el) => {
                      if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < selectableIds.length;
                    }}
                    disabled={selectableIds.length === 0}
                    onChange={(e) => setSelectedIds(e.target.checked ? new Set(selectableIds) : new Set())}
                    aria-label="Select all rows"
                  />
                )}
              </th>
              {grid.rowEditIcon && <th style={{ width: 28 }} aria-label="Edit" />}
              {grid.rowDownloadIcon && grid.downloadAction && <th style={{ width: 28 }} aria-label="Download" />}
              {grid.columns.map((c) => {
                const isSorted = sorter.sort.key === c.key && sorter.sort.dir !== null;
                // Glyphs match the shared <AppDataGrid> / list-view standard:
                // ⇅ unsorted · ▲ asc · ▼ desc.
                const caret = !isSorted ? "\u21C5" : sorter.sort.dir === "asc" ? "\u25B2" : "\u25BC";
                return (
                  <th
                    key={c.key}
                    className="sd-th-sortable"
                    style={c.width ? { width: c.width } : undefined}
                    onClick={() => sorter.setSort(c.key)}
                    title={`Sort by ${c.label}`}
                  >
                    <span className="sd-th-inner">
                      <span className="sd-th-label">{c.label}</span>
                      {/* The sort-indicator caret is hidden when the form opts into
                          config.childGridsHideSortIndicator (owner request 2026-10-26:
                          "keep the filter chevron, only remove the sort shape") — the
                          column stays click-to-sort, only the glyph is dropped. */}
                      {!hideSortIndicator && (
                        <span
                          className={`sd-sort-ind${isSorted ? " active" : ""}`}
                          aria-label={isSorted ? `sorted ${sorter.sort.dir}` : "sort"}
                        >
                          {caret}
                        </span>
                      )}
                      {/* Excel-style multi-select header filter — the shared
                          popup every grid uses. Its own click stops propagation,
                          so opening it never re-sorts the column. Options are
                          cascaded by the OTHER active filters and ordered the
                          way the column is sorted. */}
                      <ColFilterButton<ChildGridEntry>
                        variant="inline"
                        col={c.key}
                        label={c.label}
                        rows={cascadeRows(entries, filters, c.key, entryText)}
                        formatter={(e) => cellText(e.r, c.key)}
                        sortDir={sorter.sort.key === c.key ? sorter.sort.dir : "asc"}
                        sortKey={(e) => e.r[c.key]}
                        value={filters[c.key] ?? null}
                        onChange={(v) => setFilters((f) => ({ ...f, [c.key]: v }))}
                      />
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageEntries.map(({ r, rid, isPending }) => {
              // pencilRowEdit: the row keeps its CELLS — each one carrying the
              // editor for its own column, so the record edits where it sits
              // instead of in a form somewhere else. Columns with no field
              // behind them (Synonym, the computed Status) stay as text.
              if (rowEditable && editRowIds.has(rid) && childConfig) {
                const draft = rowDrafts[rid] ?? {};
                const opts = cellOpts[rid] ?? {};
                return (
                  <tr key={rid} className="sd-row-editing">
                    <td style={{ textAlign: "center" }}>
                      <i className="ti ti-pencil" aria-hidden="true" />
                    </td>
                    {grid.rowEditIcon && <td />}
                    {grid.rowDownloadIcon && <td />}
                    {grid.columns.map((c) => {
                      const f = fieldForColumn(c.key);
                      if (!f) {
                        return (
                          <td key={c.key}>
                            {r[c.key] == null
                              ? ""
                              : c.type === "date"
                                ? fmtLocalDate(r[c.key] as string)
                                : c.type === "datetime"
                                  ? fmtLocalDateTime(r[c.key] as string)
                                  : String(r[c.key])}
                          </td>
                        );
                      }
                      const val = draft[f.key] ?? "";
                      // enabledWhen is tested against the SAME context the
                      // options were fetched with — the record included — so a
                      // field whose parent lives in a hidden column (Units
                      // needs Method) is not wrongly greyed out.
                      const ctx = rowCtx(rid, draft);
                      const locked =
                        isFieldLocked(f, ctx) ||
                        (f.enabledWhen?.some((k) => String(ctx[k] ?? "").trim() === "") ?? false);
                      return (
                        <td key={c.key} className="sd-cell-edit">
                          {f.type === "select" ? (
                            <select
                              className="sd-input"
                              value={val}
                              disabled={locked || savingRow}
                              onChange={(e) => changeCell(rid, f, e.target.value)}
                            >
                              {!f.hideBlankOption && <option value="">{f.noneLabel ?? "— None —"}</option>}
                              {(opts[f.key] ?? []).map((o) => (
                                <option key={o.id} value={o.id}>
                                  {o.name}
                                </option>
                              ))}
                            </select>
                          ) : f.type === "date" ? (
                            <input
                              className="sd-input"
                              type="date"
                              value={val}
                              disabled={savingRow}
                              onChange={(e) => changeCell(rid, f, e.target.value)}
                            />
                          ) : f.type === "number" ? (
                            <input
                              className="sd-input"
                              type="number"
                              value={val}
                              disabled={savingRow}
                              min={f.min}
                              max={f.max}
                              step={f.step ?? 1}
                              onChange={(e) => changeCell(rid, f, e.target.value)}
                            />
                          ) : (
                            <input
                              className="sd-input"
                              type="text"
                              value={val}
                              disabled={savingRow}
                              maxLength={f.maxLength}
                              onChange={(e) => changeCell(rid, f, e.target.value)}
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              }
              // Opt-in (grid.rowOpensDialog): clicking a saved row opens its dialog
              // (legacy XAF "click the row → popup"), independent of the parent's
              // view/edit state — the child record (e.g. an EDD query) has its own
              // save, exactly as legacy opens the EDDQuery Builder popup from the EDD
              // view. The selection radio + row buttons stopPropagation so they act
              // without opening it. A pending (unsaved) row has nothing to open yet.
              const rowOpens = !!grid.rowOpensDialog && !isPending;
              return (
                <tr
                  key={rid}
                  style={rowOpens ? { cursor: "pointer" } : undefined}
                  onClick={
                    rowOpens
                      ? () => setEditing({ id: rid, row: grid.listReturnsFullRecord ? r : undefined })
                      : undefined
                  }
                >
                  <td className="adg-cb-col">
                    {/* A buffered (pending) row CAN be selected and deleted — dropped
                        from the buffer before the parent is saved (owner request
                        2026-10-22) — it just can't be edited yet (no server record).
                        Checkbox (legacy XAF / AppDataGrid standard) only where the
                        grid opts in; everything else keeps its single-select radio. */}
                    <input
                      type={grid.multiSelect ? "checkbox" : "radio"}
                      name={grid.multiSelect ? undefined : `child-${grid.title}`}
                      checked={selectedIds.has(rid)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => {
                        if (!grid.multiSelect) {
                          setSelected(rid);
                          return;
                        }
                        setSelectedIds((s) => {
                          const next = new Set(s);
                          if (!next.delete(rid)) next.add(rid);
                          return next;
                        });
                      }}
                      aria-label="Select row"
                    />
                  </td>
                  {/* In-row edit shortcut (legacy XAF pencil) — opens the SAME
                      dialog as the Edit button, for this row, without ticking it
                      first. Disabled on a pending row: there is nothing saved to
                      edit yet. */}
                  {grid.rowEditIcon && (
                    <td style={{ textAlign: "center" }}>
                      <button
                        type="button"
                        className="sd-row-edit"
                        title="Edit"
                        aria-label="Edit"
                        disabled={viewOnly || isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditing({
                            id: rid,
                            row: grid.listReturnsFullRecord ? r : undefined,
                          });
                        }}
                      >
                        <i className="ti ti-edit" aria-hidden="true" />
                      </button>
                    </td>
                  )}
                  {/* In-row download (legacy XAF, beside the pencil on a file
                      grid) — this row's file, no selection needed. Not gated on
                      viewOnly: downloading changes nothing. */}
                  {grid.rowDownloadIcon &&
                    grid.downloadAction &&
                    (() => {
                      const fileOid = String(r[grid.downloadAction!.fileOidKey] ?? "");
                      const nameCol = grid.columns.find((c) => c.type === "file");
                      const fname = String((nameCol && r[nameCol.key]) ?? "attachment");
                      return (
                        <td style={{ textAlign: "center" }}>
                          <button
                            type="button"
                            className="sd-row-download"
                            title={`Download ${fname}`}
                            aria-label="Download"
                            disabled={isPending || !fileOid}
                            onClick={(e) => { e.stopPropagation(); void downloadChildFile(grid.downloadAction!.urlBase, fileOid, fname); }}
                          >
                            <i className="ti ti-download" aria-hidden="true" />
                          </button>
                        </td>
                      );
                    })()}
                  {grid.columns.map((c) => {
                    // A 'file' column shows the filename as a download link (legacy
                    // attachment preview): GET downloadUrlBase/{fileOid}/download.
                    if (c.type === "file") {
                      const oid = String(r[c.fileOidKey ?? ""] ?? "");
                      const name = String(r[c.key] ?? "");
                      return (
                        <td key={c.key}>
                          {oid && c.downloadUrlBase ? (
                            <button
                              type="button"
                              className="sd-file-link"
                              title={`Download ${name || "file"}`}
                              onClick={(e) => { e.stopPropagation(); void downloadChildFile(c.downloadUrlBase!, oid, name); }}
                            >
                              <i className="ti ti-file-text" /> {name || "file"}
                            </button>
                          ) : (
                            ""
                          )}
                        </td>
                      );
                    }
                    // A boolean column renders a read-only checkbox (legacy XAF grid),
                    // never the raw "true"/"false" text (owner request 2026-09-30).
                    if (c.type === "checkbox") {
                      const v = r[c.key];
                      const on = v === true || v === "true" || v === 1 || v === "1";
                      // Centre the boolean checkbox in its cell (owner report 2026-10-28, image 1054) —
                      // legacy XAF grids centre a bit column; a left-hugged box reads as mis-aligned.
                      return (
                        <td key={c.key} style={{ textAlign: "center" }}>
                          <input type="checkbox" checked={on} disabled readOnly aria-label={c.label} />
                        </td>
                      );
                    }
                    // Dates go through the shared helper, never String(...) -
                    // otherwise the raw ISO value would leak into the grid
                    // (14b.6: MM/DD/YYYY, local, 24-hour, no inline formatting).
                    const text =
                      r[c.key] == null
                        ? ""
                        : c.type === "date"
                          ? fmtLocalDate(r[c.key] as string)
                          : c.type === "datetime"
                            ? fmtLocalDateTime(r[c.key] as string)
                            : String(r[c.key]);
                    // `ellipsis` clamps a long cell (EDD QueryBuilder SQL) to its
                    // width on one line so the row height/width stays stable; the
                    // full value shows on hover (and in the row's popup).
                    return (
                      <td key={c.key} style={c.ellipsis && c.width ? { maxWidth: c.width } : undefined}>
                        {c.ellipsis ? (
                          <div className="sd-cell-clip" style={c.width ? { maxWidth: c.width } : undefined} title={text}>
                            {text}
                          </div>
                        ) : (
                          text
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {viewEntries.length === 0 && (
              <tr>
                <td
                  colSpan={
                    grid.columns.length +
                    1 +
                    (grid.rowEditIcon ? 1 : 0) +
                    (grid.rowDownloadIcon && grid.downloadAction ? 1 : 0)
                  }
                  className="sd-child-empty"
                >
                  {loading ? "Loading\u2026" : filterActive ? "No rows match the current filter" : "No data to display"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Footer — the shared pager when the grid opts into paging, otherwise the
          plain record count this grid has always shown. Either way it is ONE
          footer, closing the card's bottom edge (rule 7: never two). */}
      {paged ? (
        <div className="sd-child-pager">
          <GridPager
            total={viewEntries.length}
            page={safePage}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            note={grid.note}
            alwaysShow
          />
        </div>
      ) : (
        <div className="sd-static-note sd-child-foot">
          <span>
            Total Records: {viewEntries.length}
            {viewEntries.length !== allRows.length ? ` of ${allRows.length}` : ""}
          </span>
          {grid.note && <span className="sd-child-note">{grid.note}</span>}
        </div>
      )}

      {editing && childConfig && (() => {
        // EDD Builder ▸ Query Details → the bespoke tabbed "EDDQuery Builder" popup
        // (SQL editor + Execute/preview + Field Editor tab) instead of the generic
        // dialog — the legacy EDDQueryBuilder_DetailView_SheetEDD (owner request
        // 2026-10-22). Requires the EDD to be saved (the EDDBuilder FK).
        if (grid.childKey === "edd-query") {
          return (
            <EddQueryBuilderDialog
              queryOid={editing.id}
              prefill={editing.row}
              eddBuilderOid={parentId ?? null}
              existingCount={rows.length}
              // No parent id yet → buffer the query (like the standard child dialog);
              // writePendingChildren POSTs it with the eddBuilder FK after the parent saves.
              onCapture={parentId ? undefined : (payload) => onPending(capturePending(payload))}
              onClose={() => setEditing(null)}
              onSaved={() => { setEditing(null); if (parentId) void load(); }}
            />
          );
        }
        // Auto-numbered field (grid.seqField): on New, parse prefix<n> out of every
        // existing + pending row, seed prefix<max+1> into the dialog so the field
        // shows the next value (legacy "Rev Number": Rev1 → Rev2). Edit keeps the
        // stored value (prefill wins over this seed). Scoped so a child grid WITHOUT
        // seqField still passes exactly `parentContext`, unchanged.
        const seqSeed: Record<string, unknown> = {};
        if (grid.seqField && editing.id === null) {
          const { key, prefix } = grid.seqField;
          const re = new RegExp(`^${prefix}(\\d+)$`, "i");
          const maxN = [...rows, ...pending].reduce((mx, r) => {
            const m = re.exec(String(r[key] ?? "").trim());
            return m ? Math.max(mx, Number(m[1])) : mx;
          }, 0);
          seqSeed[key] = `${prefix}${maxN + 1}`;
        }
        // parentIdField (opt-in) seeds the parent record's own id into the child dialog's
        // form state, so a child cascade (dependsOn) can read it AND it is sent in the payload
        // — unlike parentField, which only injects into the final payload + hides the field.
        const idSeed = grid.parentIdField && parentId ? { [grid.parentIdField]: parentId } : {};
        const dialogContext = (Object.keys(seqSeed).length || Object.keys(idSeed).length)
          ? { ...parentContext, ...idSeed, ...seqSeed }
          : parentContext;
        return (
        <ModuleCrudDialog
          config={childConfig}
          id={editing.id}
          mode="edit"
          // Always mark the parent field as fixed so the dialog HIDES it (legacy
          // child popups never show the owning record) — even before the parent
          // is saved, when the value is still null and the row buffers. Once the
          // parent exists, writePendingChildren stamps the real id on POST.
          fixedValues={{ [grid.parentField]: parentId, ...(grid.fixedValues ?? {}) }}
          contextValues={dialogContext}
          // Set only when the grid declares listReturnsFullRecord; otherwise
          // undefined and the dialog fetches as before.
          prefill={editing.row}
          // No parent id yet -> buffer the row instead of POSTing an orphan.
          onCapture={parentId ? undefined : (payload) => onPending(capturePending(payload))}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            if (parentId) void load();
          }}
        />
        );
      })()}

      {/* Link picker (legacy XAF Link popup): New + Search, then a paged multi-select grid of the
          records NOT on this parent — tick box · edit icon (opens that record) · its identifying
          column (the child grid's first) — and OK / Cancel; OK links the whole ticked set. Built on
          the shared AppDialog + AppDataGrid (rule 9), so it pages 20 a page like G5's popup
          (owner report 2026-10-15: G5 and G6 Link compared). */}
      {linkOpen &&
        (() => {
          // The picker shows / searches one column — the grid's first, unless a grid names a
          // better identifier (Notes shows Date first but is picked by Title).
          const nameCol =
            (grid.linkTitleKey ? grid.columns.find((c) => c.key === grid.linkTitleKey) : undefined) ?? grid.columns[0];
          const nameKey = nameCol?.key ?? "";
          const term = linkSearch.trim().toLowerCase();
          const candidates = term
            ? linkRows.filter((r) => String(r[nameKey] ?? "").toLowerCase().includes(term))
            : linkRows;
          const toggle = (oid: string) =>
            setLinkSel((s) => {
              const next = new Set(s);
              if (next.has(oid)) next.delete(oid);
              else next.add(oid);
              return next;
            });
          // The per-row edit pencil opens the childKey's edit form; a link-only picker
          // (linkNoCreate — e.g. quotes, managed in the Open Quotes screen) omits it.
          const editCol: AppColumn<Record<string, unknown>> = {
            field: "__edit",
            header: "",
            width: 34,
            align: "center",
            sortable: false,
            text: () => "",
            render: (r) => (
              <button
                type="button"
                className="sd-link-edit"
                title="Open"
                aria-label="Open"
                disabled={viewOnly}
                onClick={(e) => {
                  e.stopPropagation();
                  setLinkOpen(false);
                  setEditing({ id: String(r[idField] ?? ""), row: grid.listReturnsFullRecord ? r : undefined });
                }}
              >
                <i className="ti ti-edit" />
              </button>
            ),
          };
          const linkColumns: AppColumn<Record<string, unknown>>[] = [
            ...(grid.linkNoCreate ? [] : [editCol]),
            { field: nameKey, header: nameCol?.label ?? "Name" },
          ];
          return (
            <AppDialog
              open
              title={childConfig?.entityLabel ?? grid.title}
              width={720}
              className={`dlg-center${childConfig?.controlHeight24 ? " inv-24" : ""}`}
              maximizable
              onClose={() => setLinkOpen(false)}
              footer={
                <>
                  <button
                    type="button"
                    className="ca-btn ca-btn-solid"
                    onClick={() => void doLink()}
                    disabled={linkBusy || linkSel.size === 0}
                  >
                    <BusyIcon busy={linkBusy} icon="ti-link" /> OK
                  </button>
                  <button type="button" className="ca-btn" onClick={() => setLinkOpen(false)} disabled={linkBusy}>
                    Cancel
                  </button>
                </>
              }
            >
              <div className="sd-link-top">
                {/* New opens the childKey's create form; a link-only picker (linkNoCreate —
                    e.g. quotes, created in the Open Quotes screen) hides it. */}
                {!grid.linkNoCreate && (
                  <button
                    type="button"
                    className="ca-btn"
                    disabled={viewOnly}
                    onClick={() => {
                      setLinkOpen(false);
                      setEditing({ id: null });
                    }}
                  >
                    New
                  </button>
                )}
                {/* Enter here searches — it must not press OK (AppDialog's Enter-to-submit). */}
                <div data-hotkeys-ignore>
                  <GridSearch value={linkSearch} onChange={setLinkSearch} placeholder="Text to search…" />
                </div>
              </div>
              <AppDataGrid<Record<string, unknown>>
                rows={candidates}
                columns={linkColumns}
                getRowId={(r) => String(r[idField] ?? "")}
                defaultPageSize={20}
                selectable
                selectedIds={linkSel}
                onSelectionChange={(s) => setLinkSel(new Set([...s].map(String)))}
                onRowClick={(r) => toggle(String(r[idField] ?? ""))}
                emptyText="No records to link."
              />
            </AppDialog>
          );
        })()}
    </div>
  );
}

/** Renders one {@link CrudStaticSection}: a read-only reference table plus an
 *  optional note. Purely presentational — it holds no state and issues no
 *  request, because its content is configuration, not data.
 *
 *  The table scrolls inside its own container so a wide matrix never makes the
 *  PAGE scroll sideways (§7.1 responsive rule). */
function StaticSection({ section }: { section: CrudStaticSection }) {
  const strong = new Set(section.strong ?? []);
  return (
    <div className="sd-static">
      <h3 className="sd-sub-h">{section.title}</h3>
      <div className="sd-static-scroll">
        <table className="sd-static-tbl">
          <thead>
            <tr>
              {section.columns.map((c) => (
                <th key={c}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {section.rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j}>{strong.has(cell) ? <strong>{cell}</strong> : cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {section.note && (
        <div className="sd-static-note">
          {section.note.label && <strong>{section.note.label} </strong>}
          {section.note.text}
        </div>
      )}
    </div>
  );
}

/** The legacy Reagent "Result Correction Formula" field — a textarea PLUS a
 *  "Formula" button that opens the builder popup (field.formulaBuilder). The button
 *  is gated exactly as legacy `Formula_Execute`: C1/C2/V2 units set AND
 *  ((Approach contains "V1" AND V1 set) OR (Approach contains "W1" AND W1 set)), else
 *  the verbatim "Select the all units." The box itself is ALWAYS read-only — the
 *  formula is written only through the popup; in view mode the button is hidden
 *  too. Owns only the popup open state. */
function FormulaBuilderField({
  field,
  value,
  readOnly,
  showEmpty,
  approachLabel,
  unitInfo,
  operatorsUrl,
  onChange,
}: {
  field: CrudField;
  value: unknown;
  readOnly: boolean;
  showEmpty: boolean;
  /** The Calculation Approach's display text (its last word is the target variable). */
  approachLabel: string;
  /** Each of C1/C2/V1/V2/W1 with the unit chosen for it and whether it is set. */
  unitInfo: { variable: string; unit: string; isSet: boolean }[];
  operatorsUrl: string;
  onChange: (v: unknown) => void;
}) {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const text = value == null ? "" : String(value);
  // `sd-input--tall` = the shared two-row textarea height; legacy shows the formula box two lines tall.
  // The box is never typed into: the formula is built in the popup only (owner 2026-10-05).
  const cls = `sd-input sd-input--tall readonly${showEmpty ? " required-empty" : ""}`;

  // Legacy: target = the Approach's last word; HideV1 ⟺ Approach lacks "V1", HideW1 ⟺ lacks "W1".
  const target = approachLabel.trim().split(/\s+/).filter(Boolean).pop() ?? "";
  const byVar = new Map(unitInfo.map((u) => [u.variable, u]));
  const isSet = (v: string) => byVar.get(v)?.isSet ?? false;

  const openBuilder = () => {
    // The exact legacy Formula_Execute precondition (RegentPrepCalculationEditor BO
    // HideV1Unit/HideW1Unit = the Approach not containing "V1"/"W1").
    const ok =
      isSet("C1") &&
      isSet("C2") &&
      isSet("V2") &&
      ((approachLabel.includes("V1") && isSet("V1")) || (approachLabel.includes("W1") && isSet("W1")));
    if (!ok) {
      notify.error("Select the all units.");
      return;
    }
    setOpen(true);
  };

  // Seed `{target}({unit})=` when the target is one of the five variables (legacy OnShowing).
  const targetUnit = byVar.get(target)?.unit ?? "";
  const seedPrefix = byVar.has(target) && targetUnit ? `${target}(${targetUnit})=` : "";
  // Variables list = unit set AND not the target (legacy VariableAndUnits build).
  const variables = unitInfo
    .filter((u) => u.isSet && u.variable !== target)
    .map((u) => ({ variable: u.variable, unit: u.unit }));

  return (
    <div className="sd-row full">
      <label className="sd-label" htmlFor={`sd-${field.key}`}>
        {field.label}
        {(field.required || field.requiredStar) ? <span className="req">*</span> : null}
      </label>
      <div className="sd-formula-wrap">
        <textarea
          id={`sd-${field.key}`}
          className={cls}
          rows={3}
          readOnly
          disabled={readOnly}
          spellCheck={false}
          value={text}
        />
        {!readOnly && (
          <button
            type="button"
            className="ca-btn sd-formula-btn"
            onClick={openBuilder}
            title="Build the result-correction formula"
          >
            <i className="ti ti-math-function" aria-hidden="true" /> Formula
          </button>
        )}
      </div>
      {showEmpty && (
        <span className="sd-field-error">{field.requiredMessage ?? `${field.label} Should not be empty.`}</span>
      )}
      {field.hint && <span className="sd-hint">{field.hint}</span>}
      {open && (
        <ReagentFormulaDialog
          open
          initialText={text}
          seedPrefix={seedPrefix}
          variables={variables}
          operatorsUrl={operatorsUrl}
          onCancel={() => setOpen(false)}
          onSave={(t) => {
            setOpen(false);
            onChange(t);
          }}
        />
      )}
    </div>
  );
}

/** A plain select, PLUS the legacy XAF grid-lookup "New" button when the field
 *  declares `quickAdd`: a "+" beside the dropdown that opens the QuickAddDialog to
 *  create a lookup value inline, then selects the freshly-created row. Owns only the
 *  popup open/close state, so Field's own hooks stay unconditional. */
function SelectWithQuickAdd({
  field,
  value,
  options,
  ro,
  id,
  cls,
  square = false,
  quickAddContext,
  onChange,
  onOptionsReplaced,
}: {
  field: CrudField;
  value: unknown;
  options: Option[];
  ro: boolean;
  id: string;
  cls: string;
  square?: boolean;
  /** The surrounding record's values, so a quick-add `variant:"contact"` can resolve its
   *  `dependsOn` parent (the selected Client) for the create URL + the cascaded re-read. */
  quickAddContext?: Record<string, unknown>;
  onChange: (v: unknown) => void;
  onOptionsReplaced?: (opts: Option[]) => void;
}) {
  const [open, setOpen] = useState(false);
  // For a `variant:"contact"` quick-add: the chosen parent id (the Client). The "+" creates
  // the contact UNDER it (POST /clients/{id}/contacts) and the re-read is the cascaded list
  // (?ownerId={id}), so the new contact is found and selected. Empty → the "+" is disabled.
  const qaDep = depsOf(field)[0];
  const qaParentId = qaDep ? String(quickAddContext?.[qaDep] ?? "") : "";
  const qaContactUrl = qaParentId ? cascadeUrl(field.optionsUrl ?? "", field, quickAddContext ?? {}) : "";

  // `allowCustom` (opt-in): pick from the list OR type a new value — the legacy
  // combo with a free-text editor. A native datalist keeps it the same plain
  // control as the select beside it.
  if (field.allowCustom) {
    return (
      <>
        <input
          id={id}
          className={cls}
          type="text"
          list={`${id}-options`}
          value={value == null ? "" : String(value)}
          readOnly={ro}
          maxLength={field.maxLength}
          autoComplete="off"
          onChange={(e) => onChange(e.target.value)}
        />
        <datalist id={`${id}-options`}>
          {options.map((o) => <option key={o.id} value={o.id}>{o.name === o.id ? undefined : o.name}</option>)}
        </datalist>
      </>
    );
  }

  // The legacy XAF lookup control (orange caret + highlighted row) that the
  // Department / Division details use. Checked BEFORE comboList so a field can
  // move from one to the other by changing a single flag.
  if (field.aiCombo) {
    return (
      <AiCombo
        className={cls.includes("required-empty") ? "required-empty" : undefined}
        value={value == null ? null : String(value)}
        options={options.map((o) => ({ value: o.id, label: o.name }))}
        placeholder={field.noneLabel ?? "— None —"}
        disabled={ro}
        erasable={field.erasable}
        onChange={(v) => onChange(v ?? "")}
      />
    );
  }

  if (field.comboList) {
    return (
      <ComboSelect
        className={`sd-combo${cls.includes("required-empty") ? " required-empty" : ""}`}
        value={value == null ? "" : String(value)}
        options={options.map((o) => ({ value: o.id, label: o.name }))}
        placeholder={field.noneLabel ?? "— None —"}
        disabled={ro}
        clearable={!field.hideBlankOption}
        clearAlways={!!field.clearAlways}
        zebra={square}
        onChange={onChange}
      />
    );
  }

  // Welded ＋ (owner report 2026-10-12, image 441): the shared <AiCombo> with the
  // "New" button welded to the dropdown caret as ONE control (the Payment Terms
  // "Terms" style), instead of a native <select> + a separate ＋ box. Opt-in per
  // field; read-only falls through to the plain select below.
  if (field.quickAdd?.welded && !ro) {
    const qa = field.quickAdd;
    // A client-scoped contact "+" is inert until its parent (the Client) is chosen — the new contact
    // must belong to it, else it would not appear in the cascaded list. newDisabled greys ONLY the
    // welded ＋, leaving the dropdown usable. A STANDALONE contact "+" (Open Prospect ▸ Primary
    // Contact — a prospect is not a Customer) needs no parent, so it is never gated.
    const qaDisabled = qa.variant === "contact" && !qa.standalone && !qaParentId;
    return (
      <>
        <AiCombo
          value={value == null ? null : String(value)}
          options={options.map((o) => ({ value: o.id, label: o.name }))}
          onChange={(v) => onChange(v ?? "")}
          onNew={() => setOpen(true)}
          newTitle={qaDisabled ? "Select a Client first" : `Add ${qa.title}`}
          newDisabled={qaDisabled}
          placeholder={field.noneLabel ?? "— None —"}
          naLabel={field.noneLabel ?? "— None —"}
          className={cls.includes("required-empty") ? "required-empty" : undefined}
          disabled={ro}
          erasable={field.erasable}
          compact
        />
        {open &&
          (qa.variant === "prospect" ? (
            <OpenProspectDialog
              createUrl={qa.createUrl}
              optionsUrl={field.optionsUrl ?? ""}
              onClose={() => setOpen(false)}
              onAdded={(opts, selectedId) => {
                onOptionsReplaced?.(opts);
                if (selectedId) onChange(selectedId);
              }}
            />
          ) : qa.variant === "country" ? (
            <CountryDialog
              createUrl={qa.createUrl}
              optionsUrl={field.optionsUrl ?? ""}
              onClose={() => setOpen(false)}
              onAdded={(opts, selectedId) => {
                onOptionsReplaced?.(opts);
                if (selectedId) onChange(selectedId);
              }}
            />
          ) : qa.variant === "state" ? (
            // The full legacy State master (StateDialog) in STANDALONE create mode — posts to
            // /api/v1/system/states + re-reads this field's options (owner report 2026-10-28, image 1046).
            <StateDialog
              standalone={{ optionsUrl: field.optionsUrl ?? "", onAdded: (opts, selectedId) => {
                onOptionsReplaced?.(opts);
                if (selectedId) onChange(selectedId);
              } }}
              onClose={() => setOpen(false)}
            />
          ) : qa.variant === "city" ? (
            <CityDialog
              standalone={{ optionsUrl: field.optionsUrl ?? "", onAdded: (opts, selectedId) => {
                onOptionsReplaced?.(opts);
                if (selectedId) onChange(selectedId);
              } }}
              onClose={() => setOpen(false)}
            />
          ) : qa.variant === "contact" ? (
            // The full CRM Contact master popup (ContactDialog). STANDALONE (Open Prospect ▸ Primary
            // Contact) → POST qa.createUrl (/api/v1/clients/contacts, Customer NULL) + re-read the
            // field's own options; otherwise create UNDER the selected Client (the cascaded list).
            <ContactDialog
              createUrl={qa.standalone ? qa.createUrl : `/api/v1/clients/${qaParentId}/contacts`}
              optionsUrl={qa.standalone ? (field.optionsUrl ?? "") : qaContactUrl}
              onClose={() => setOpen(false)}
              onAdded={(opts, selectedId) => {
                onOptionsReplaced?.(opts);
                if (selectedId) onChange(selectedId);
              }}
            />
          ) : (
            <QuickAddDialog
              spec={qa}
              optionsUrl={field.optionsUrl ?? ""}
              onClose={() => setOpen(false)}
              onAdded={(opts, selectedId) => {
                onOptionsReplaced?.(opts);
                if (selectedId) onChange(selectedId);
              }}
            />
          ))}
      </>
    );
  }

  // On a square-control (Utility) form the plain select is the shared <ComboSelect>, so
  // its open list matches every other dropdown instead of the browser-native blue one.
  const select = square ? (
    <ComboSelect
      className={`sd-combo${cls.includes("required-empty") ? " required-empty" : ""}`}
      value={value == null ? "" : String(value)}
      options={options.map((o) => ({ value: o.id, label: o.name }))}
      placeholder={field.noneLabel ?? "— None —"}
      disabled={ro}
      clearable={!field.hideBlankOption}
      clearAlways={!!field.clearAlways}
      zebra={square}
      onChange={onChange}
    />
  ) : (
    <select
      id={id}
      className={cls}
      value={value == null ? "" : String(value)}
      disabled={ro}
      onChange={(e) => onChange(e.target.value)}
    >
      {!field.hideBlankOption && <option value="">{field.noneLabel ?? "— None —"}</option>}
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  );

  if (!field.quickAdd || ro) return select;

  // A client-scoped contact "+" is inert until its parent (the Client) is chosen — the new contact
  // must belong to it, else it would not appear in the cascaded list. A STANDALONE contact "+"
  // (Open Prospect ▸ Primary Contact) needs no parent, so it is never gated.
  const qaDisabled = field.quickAdd.variant === "contact" && !field.quickAdd.standalone && !qaParentId;
  return (
    <div className="sd-select-qa">
      {select}
      <button
        type="button"
        className="sd-qa-btn"
        title={qaDisabled ? "Select a Client first" : `Add ${field.quickAdd.title}`}
        aria-label={`Add ${field.quickAdd.title}`}
        disabled={qaDisabled}
        onClick={() => setOpen(true)}
      >
        <i className="ti ti-plus" aria-hidden="true" />
      </button>
      {open &&
        (field.quickAdd.variant === "prospect" ? (
          <OpenProspectDialog
            createUrl={field.quickAdd.createUrl}
            optionsUrl={field.optionsUrl ?? ""}
            onClose={() => setOpen(false)}
            onAdded={(opts, selectedId) => {
              onOptionsReplaced?.(opts);
              if (selectedId) onChange(selectedId);
            }}
          />
        ) : field.quickAdd.variant === "country" ? (
          <CountryDialog
            createUrl={field.quickAdd.createUrl}
            optionsUrl={field.optionsUrl ?? ""}
            onClose={() => setOpen(false)}
            onAdded={(opts, selectedId) => {
              onOptionsReplaced?.(opts);
              if (selectedId) onChange(selectedId);
            }}
          />
        ) : field.quickAdd.variant === "contact" ? (
          // The full CRM Contact master popup (ContactDialog). STANDALONE (Open Prospect ▸ Primary
          // Contact) → POST qa.createUrl (/api/v1/clients/contacts, Customer NULL) + re-read the
          // field's own options; otherwise create UNDER the selected Client (the cascaded list).
          <ContactDialog
            createUrl={field.quickAdd.standalone ? field.quickAdd.createUrl : `/api/v1/clients/${qaParentId}/contacts`}
            optionsUrl={field.quickAdd.standalone ? (field.optionsUrl ?? "") : qaContactUrl}
            onClose={() => setOpen(false)}
            onAdded={(opts, selectedId) => {
              onOptionsReplaced?.(opts);
              if (selectedId) onChange(selectedId);
            }}
          />
        ) : (
          <QuickAddDialog
            spec={field.quickAdd}
            optionsUrl={field.optionsUrl ?? ""}
            onClose={() => setOpen(false)}
            onAdded={(opts, selectedId) => {
              onOptionsReplaced?.(opts);
              if (selectedId) onChange(selectedId);
            }}
          />
        ))}
    </div>
  );
}

function Field({
  field,
  value,
  options,
  showEmpty,
  readOnly,
  pairedWithCheckbox = false,
  horizontalLabels = false,
  usDate = false,
  square = false,
  quickAddContext,
  onChange,
  onFill,
  onOptionsReplaced,
  insertBase,
  hideLabel = false,
  viewHtml,
  lockReason,
}: {
  field: CrudField;
  value: unknown;
  options: Option[];
  showEmpty: boolean;
  /** The surrounding record's values, passed through to a `variant:"contact"` quick-add so
   *  its "+" can resolve the selected Client for the contact's create URL + cascaded re-read. */
  quickAddContext?: Record<string, unknown>;
  /** For type 'datasource' — the CURRENT value of the rich-text field the "Add"
   *  button appends into (`field.insertIntoKey`), so a click appends to what is
   *  already in the Body rather than replacing it. */
  insertBase?: string;
  /** VIEW mode — render the control disabled/greyed (same as type 'readonly'). */
  readOnly: boolean;
  /** WHY this field is disabled for THIS record (CrudField.disabledWhen), shown
   *  as the control's tooltip. A greyed control with no explanation reads as a
   *  bug; this is the difference between "you cannot" and "you cannot, because…". */
  lockReason?: string | null;
  /** The field opposite this one in the grid is ALSO a checkbox, so there is no
   *  neighbouring label to line up against and the spacer is dropped. Set by the
   *  grid, which is the only thing that knows the pairing. */
  pairedWithCheckbox?: boolean;
  /** When true (horizontalLabels mode) a checkbox renders its label text in the
   *  LEFT label column and places just the tick box in the field column — matching
   *  the layout of every other row in that mode. */
  horizontalLabels?: boolean;
  /** ModuleCrudConfig.usDateFormat — render date fields as MM/DD/YYYY text. */
  usDate?: boolean;
  /** Square-control (Settings ▸ Utility) form: a plain select renders as the shared
   *  <ComboSelect> so every dropdown looks the same, not the browser-native blue list. */
  square?: boolean;
  onChange: (v: unknown) => void;
  /** Auto-fill sibling fields from a picked lookup row (type 'lookup'). */
  onFill?: (patch: Record<string, unknown>) => void;
  /** Replace this select's option list after an inline quick-add (field.quickAdd). */
  onOptionsReplaced?: (opts: Option[]) => void;
  /** No label — the field fills a fieldTabs page whose tab caption already names it
   *  (legacy ShowCaption="False"). Honoured by the rich-text field. */
  hideLabel?: boolean;
  /** For type 'richdoc' — the document as HTML (values[field.htmlKey]), shown in VIEW mode. */
  viewHtml?: string;
}) {
  // Layout-only spacer: an empty grid cell that keeps the following fields in
  // their intended column/row (legacy Maintenance Setup leaves the last left
  // cell blank so Alert lands opposite it in the right column).
  if (field.type === "spacer") return <div className="sd-row" aria-hidden="true" />;

  // undefined, not null, so React omits the attribute entirely when there is
  // no reason to give.
  const lockTitle = lockReason ?? undefined;

  const id = `sd-${field.key}`;
  const isArea = field.type === "textarea";
  const isRich = field.type === "richtext";
  // A textarea whose stored value is legacy RTF (e.g. Accreditation Statement).
  const isRtf = isArea && field.rtf === true;
  const ro = readOnly || field.type === "readonly" || field.readOnly === true;
  const cls = `sd-input${showEmpty ? " required-empty" : ""}${ro ? " readonly" : ""}`;

  // RTF document (type 'richdoc') — the G5 DevExpress ribbon editor while editing; in
  // VIEW mode the formatted HTML rendering, as G5 shows the document read-only.
  if (field.type === "richdoc") {
    return (
      <div className={`sd-row full${hideLabel ? " sd-row--nolabel" : ""}`}>
        {!hideLabel && (
          <label className="sd-label">
            {field.label}
            {(field.required || field.requiredStar) ? <span className="req">*</span> : null}
          </label>
        )}
        {ro ? (
          <div
            className="sd-input readonly sd-rich-view"
            aria-readonly="true"
            dangerouslySetInnerHTML={{ __html: viewHtml ?? "" }}
          />
        ) : (
          <DxRichEditor
            value={value == null ? "" : String(value)}
            onChange={(rtf) => onChange(rtf)}
            documentName={field.label}
          />
        )}
      </div>
    );
  }

  // Rich-text (HTML) field — reuses the shared RichTextEditor (variant 'full' =
  // the legacy XAF HtmlEditor: font/size/colour + Design/HTML/Preview tabs). In
  // VIEW/read-only mode it renders the stored HTML as a bordered preview.
  if (isRich) {
    const html = value == null ? "" : String(value);
    return (
      <div className={`sd-row full${hideLabel ? " sd-row--nolabel" : ""}`}>
        {!hideLabel && (
          <label className="sd-label">
            {field.label}
            {(field.required || field.requiredStar) ? <span className="req">*</span> : null}
          </label>
        )}
        {ro ? (
          <div
            className="sd-input readonly sd-rich-view"
            aria-readonly="true"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <RichTextEditor value={html} onChange={(h) => onChange(h)} variant="full" invalid={showEmpty} />
        )}
        {field.hint && <span className="sd-hint">{field.hint}</span>}
      </div>
    );
  }

  // RTF field (legacy dbo.Accrediation.Text). VIEW renders the flattened,
  // readable statement via the SAME conversion the report cover page uses, in a
  // bordered read-only box with paragraph breaks preserved (matches ALPACA's
  // rendered statement). EDIT keeps the raw RTF textarea so a save never
  // rewrites the XAF-shared value. (User request — Statement showed raw RTF.)
  if (isRtf) {
    const raw = value == null ? "" : String(value);
    return (
      <div className="sd-row full">
        <label className="sd-label">
          {field.label}
          {(field.required || field.requiredStar) ? <span className="req">*</span> : null}
        </label>
        {ro ? (
          <div className={`${cls} sd-rich-view`} aria-readonly="true" style={{ whiteSpace: "pre-wrap" }}>
            {rtfToPlainText(raw)}
          </div>
        ) : (
          <textarea
            id={id}
            className={cls}
            rows={15}
            value={rtfToPlainText(raw)}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
        {field.hint && <span className="sd-hint">{field.hint}</span>}
      </div>
    );
  }

  if (field.type === "checkbox") {
    // In horizontalLabels mode: render the field label in the left label column
    // and put only the tick box in the field column — matching every other row.
    // checkboxLabelLeft is the per-field opt-in for a sideLabels form, which
    // otherwise takes the inline-caption branch below (owner request 2026-10-15).
    if (horizontalLabels || field.checkboxLabelLeft) {
      return (
        <div className="sd-row sd-row--check" title={lockTitle}>
          <label className="sd-label" htmlFor={id}>
            {field.label}
            {(field.required || field.requiredStar) ? <span className="req">*</span> : null}
          </label>
          <input
            id={id}
            type="checkbox"
            checked={Boolean(value)}
            disabled={ro}
            // NOT .sd-input on the checkboxLabelLeft path: that is the full-width
            // FIELD style, so on a sideLabels + squareControls form it stretched the
            // tick box across the whole column (owner report 2026-10-15). A bare
            // checkbox keeps its natural size and sits at the left of the cell, like
            // an inline-caption row (Is Group). horizontalLabels forms are unchanged.
            className={horizontalLabels ? "sd-input" : "sd-check-box"}
            onChange={(e) => onChange(e.target.checked)}
          />
        </div>
      );
    }
    return (
      <div className="sd-row sd-row--check">
        {/* An EMPTY label spacer so the checkbox aligns with the field column.
            In normal (2-column) mode it keeps the row the same height as its
            neighbour so the tick box lines up with the adjacent INPUT.
            SKIPPED when the paired field is also a checkbox: no label opposite,
            so the spacer would only push both boxes down and leave dead space. */}
        {!pairedWithCheckbox && (
          <span className="sd-label" aria-hidden="true">
            &nbsp;
          </span>
        )}
        <label className="sd-check" htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            checked={Boolean(value)}
            disabled={ro}
            onChange={(e) => onChange(e.target.checked)}
          />
          {field.label}
        </label>
      </div>
    );
  }

  // Data Source — the legacy XAF merge-token side panel (Email Content Template).
  // `options` are the merge tokens; the field VALUE is the comma-joined checked set
  // (matching the stored Datasource column). Toggling a token updates that value;
  // "Add" appends the checked tokens into the rich-text field named by
  // `field.insertIntoKey` (the Body), via onFill.
  if (field.type === "datasource") {
    const raw = value == null ? "" : String(value);
    const checked = new Set(
      raw.split(",").map((s) => s.trim()).filter(Boolean),
    );
    const toggle = (tok: string, on: boolean) => {
      if (on) checked.add(tok);
      else checked.delete(tok);
      onChange(options.filter((o) => checked.has(o.name)).map((o) => o.name).join(","));
    };
    const addToBody = () => {
      if (!field.insertIntoKey || checked.size === 0 || !onFill) return;
      const tokens = options.filter((o) => checked.has(o.name)).map((o) => o.name).join(" ");
      const base = insertBase ?? "";
      const sep = base && !/\s$/.test(base) ? " " : "";
      onFill({ [field.insertIntoKey]: base + sep + tokens });
    };
    return (
      <div className="sd-ds-panel">
        {/* Label on the LEFT of the checklist (owner request 2026-09-30), matching
            the form's side-label style, rather than a heading on top. */}
        <label className="sd-ds-label">{field.label}</label>
        <div className="sd-ds-main">
          <div className="sd-ds-list">
            {options.map((o) => (
              <label key={o.id} className="sd-ds-item">
                <input
                  type="checkbox"
                  disabled={ro}
                  checked={checked.has(o.name)}
                  onChange={(e) => toggle(o.name, e.target.checked)}
                />
                {o.name}
              </label>
            ))}
          </div>
          {/* Rows-count footer (legacy "Page 1 of 1 (N items)"). */}
          <div className="sd-ds-foot">Page 1 of 1 ({options.length} items)</div>
          {!ro && field.insertIntoKey && (
            <button
              type="button"
              className="ca-btn sd-ds-add"
              onClick={addToBody}
              disabled={checked.size === 0}
              title="Insert the checked tokens into the Body"
            >
              <span className="tb-i">＋</span> Add
            </button>
          )}
          {field.hint && <span className="sd-hint">{field.hint}</span>}
        </div>
      </div>
    );
  }

  // Multi-select — a checked-list DROPDOWN (shared CheckedCombo), matching the
  // legacy XAF CheckedListBoxEditor. Options are {id,name}; the stored value is
  // an array of ids, so map id↔name at the control boundary.
  if (field.type === "multiselect") {
    const ids = Array.isArray(value) ? (value as unknown[]).map(String) : [];
    // Match ids case-INSENSITIVELY: legacy XAF stores VisualMatrix (and similar)
    // Oids lowercase, while SQL `uniqueidentifier` options come back UPPERCASE, so
    // a case-sensitive lookup left the control showing nothing selected even when
    // the record had values (e.g. Rejection Criteria / Site Source / Sample Type;
    // reported & fixed 2026-09-26). Save still uses the option's own id casing.
    const nameByIdUpper = new Map(options.map((o) => [String(o.id).toUpperCase(), o.name] as const));
    const idByName = new Map(options.map((o) => [o.name, o.id] as const));
    const selectedNames = ids.map((i) => nameByIdUpper.get(i.toUpperCase())).filter((n): n is string => !!n);
    return (
      <div className="sd-row">
        <label className="sd-label">
          {field.label}
          {(field.required || field.requiredStar) ? <span className="req">*</span> : null}
        </label>
        <div className="sd-ms">
          <CheckedCombo
            options={options.map((o) => o.name)}
            values={selectedNames}
            onChange={(names) => onChange(names.map((n) => idByName.get(n)).filter((x): x is string => !!x))}
            disabled={ro}
            required={showEmpty}
            placeholder={field.noneLabel ?? "Select…"}
            ariaLabel={field.label}
            pageSize={field.pageSize}
            summaryJoin={field.summarySeparator}
            hideOk={field.hideOk}
            zebra={square}
          />
        </div>
        {field.hint && <span className="sd-hint">{field.hint}</span>}
      </div>
    );
  }

  // Grid-lookup — the legacy XAF ASPxGridLookup: a searchable multi-column popup
  // (e.g. Client · Address · Phone). Self-fetches its rows; stores the id, shows
  // the name.  (Names may repeat, so the id travels with the picked row.)
  if (field.type === "lookup") {
    return (
      <div className="sd-row">
        <label className="sd-label">
          {field.label}
          {(field.required || field.requiredStar) ? <span className="req">*</span> : null}
        </label>
        <div className="sd-ms">
          <LookupField
            field={field}
            value={value}
            readOnly={ro}
            showEmpty={showEmpty}
            onChange={onChange}
            onFill={onFill}
          />
        </div>
        {field.hint && <span className="sd-hint">{field.hint}</span>}
      </div>
    );
  }

  return (
    <div
      className={`sd-row${isArea && !field.halfWidth ? " full" : ""}`}
      style={field.rowSpan ? { gridRow: `span ${field.rowSpan}` } : undefined}
      /* The reason a disabled field is disabled, on the whole row so it is
         reachable from the label as well as the control. */
      title={lockTitle}
    >
      {/* The id lets a radio GROUP point at this label with aria-labelledby —
          a fieldset's options have no single control for htmlFor to name.
          A BLANK label (e.g. the Contact "Report / Invoice" radio, shown with no
          side label) is marked aria-hidden so the side-label CSS does not append a
          lone ":" — the same exclusion the checkbox spacer uses. */}
      <label
        className="sd-label"
        id={`${id}-lbl`}
        htmlFor={id}
        aria-hidden={field.label?.trim() ? undefined : true}
      >
        {field.label}
        {(field.required || field.requiredStar) ? <span className="req">*</span> : null}
      </label>
      {field.type === "readonly" ? (
        <input
          id={id}
          className={cls}
          type="text"
          readOnly
          disabled
          placeholder={field.placeholder}
          // An EXPLICIT placeholder wins, including an empty one - that is how a
          // read-only field asks to render blank rather than the '(auto)' hint.
          // Testing truthiness instead would make placeholder:'' indistinguishable
          // from not setting it at all.
          value={value == null || value === "" ? (field.placeholder !== undefined ? "" : "(auto)") : String(value)}
        />
      ) : field.type === "radio" ? (
        // A radio GROUP for a short, fixed choice — every option visible at once
        // instead of hidden behind a dropdown. Use it only for a handful of
        // values that never grow; anything longer, or loaded from an endpoint,
        // belongs in a 'select'.
        //
        // `role="radiogroup"` + aria-labelledby ties the options to the field's
        // own label, so a screen reader announces "Action Type" once rather than
        // reading three orphaned radios (§7.1 a11y).
        <div className="sd-radios" role="radiogroup" aria-labelledby={`${id}-lbl`}>
          {options.map((o) => (
            <label key={o.id} className={`sd-radio${ro ? " readonly" : ""}`}>
              <input
                type="radio"
                // One name per FIELD, so the options are mutually exclusive
                // within this group and independent of any other on the form.
                name={id}
                value={o.id}
                checked={String(value ?? "") === o.id}
                disabled={ro}
                onChange={() => onChange(o.id)}
              />
              <span>{o.name}</span>
            </label>
          ))}
        </div>
      ) : field.type === "select" ? (
        <SelectWithQuickAdd
          field={field}
          square={square}
          value={value}
          options={options}
          ro={ro}
          id={id}
          cls={cls}
          quickAddContext={quickAddContext}
          onChange={onChange}
          onOptionsReplaced={onOptionsReplaced}
        />
      ) : field.type === "number" ? (
        <input
          id={id}
          className={cls}
          type="number"
          min={field.min}
          max={field.max}
          step={field.step ?? 1}
          value={value == null ? "" : String(value)}
          readOnly={ro}
          disabled={ro}
          onChange={(e) => onChange(e.target.value)}
          // Settle a typed value to the field's fixed decimals on blur, so 5
          // becomes 5.00 like the value that arrived from the server. Left alone
          // while typing — reformatting mid-keystroke fights the caret.
          onBlur={
            field.decimals == null
              ? undefined
              : (e) => {
                  const s = e.target.value.trim();
                  if (s === "") return;
                  const n = Number(s);
                  if (Number.isFinite(n)) onChange(n.toFixed(field.decimals));
                }
          }
        />
      ) : field.type === "date" ? (
        usDate ? (
          // MM/DD/YYYY whatever the browser locale is (ModuleCrudConfig.usDateFormat).
          <UsDateInput
            id={id}
            className={cls}
            value={value == null ? "" : String(value)}
            readOnly={ro}
            onChange={onChange}
          />
        ) : (
          // sd-date-blank hides the browser's own "dd-mm-yyyy" hint while the field
          // is empty (see the CSS) — legacy shows a plain empty box there.
          <input
            id={id}
            type="date"
            className={`${cls}${value == null || value === "" ? " sd-date-blank" : ""}`}
            value={value == null ? "" : String(value)}
            readOnly={ro}
            disabled={ro}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      ) : field.datePickText ? (
        // Free text + a calendar that writes MM/DD/YYYY into it (CrudField.datePickText) — the stored text is never reinterpreted.
        <DatePickText
          id={id}
          className={cls}
          value={value == null ? "" : String(value)}
          readOnly={ro}
          maxLength={field.maxLength}
          onChange={onChange}
        />
      ) : isArea ? (
        <textarea
          id={id}
          className={cls}
          rows={3}
          // Opt-in taller box (CrudField.textRows): ~18px a row plus the box's own padding.
          style={field.textRows ? { height: field.textRows * 18 + 10, minHeight: 0, whiteSpace: 'pre-wrap' } : undefined}
          maxLength={field.maxLength}
          readOnly={ro}
          disabled={ro}
          value={value == null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          id={id}
          className={cls}
          type="text"
          maxLength={field.maxLength}
          readOnly={ro}
          disabled={ro}
          value={value == null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {showEmpty && (
        <span className="sd-field-error">
          {/* Standard fallback wording per CrudField.requiredMessage doc; a field's
              own requiredMessage (legacy [RuleRequiredField] verbatim) wins. */}
          {field.requiredMessage ?? `${field.label} Should not be empty.`}
        </span>
      )}
      {field.hint && <span className="sd-hint">{field.hint}</span>}
    </div>
  );
}

/** Grid-lookup FK field — wraps the shared <LookupCombo> (legacy ASPxGridLookup).
 *  Self-fetches `{ data: [{ id, name, cols }] }` from `field.optionsUrl`; the
 *  stored value is the id, the trigger shows the name, and the popup shows
 *  `field.lookupColumns` (Client · Address · Phone …). The picked row carries its
 *  id back (names can repeat), so selection is unambiguous. */
function LookupField({
  field,
  value,
  readOnly,
  showEmpty,
  onChange,
  onFill,
}: {
  field: CrudField;
  value: unknown;
  readOnly: boolean;
  showEmpty: boolean;
  onChange: (v: unknown) => void;
  onFill?: (patch: Record<string, unknown>) => void;
}) {
  interface LkRow {
    id: string;
    cells: string[];
  }
  const [rows, setRows] = useState<LkRow[]>([]);
  const selectedId = value == null ? "" : String(value);
  // lookupEditClient: the picker's edit pencil opens the editable Client detail; on
  // save we bump reloadKey to re-fetch the picker rows so the edit shows at once.
  const [editId, setEditId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // The welded "+" that CREATES a new Client (quickAdd.variant "client") — opens the full
  // Customer form (ClientDialog), then selects the new client (owner report 2026-10-26, image 978).
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!field.optionsUrl) return;
    let alive = true;
    // Include the currently-selected id so a value that is not in the live picker
    // list (e.g. a soft-deleted client still referenced by this record) still
    // resolves to its name instead of showing the "N/A" placeholder. The endpoint
    // returns the live options plus any requested id; endpoints that don't read
    // `ids` simply ignore it, so this is safe for every lookup.
    const url = selectedId
      ? `${field.optionsUrl}${field.optionsUrl.includes("?") ? "&" : "?"}ids=${encodeURIComponent(selectedId)}`
      : field.optionsUrl;
    api
      .get<{ data?: Array<{ id: string; name: string; cols?: string[] }> }>(url)
      .then(({ data }) => {
        if (!alive) return;
        setRows((data?.data ?? []).map((o) => ({ id: o.id, cells: [o.name, ...(o.cols ?? [])] })));
      })
      .catch(() => {
        if (alive) setRows([]);
      });
    return () => {
      alive = false;
    };
  }, [field.optionsUrl, selectedId, reloadKey]);

  const cols = field.lookupColumns ?? ["Name"];
  const lookup = useMemo(() => ({ cols, rows: rows.map((r) => r.cells) }), [cols, rows]);
  const idByRow = useMemo(() => {
    const m = new Map<string[], string>();
    rows.forEach((r) => m.set(r.cells, r.id));
    return m;
  }, [rows]);
  const nameById = useMemo(() => new Map(rows.map((r) => [r.id, r.cells[0] ?? ""])), [rows]);

  const displayName = selectedId ? (nameById.get(selectedId) ?? "") : "";

  // Legacy ASPxGridLookup presentation (CrudField.lookupModal): a text box + Find /
  // Clear buttons that open a MODAL grid picker (image 931), instead of the inline
  // dropdown. Same rows + columns + fill, so it is a drop-in for the combo below.
  if (field.lookupModal) {
    return (
      <>
        <GridLookupModal
          field={field}
          rows={rows}
          value={selectedId}
          labelText={displayName}
          readOnly={readOnly}
          showEmpty={showEmpty}
          onPick={(id, cells) => {
            onChange(id);
            if (field.lookupFill && onFill) {
              const patch: Record<string, unknown> = {};
              for (const [target, col] of Object.entries(field.lookupFill)) patch[target] = cells[col] ?? "";
              onFill(patch);
            }
          }}
          onClear={() => onChange("")}
          // Edit pencil (owner request 2026-10-26) — opens the editable Client detail
          // (CLIENT_EDIT_CONFIG via ModuleCrudDialog); on save, refresh the picker rows.
          onEditRow={field.lookupEditClient ? (id) => setEditId(id) : undefined}
        />
        {editId && (
          <ModuleCrudDialog
            config={CLIENT_EDIT_CONFIG}
            id={editId}
            mode="edit"
            // Opened from WITHIN the GridLookupModal picker (an AppDialog); portal above it so the
            // edit popup is not hidden behind the picker (user report 2026-10-28, image 1045).
            elevated
            onClose={() => setEditId(null)}
            onSaved={() => {
              setEditId(null);
              setReloadKey((k) => k + 1);
            }}
          />
        )}
      </>
    );
  }

  // quickAdd.variant "client": the "+" CREATES a new Client (opens the full Customer form) —
  // distinct from lookupAddButton, whose "+" opens the grid picker.
  const quickAddClient = field.quickAdd?.variant === "client";
  return (
    <>
    <LookupCombo
      lookup={lookup}
      // Highlight the selected row by its ID, not its name — client/site names repeat
      // (26 "City of Houston" rows), so a name match lit up EVERY one of them (owner report
      // 2026-10-26). `value` is the id and `rowKey` resolves each row to its id (the same
      // reference-keyed map `onPick` uses), so exactly the chosen row highlights; `labelText`
      // keeps the trigger showing the name, never the raw id.
      value={selectedId}
      rowKey={(row) => idByRow.get(row) ?? ""}
      labelText={displayName}
      disabled={readOnly}
      placeholder={field.noneLabel ?? "N/A"}
      popupWidth={560}
      // Opt-in "+" that OPENS the grid picker (a second, visible way to select) —
      // CrudField.lookupAddButton (e.g. the Contacts Client lookup, legacy parity).
      newOpensPicker={!!field.lookupAddButton}
      // The create "+" (quickAdd.variant "client") opens ClientDialog; gated out while read-only.
      onNew={quickAddClient && !readOnly ? () => setCreating(true) : undefined}
      newTitle={field.lookupAddButton ? `Select ${field.label}` : quickAddClient ? `New ${field.label}` : undefined}
      className={showEmpty ? "required-empty" : undefined}
      onPick={(row) => {
        onChange(idByRow.get(row) ?? "");
        // Auto-fill sibling fields from the picked row (e.g. Client → Address).
        if (field.lookupFill && onFill) {
          const patch: Record<string, unknown> = {};
          for (const [target, col] of Object.entries(field.lookupFill)) patch[target] = row[col] ?? "";
          onFill(patch);
        }
      }}
    />
    {creating && (
      <ClientDialog
        createUrl="/api/v1/customers"
        optionsUrl={field.optionsUrl ?? ""}
        onClose={() => setCreating(false)}
        onAdded={(_opts, id) => { if (id) onChange(id); setReloadKey((k) => k + 1); setCreating(false); }}
      />
    )}
    </>
  );
}

interface AssocRow {
  id: string;
  name?: string;
  // Default Test-Method columns; custom columns (Email recipients) read by key.
  methodName?: string;
  formulaCount?: number;
  isFormulaActive?: boolean;
  [k: string]: unknown;
}

/** Default assocgrid columns — legacy DataPackageCheckList "Test Methods". */
const ASSOC_DEFAULT_COLUMNS: AssocColumn[] = [
  { key: "name", label: "TestName" },
  { key: "methodName", label: "MethodName" },
  { key: "formulaCount", label: "FormulaCount" },
  { key: "isFormulaActive", label: "IsFormulaActive", bool: true },
];

const assocColumnsOf = (field: CrudField): AssocColumn[] => field.assocColumns ?? ASSOC_DEFAULT_COLUMNS;

/** One cell value for a column: date → fmtLocalDate, bool → Yes/No, else text. */
function assocCell(row: AssocRow, col: AssocColumn): string {
  const v = row[col.key];
  if (col.bool) return v ? "Yes" : "No";
  if (col.date) return v == null || v === "" ? "" : fmtLocalDate(String(v));
  return v == null ? "" : String(v);
}

/** Truthy test for a legacy bit / Yes-No value, for a `checkbox` assoc column. */
function assocBool(v: unknown): boolean {
  return v === true || v === 1 || v === "1" || v === "true" || v === "Yes" || v === "yes";
}

/**
 * The body of an association grid — toolbar (Link/Unlink/Export) + linked-row grid
 * + footer + Link picker. No tab bar of its own, so it serves both a single panel
 * (AssocGridField) and the active tab of a group (AssocTabGroupField). Self-fetches
 * candidate rows from `field.optionsUrl`; the value is the array of linked ids
 * (same wire shape as a multiselect). Columns come from `field.assocColumns`
 * (default: the Test-Method set). Read-only in view mode.
 */
function AssocGridBody({
  field,
  value,
  readOnly,
  onChange,
}: {
  field: CrudField;
  value: string[];
  readOnly: boolean;
  onChange: (v: string[]) => void;
}) {
  const columns = assocColumnsOf(field);
  // The Link PICKER can declare its OWN (usually narrower) columns; otherwise it reuses
  // the linked-rows columns. Legacy Contact picker = FullName · Street1 · Phone · Email.
  const pickerColumns = field.assocPickerColumns ?? columns;
  const [rows, setRows] = useState<AssocRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [pickOpen, setPickOpen] = useState(false);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [pickSel, setPickSel] = useState<Set<string>>(new Set());
  const [pickSearch, setPickSearch] = useState("");
  // Picker pagination (legacy XAF picker footer: Page X of N + size dropdown) — the
  // candidate list can be hundreds of rows, so it pages like the old form instead of
  // one long scroll.
  const [pickPage, setPickPage] = useState(1);
  const [pickPageSize, setPickPageSize] = useState(20);
  // Edit-a-Test-Method pencil (opt-in, Data Package Review Checklist picker): the
  // Test Method whose editor dialog is open, and a counter bumped on its close so
  // the candidate rows re-fetch (a rename / method change shows at once).
  const editsTestMethod = field.assocPickerTestMethodEdit === true;
  const [editTmId, setEditTmId] = useState<string | null>(null);
  // Edit-a-Contact pencil (opt-in, Email To/CC/Bcc recipient picker): the Contact whose
  // ContactEditDialog is open; on close the candidate rows re-fetch so a name/email change
  // shows at once. editsContact and editsTestMethod are mutually exclusive per picker.
  const editsContact = field.assocPickerContactEdit === true;
  const [editContactId, setEditContactId] = useState<string | null>(null);
  const editsRow = editsTestMethod || editsContact;
  const [rowReload, setRowReload] = useState(0);

  useEffect(() => {
    let alive = true;
    if (!field.optionsUrl) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get<{ data?: AssocRow[] }>(field.optionsUrl)
      .then((r) => {
        if (alive) setRows(r.data.data ?? []);
      })
      .catch(() => {
        /* leave empty; grid shows "No data" */
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [field.optionsUrl, rowReload]);

  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
  const selectedRows = value.map((id) => byId.get(id)).filter((r): r is AssocRow => !!r);
  // Paging for the linked-rows grid (legacy footer: Page X of N + size dropdown).
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const totalPages = Math.max(1, Math.ceil(selectedRows.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const pageRows = selectedRows.slice((curPage - 1) * pageSize, curPage * pageSize);
  // Add-only picker hides rows already linked; the legacy grid-lookup
  // (assocPickerIncludesLinked) instead shows ALL rows with the linked ones
  // pre-checked, so its count matches the old form.
  const includesLinked = field.assocPickerIncludesLinked === true;
  const available = includesLinked ? rows : rows.filter((r) => !value.includes(r.id));
  const q = pickSearch.trim().toLowerCase();
  // Search matches every column SHOWN in the picker (pickerColumns — e.g. Matrix ·
  // Test · Method) as well as the linked-grid columns, so typing a Matrix value like
  // "Water" filters too. Previously only `columns` was searched, so a Matrix query
  // matched nothing and the search looked dead (owner report 2026-10-15).
  const searchCols = Array.from(
    new Map([...pickerColumns, ...columns].map((c) => [c.key, c])).values(),
  );
  const rowText = (r: AssocRow) => searchCols.map((c) => assocCell(r, c)).join(" ").toLowerCase();
  const availFiltered = q ? available.filter((r) => rowText(r).includes(q)) : available;
  const pickTotalPages = Math.max(1, Math.ceil(availFiltered.length / pickPageSize));
  const pickCurPage = Math.min(pickPage, pickTotalPages);
  const pickPageRows = availFiltered.slice((pickCurPage - 1) * pickPageSize, pickCurPage * pickPageSize);
  // Back to page 1 whenever the search narrows the list or the picker (re)opens.
  useEffect(() => { setPickPage(1); }, [pickSearch, pickOpen]);
  const colSpan = columns.length + (readOnly ? 0 : 1);
  const pickTitle = field.assocPickerTitle ?? field.label;

  const toggleSel = (id: string) =>
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const togglePick = (id: string) =>
    setPickSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const unlink = () => {
    onChange(value.filter((id) => !sel.has(id)));
    setSel(new Set());
  };
  const link = () => {
    // Full-membership picker (legacy grid-lookup): the checked set IS the new
    // membership (add + remove). Add-only picker: append the checked rows.
    onChange(includesLinked ? [...pickSel] : [...value, ...pickSel]);
    setPickSel(new Set());
    setPickOpen(false);
  };

  return (
    <div className="sd-assoc-panel">
      <div className="sd-assoc-toolbar">
        {!readOnly && (
          <>
            <button
              type="button"
              className="ca-btn"
              onClick={() => {
                // Pre-check the already-linked rows for the full-membership picker;
                // start empty for the add-only picker.
                setPickSel(includesLinked ? new Set(value) : new Set());
                setPickSearch("");
                setPickOpen(true);
              }}
            >
              <span className="tb-i tb-i-green">＋</span> Link
            </button>
            <button type="button" className="ca-btn" disabled={sel.size === 0} onClick={unlink}>
              <span className="tb-i tb-i-red">✕</span> Unlink
            </button>
          </>
        )}
        {/* Shared "Export to" dropdown (all ALPACA formats) — exports the currently-linked rows. */}
        <ExportMenu
          filename={field.label.replace(/\s+/g, "")}
          headers={columns.map((c) => c.label)}
          rows={selectedRows.map((r) => columns.map((c) => assocCell(r, c)))}
          triggerClassName="ca-btn"
        />
      </div>
      <div className="sd-assoc-grid">
        <table>
          <thead>
            <tr>
              {!readOnly && <th className="sd-assoc-cb" />}
              {columns.map((c) => (
                <th key={c.key} style={c.width ? { minWidth: c.width } : undefined}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="sd-assoc-empty">
                  Loading…
                </td>
              </tr>
            ) : selectedRows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="sd-assoc-empty">
                  No data to display
                </td>
              </tr>
            ) : (
              pageRows.map((r) => (
                <tr key={r.id}>
                  {!readOnly && (
                    <td className="sd-assoc-cb">
                      <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggleSel(r.id)} />
                    </td>
                  )}
                  {columns.map((c) => (
                    <td key={c.key}>
                      {c.checkbox
                        ? <input type="checkbox" checked={assocBool(r[c.key])} disabled readOnly aria-label={c.label} />
                        : assocCell(r, c)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Footer: Page X of N (items) · numbered pages · Page size (legacy parity). */}
      {selectedRows.length > 0 && (
        <div className="sd-assoc-foot">
          <span>
            Page {curPage} of {totalPages} ({selectedRows.length} items)
          </span>
          <span className="sd-assoc-pager">
            <button type="button" disabled={curPage <= 1} onClick={() => setPage(Math.max(1, curPage - 1))}>
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                type="button"
                key={n}
                className={n === curPage ? "sd-assoc-pg-active" : ""}
                onClick={() => setPage(n)}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              disabled={curPage >= totalPages}
              onClick={() => setPage(Math.min(totalPages, curPage + 1))}
            >
              ›
            </button>
          </span>
          <span className="sd-assoc-size">
            Page size:
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 20, 50, 100].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </span>
        </div>
      )}

      {pickOpen && (
        <div className="sd-assoc-modal" role="dialog" aria-modal="true">
          <div className="sd-assoc-modal-box">
            <div className="sd-assoc-modal-head">
              {pickTitle}
              <button type="button" className="sd-assoc-x" onClick={() => setPickOpen(false)}>
                ✕
              </button>
            </div>
            <input
              className="sd-input sd-assoc-search"
              placeholder="Search…"
              value={pickSearch}
              onChange={(e) => setPickSearch(e.target.value)}
            />
            <div className="sd-assoc-modal-list">
              <table>
                <thead>
                  <tr>
                    <th className="sd-assoc-cb" />
                    {editsRow && <th className="sd-assoc-ed" />}
                    {pickerColumns.map((c) => (
                      <th key={c.key} style={c.width ? { minWidth: c.width } : undefined}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pickPageRows.map((r) => (
                    <tr key={r.id} onClick={() => togglePick(r.id)} style={{ cursor: "pointer" }}>
                      <td className="sd-assoc-cb">
                        <input type="checkbox" readOnly checked={pickSel.has(r.id)} />
                      </td>
                      {/* Edit pencil — opens the row's Test Method (Data Package Review
                          Checklist) or Contact (Email recipients) record for edit (owner
                          request 2026-10-15). stopPropagation so clicking it does not toggle
                          the row's select checkbox. */}
                      {editsRow && (
                        <td className="sd-assoc-ed">
                          <button
                            type="button"
                            className="sd-assoc-ed-btn"
                            title={editsContact ? "Edit contact" : "Edit test method"}
                            aria-label={editsContact ? "Edit contact" : "Edit test method"}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (editsContact) setEditContactId(r.id);
                              else setEditTmId(r.id);
                            }}
                          >
                            <i className="ti ti-pencil" />
                          </button>
                        </td>
                      )}
                      {pickerColumns.map((c) => (
                        <td key={c.key}>{assocCell(r, c)}</td>
                      ))}
                    </tr>
                  ))}
                  {availFiltered.length === 0 && (
                    <tr>
                      <td colSpan={pickerColumns.length + 1 + (editsRow ? 1 : 0)} className="sd-assoc-empty">
                        No records
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="sd-assoc-modal-foot">
              {/* Page X of N (items) + numbered pager + page-size (legacy XAF picker
                  footer parity, owner request 2026-10-12) — count stays bottom-left. */}
              <span style={{ marginRight: "auto", alignSelf: "center", color: "var(--ai-muted, #6b7280)", fontSize: 13 }}>
                Page {pickCurPage} of {pickTotalPages} ({availFiltered.length} item{availFiltered.length === 1 ? "" : "s"})
              </span>
              {pickTotalPages > 1 && (
                <span className="sd-assoc-pager">
                  <button type="button" disabled={pickCurPage <= 1} onClick={() => setPickPage(Math.max(1, pickCurPage - 1))}>‹</button>
                  {Array.from({ length: pickTotalPages }, (_, i) => i + 1).map((n) => (
                    <button type="button" key={n} className={n === pickCurPage ? "sd-assoc-pg-active" : ""} onClick={() => setPickPage(n)}>
                      {n}
                    </button>
                  ))}
                  <button type="button" disabled={pickCurPage >= pickTotalPages} onClick={() => setPickPage(Math.min(pickTotalPages, pickCurPage + 1))}>›</button>
                </span>
              )}
              <span className="sd-assoc-size">
                Page size:
                <select value={pickPageSize} onChange={(e) => { setPickPageSize(Number(e.target.value)); setPickPage(1); }}>
                  {[10, 20, 50, 100].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </span>
              {/* Full-membership picker uses OK (applies the whole checked set, even
                  when cleared); add-only picker uses Link (N), disabled with none ticked. */}
              {includesLinked ? (
                <button type="button" className="ca-btn" onClick={link}>
                  OK
                </button>
              ) : (
                <button type="button" className="ca-btn" disabled={pickSel.size === 0} onClick={link}>
                  Link{pickSel.size ? ` (${pickSel.size})` : ""}
                </button>
              )}
              <button type="button" className="ca-btn" onClick={() => setPickOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit-a-Test-Method dialog (opt-in pencil in the picker). Reuses the shared
          Test Method window (G5 TestMethod_DetailView); on close the candidate rows
          re-fetch so a rename / method change is reflected. */}
      {editTmId && (
        <TestMethodDetailDialog
          testMethodId={editTmId}
          // Lift above the picker modal (.sd-assoc-modal is z-index 5000; the shared
          // AppDialog overlay is only 1500) so the editor stacks on top of the picker.
          overlayClassName="sd-assoc-edit-over"
          onClose={() => {
            setEditTmId(null);
            setRowReload((n) => n + 1);
          }}
        />
      )}

      {/* Edit-a-Contact dialog (opt-in pencil in the Email recipient picker). On save the
          candidate rows re-fetch so an edited name / email shows at once. Lifted above the
          picker modal like the Test Method editor. */}
      {editContactId && (
        <ContactEditDialog
          contactId={editContactId}
          overlayClassName="sd-assoc-edit-over"
          onSaved={() => setRowReload((n) => n + 1)}
          onClose={() => setEditContactId(null)}
        />
      )}
    </div>
  );
}

/**
 * Association grid (shared 'assocgrid' CrudFieldType) — a single legacy XAF
 * child-collection tab (e.g. DataPackageCheckList "Test Methods" Link/Unlink).
 */
function AssocGridField({
  field,
  value,
  readOnly,
  onChange,
}: {
  field: CrudField;
  value: string[];
  readOnly: boolean;
  onChange: (v: string[]) => void;
}) {
  return (
    <div className="sd-assoc">
      <div className="sd-assoc-tabbar">
        <span className="sd-assoc-tab active">
          <i className="ti ti-list-details" /> {field.label}
        </span>
      </div>
      <AssocGridBody field={field} value={value} readOnly={readOnly} onChange={onChange} />
    </div>
  );
}

/**
 * Several assocgrid fields sharing one `assocTabGroup` rendered as ONE tabbed panel
 * (a tab per field) — the legacy XAF TabbedGroup look (e.g. Email To / CC / Bcc).
 * The active tab's body is keyed by field so switching tabs gives it fresh state.
 */
function AssocTabGroupField({
  fields,
  values,
  readOnly,
  onChange,
}: {
  fields: CrudField[];
  values: Record<string, unknown>;
  readOnly: boolean;
  onChange: (key: string, v: string[]) => void;
}) {
  const [tab, setTab] = useState(0);
  const valOf = (f: CrudField) => (Array.isArray(values[f.key]) ? (values[f.key] as string[]) : []);
  if (fields.length === 0) return null;
  const active = fields[Math.min(tab, fields.length - 1)]!;
  return (
    <div className="sd-assoc">
      <div className="sd-assoc-tabbar" role="tablist">
        {fields.map((f, i) => (
          <button
            key={f.key}
            type="button"
            role="tab"
            aria-selected={i === tab}
            className={`sd-assoc-tab${i === tab ? " active" : ""}`}
            onClick={() => setTab(i)}
          >
            <i className="ti ti-users" /> {f.label}
            {valOf(f).length > 0 ? ` (${valOf(f).length})` : ""}
          </button>
        ))}
      </div>
      <AssocGridBody
        key={active.key}
        field={active}
        value={valOf(active)}
        readOnly={readOnly}
        onChange={(v) => onChange(active.key, v)}
      />
    </div>
  );
}

/**
 * Certificate-style file field (shared 'file' CrudFieldType). Two-phase upload:
 * picking a file POSTs it to `field.uploadUrl` and stores the returned FileData
 * Oid in the form (saved as the record's FK on Save); the filename renders as a
 * download link (GET `${downloadUrlBase}/${oid}/download`); Clear unsets it.
 * Mirrors the legacy XAF Certificate control (link + upload + clear).
 */
function FileField({
  field,
  oid,
  fileName,
  readOnly,
  onChange,
}: {
  field: CrudField;
  oid: string;
  fileName: string;
  readOnly: boolean;
  /** (newOid, newFileName) — '' clears the attachment. */
  onChange: (oid: string, fileName: string) => void;
}) {
  const notify = useNotify();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const hasFile = oid !== "" && oid != null;

  const download = async () => {
    if (!hasFile || !field.downloadUrlBase) return;
    try {
      const res = await api.get(`${field.downloadUrlBase}/${oid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "certificate";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      notify.error("Could not download the file.");
    }
  };

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file || !field.uploadUrl) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post<{ data?: { fileDataOid?: string; fileName?: string } }>(field.uploadUrl, fd);
      const newOid = res.data.data?.fileDataOid;
      if (!newOid) throw new Error("no oid");
      onChange(newOid, res.data.data?.fileName ?? file.name);
    } catch {
      notify.error("Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sd-row full">
      <label className="sd-label">{field.label}</label>
      <div className="sd-file">
        {hasFile ? (
          <button type="button" className="sd-file-link" onClick={download} title="Download">
            <i className="ti ti-file-text" /> {fileName || "certificate"}
          </button>
        ) : (
          <span className="sd-file-none">N/A</span>
        )}
        {!readOnly && (
          <span className="sd-file-actions">
            <input ref={fileRef} type="file" hidden onChange={pick} />
            <button
              type="button"
              className="sd-file-btn"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
              title={hasFile ? "Replace" : "Upload"}
            >
              <BusyIcon busy={busy} icon="ti-upload" /> {hasFile ? "Replace" : "Upload"}
            </button>
            {hasFile && (
              <button
                type="button"
                className="sd-file-btn danger"
                disabled={busy}
                onClick={() => onChange("", "")}
                title="Clear"
              >
                <i className="ti ti-trash" /> Clear
              </button>
            )}
          </span>
        )}
      </div>
      {field.hint && <span className="sd-hint">{field.hint}</span>}
    </div>
  );
}

/** Build a previewable data URL from raw base64, sniffing the MIME from the magic
 *  bytes (PNG / GIF / BMP, else JPEG). Returns "" when there is no image. The stored
 *  blob carries no MIME, so the sniff is what lets the thumbnail render. */
function imageDataUrl(b64: string): string {
  if (!b64) return "";
  try {
    const head = atob(b64.slice(0, 16)); // 16 is a multiple of 4 → valid base64 group
    const c = (i: number) => head.charCodeAt(i);
    let mime = "image/jpeg";
    if (c(0) === 0x89 && c(1) === 0x50) mime = "image/png";
    else if (c(0) === 0x47 && c(1) === 0x49) mime = "image/gif";
    else if (c(0) === 0x42 && c(1) === 0x4d) mime = "image/bmp";
    else if (c(0) === 0xff && c(1) === 0xd8) mime = "image/jpeg";
    return `data:${mime};base64,${b64}`;
  } catch {
    return "";
  }
}

/** Direct image-blob field (dbo.Contact.Photo = varbinary(max)) — a photo stored ON
 *  the row, unlike the FileData-backed 'file' type. Shows a thumbnail + Browse / Clear.
 *  The field value is RAW base64: it arrives from the GET (byte[] → base64), the picker
 *  reads a chosen image as a data URL and strips the prefix, and buildPayload posts the
 *  base64 string (or null when cleared) for the server to Convert.FromBase64String. */
function ImageField({
  field,
  value,
  readOnly,
  onChange,
}: {
  field: CrudField;
  value: unknown;
  readOnly: boolean;
  /** RAW base64, or "" to clear. */
  onChange: (b64: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const b64 = value == null ? "" : String(value);
  const src = imageDataUrl(b64);

  const pick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result ?? "");
      const comma = res.indexOf(","); // strip the "data:<mime>;base64," prefix
      onChange(comma >= 0 ? res.slice(comma + 1) : res);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="sd-row">
      <label className="sd-label">{field.label}</label>
      <div className="sd-photo">
        {src ? (
          <img className="sd-photo-img" src={src} alt={field.label} />
        ) : (
          <span className="sd-file-none">N/A</span>
        )}
        {!readOnly && (
          <span className="sd-file-actions">
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={pick} />
            <button
              type="button"
              className="sd-file-btn"
              onClick={() => fileRef.current?.click()}
              title={b64 ? "Replace" : "Browse"}
            >
              <i className="ti ti-upload" /> {b64 ? "Replace" : "Browse…"}
            </button>
            {b64 && (
              <button
                type="button"
                className="sd-file-btn danger"
                onClick={() => onChange("")}
                title="Clear"
              >
                <i className="ti ti-trash" /> Clear
              </button>
            )}
          </span>
        )}
      </div>
      {field.hint && <span className="sd-hint">{field.hint}</span>}
    </div>
  );
}
